import { todayLocalDay } from "../../shared/format/local-date.js";
import { 
  createIcons, 
  Box, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Clock, 
  Search, 
  Check, 
  HelpCircle, 
  FileText, 
  ChevronDown, 
  ChevronUp, 
  Camera, 
  Sparkles, 
  ArrowLeft, 
  Save, 
  Printer, 
  Eye, 
  Trash2, 
  CheckCircle,
  PlusCircle,
  AlertCircle,
  Wrench,
  MinusCircle,
  ShieldCheck,
  Edit3,
  Lock,
  Activity,
  Hash,
  Upload,
  Image,
  FileImage,
  X
} from "lucide";
import { router } from "../router/router.js";
import { contextStore } from "../auth/context-store.js";
import { sessionRequestContext } from "../auth/request-context.js";
// Editor de marcações das fotos do Checklist — importado aqui para que o
// custom element exista mesmo se esta página for carregada isoladamente.
import "../components/image-annotation-editor.js";
import type { IntegrityReportId, InspectionId, EquipmentId } from "../../shared/kernel/identifiers.js";
import type { ChecklistPhoto } from "../../domain/inspection/checklist-domain.js";
import { UnifiedInspectionIntegrityReportMapper } from "../../application/mappers/unified-inspection-integrity-report-mapper.js";
import { formatWorkOrderLabel, formatSubWorkOrderLabel, formatProjectLabel, formatUnitAreaLabel } from "../utils/formatters.js";
import "../components/document/pdf-preview.js";
import { PLH_REVIEW_UI_STYLES } from "../components/plh-review-ui.js";
import { HOLD_UI_STYLES, holdBanner, holdButton, loadActiveHold, openHoldModal, openHoldReleaseModal } from "../components/hold-ui.js";
import { CORRECTION_UI_STYLES, highlightElement, pendingCorrectionsBanner } from "../components/correction-request-ui.js";
import { parseDeepLinkTarget, type TechnicalCorrectionRequest } from "../../domain/review/technical-correction.js";
import type { DocumentHold } from "../../domain/review/document-hold.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { CONTEXT_LOAD_STYLES, ContextLoadTracker } from "../context/context-load.js";
import { missingRestoredLevels, verifyRestoredContext } from "../context/context-hydration.js";

export interface ChecklistRecommendation {
  id: string;
  criticality: 'BAIXA' | 'MÉDIA' | 'ALTA' | 'CRÍTICA';
  discipline: 'CALDEIRARIA' | 'PINTURA' | 'ISOLAMENTO TÉRMICO' | 'MECÂNICA' | 'CIVIL' | 'INSTRUMENTAÇÃO' | 'TUBULAÇÃO' | 'ELÉTRICA';
  execution: 'PARADA' | 'ROTINA';
  description: string;
  code?: string;
  zr?: string;
  scope?: string;
}

export interface PerformedTestRecord {
  methodId: string;
  result?: 'APPROVED' | 'REJECTED' | 'CLIENT_DISCRETION' | 'NOT_APPLICABLE' | 'PENDING';
  comment?: string;
  otherName?: string;
}

export interface InspectionReferenceDocument {
  id: string;
  name: string;
  description: string;
  fileData?: string;
  fileName?: string;
  fileType?: string;
}

export interface VisualChecklistItem {
  id: string;
  index: number;
  name: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'NOT_APPLICABLE';
  condition: string;
  technicalComment: string;
  photos: any[];
  recommendations: ChecklistRecommendation[];
  recommendation?: string; // legacy string support
  isExpanded?: boolean;
  isManual?: boolean;
  category?: string;
}

export const InspectionTestMethodCatalog = [
  { id: "VISUAL", code: "VISUAL", name: "INSPEÇÃO VISUAL", active: true, requiresDetail: false, linkedModule: null },
  { id: "REMOTE_VISUAL", code: "RVI", name: "INSPEÇÃO VISUAL REMOTA", active: true, requiresDetail: false, linkedModule: null },
  { id: "THICKNESS", code: "ME", name: "MEDIÇÃO DE ESPESSURA", active: true, requiresDetail: false, linkedModule: "ME" },
  { id: "LIQUID_PENETRANT", code: "LP", name: "LÍQUIDO PENETRANTE", active: true, requiresDetail: false, linkedModule: "LP" },
  { id: "BSCAN", code: "BSCAN", name: "B-SCAN", active: true, requiresDetail: false, linkedModule: "BSCAN" },
  { id: "MAGNETIC_PARTICLES", code: "PM", name: "PARTÍCULAS MAGNÉTICAS (PM)", active: true, requiresDetail: false, linkedModule: "PM" },
  { id: "PECA", code: "PECA", name: "PECA", active: true, requiresDetail: false, linkedModule: null },
  { id: "OTHER", code: "OTHER", name: "OUTROS", active: true, requiresDetail: true, linkedModule: null }
];

export class AppVisualChecklistPage extends HTMLElement {
  private escapeHtml(str: any): string {
    if (str === null || str === undefined) return '';
    return String(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private async ensureReportId(ctx: any): Promise<string> {
    if (this.reportId) return this.reportId;
    const p = (window as any).app?.platform;
    if (!p?.reportService || !this.inspectionId) throw new Error("Inspection ID ausente para criação de relatório.");
    const existing = await p.reportService.getReportByInspection(ctx, this.inspectionId as InspectionId);
    if (existing) {
      this.reportId = existing.id;
      return existing.id;
    }
    const draft = await p.reportService.createDraftReport(ctx, this.inspectionId as InspectionId);
    this.reportId = draft.id;
    return draft.id;
  }

  private reportId: string | null = null;
  private reportStatus: string | null = null;
  private hasUnsavedChanges: boolean = false;
  private reportNumber: string = '';
  private clientReportNumber: string | null = null;
  private inspectionId: string = '';
  private equipmentId: string = '';
  private equipmentTag: string = '';
  private equipmentName: string = '';
  private inspectionType: string = 'Inspeção Externa';
  private clientName: string = '';
  private clients: any[] = [];
  private clientsLoadState: 'LOADING' | 'LOADED' | 'ERROR' = 'LOADING';
  private selectedClientId: string = '';
  private professionals: any[] = [];
  private workOrders: any[] = [];
  private subWorkOrders: any[] = [];
  private subOsAccesses: any[] = [];
  private selectedInspectorId: string = '';
  private selectedWorkOrderId: string = '';
  private selectedSubWorkOrderId: string = '';
  private selectedOsCode: string = '';
  private selectedSubOsCode: string = '';
  private projects: any[] = [];
  private selectedProjectId: string = '';
  private unitAreas: any[] = [];
  private selectedUnitAreaId: string = '';
  private registeredEquipments: any[] = [];
  private isHydratingContext: boolean = false;
  /**
   * Tratamento único das leituras de contexto (política em
   * presentation/context/context-load.ts). Aqui ele resolve também o
   * "Aguardando sincronização..." eterno do Snapshot Técnico: o snapshot sai
   * de `registeredEquipments`, e essa lista vinha vazia sempre que a leitura
   * de Equipamentos era negada — o que a tela anunciava como sincronização
   * pendente, quando na verdade a consulta havia falhado.
   */
  private readonly ctxLoad = new ContextLoadTracker();

  private activeEquipmentSnapshot: any = null;
  private reportArea: string = '';
  private selectedEquipment: string = '';
  private selectedSystems: string[] = [];
  private deleteConfirmingCompId: string | null = null;
  private targetRec: string | null = null;

  // Recommendation Modal State
  private showCreateRecModal: boolean = false;
  private recTargetItem: VisualChecklistItem | null = null;
  private recDiscipline: string = 'CALDEIRARIA';
  private recCriticality: 'BAIXA' | 'MÉDIA' | 'ALTA' | 'CRÍTICA' = 'MÉDIA';
  private recExecution: 'ROTINA' | 'PARADA' = 'ROTINA';
  private recScope: string = '';
  private recZr: string = '';

  // Minuta Context State
  private currentMinuta: any = null;
  private currentMinutaSnapshotRecIds = new Set<string>();
  private minutaCorrections: any[] = [];

  private validationErrors: string[] = [];

  private validateInspectionRequiredFields(): string[] {
    const errors: string[] = [];
    this.validationErrors = []; // reset visual errors

    if (!this.selectedInspectorId) {
      const auth = contextStore.get();
      this.selectedInspectorId = auth.professionalId || auth.userId || (this.professionals[0]?.id) || 'usr_inspector';
    }

    if (!this.selectedClientId) { errors.push('Cliente'); this.validationErrors.push('clientId'); }
    if (!this.selectedInspectorId) {
      const auth = contextStore.get();
      this.selectedInspectorId = auth.professionalId || auth.userId || (this.professionals[0]?.id) || 'usr_inspector';
    }
    if (!this.selectedInspectorId) { errors.push('Inspetor Cadastrado (Elaborador)'); this.validationErrors.push('inspectorId'); }
    if (!this.selectedWorkOrderId) { errors.push('Ordem de Serviço (OS)'); this.validationErrors.push('workOrderId'); }
    if (!this.selectedSubWorkOrderId) { errors.push('Sub-OS do Inspetor'); this.validationErrors.push('subWorkOrderId'); }
    if (!this.selectedProjectId) { errors.push('Projeto'); this.validationErrors.push('projectId'); }
    if (!this.selectedUnitAreaId) { errors.push('Unidade / Área'); this.validationErrors.push('unitAreaId'); }
    if (!this.equipmentId) { errors.push('Equipamento / Ativo'); this.validationErrors.push('equipmentId'); }
    if (!this.conditionState) { errors.push('Condição'); this.validationErrors.push('condition'); }
    if (!this.natureState) { errors.push('Natureza'); this.validationErrors.push('nature'); }
    if (!this.modalityState) { errors.push('Modalidade'); this.validationErrors.push('modality'); }
    if (!this.startDate) { errors.push('Data Inicial'); this.validationErrors.push('startDate'); }
    if (!this.endDate) { errors.push('Data Final'); this.validationErrors.push('endDate'); }

    if (this.startDate && this.endDate) {
      if (new Date(this.endDate) < new Date(this.startDate)) {
        errors.push('Data Final (não pode ser anterior à Data Inicial)');
        this.validationErrors.push('endDate');
      }
    }

    return errors;
  }

  /**
   * O SNAPSHOT NÃO VEIO — E POR QUÊ.
   *
   * "Aguardando sincronização..." era sempre a mesma frase, mesmo quando não
   * havia nada a aguardar: sem Equipamento selecionado, com a leitura de
   * Equipamentos negada por permissão ou com um TAG que não existe no
   * cadastro. Quem lia ficava esperando uma sincronização que nunca ia
   * acontecer, em vez de resolver a causa.
   */
  private renderSnapshotUnavailable(): string {
    const grey = 'style="color: var(--color-text-secondary);"';
    const warn = 'style="color: var(--ia-warning-fg, #fbbf24);"';

    if (this.ctxLoad.statusOf("Equipamentos") !== "ok") {
      const [message] = this.ctxLoad.issues();
      return `<div><span ${warn}>${this.escapeHtml(message || "Não foi possível carregar o cadastro de Equipamentos.")}</span></div>`;
    }
    if (!this.equipmentId && !this.equipmentTag) {
      return `<div><span ${grey}>Selecione o Equipamento / TAG para carregar o cadastro técnico.</span></div>`;
    }
    const reference = this.equipmentTag || this.equipmentId;
    return `<div><span ${warn}>O ativo <strong>${this.escapeHtml(String(reference))}</strong> não foi encontrado no Cadastro de Equipamentos deste Cliente. Verifique o cadastro do ativo — o Snapshot Técnico é montado a partir dele.</span></div>`;
  }

  private renderEquipmentSnapshotFields(eq: any, fallbackName: string): string {
    if (!eq) return this.renderSnapshotUnavailable();
    
    const formatVal = (val: any) => {
      if (val === null || val === undefined || val === '') return 'Não informado';
      return this.escapeHtml(String(val));
    };

    const formatMeas = (meas: any, defaultUnit: string) => {
      if (!meas || meas.value === null || meas.value === undefined) return 'Não informado';
      return this.escapeHtml(`${meas.value} ${meas.unit || defaultUnit}`);
    };

    let html = `<div><span style="color: var(--color-text-secondary);">Nome/Descrição:</span> <strong>${formatVal(eq.name || eq.description || fallbackName)}</strong></div>`;

    if (eq.equipmentType === 'VASO_PRESSAO' || eq.equipmentType === 'PRESSURE_VESSEL') {
      html += `
        <div><span style="color: var(--color-text-secondary);">Serviço:</span> <strong>${formatVal(eq.service)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Fluido:</span> <strong>${formatVal(eq.fluid)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Categoria NR-13:</span> <strong>${formatVal(eq.nr13Category)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Classe de Fluido:</span> <strong>${formatVal(eq.fluidClass)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Norma de Fabricação:</span> <strong>${formatVal(eq.fabricationStandard)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Desenho de Referência:</span> <strong>${formatVal(eq.drawingReference || eq.projectDrawingFlowchart)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Fluxograma:</span> <strong>${formatVal(eq.flowDiagramReference || eq.projectDrawingFlowchart)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Material:</span> <strong>${formatVal(eq.material)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Pressão de Projeto:</span> <strong>${formatMeas(eq.designPressure, 'kgf/cm²')}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Pressão Operação:</span> <strong>${formatMeas(eq.operatingPressure, 'kgf/cm²')}</strong></div>
        <div><span style="color: var(--color-text-secondary);">PMTA:</span> <strong>${eq.pmtaValue ? this.escapeHtml(eq.pmtaValue + ' ' + (eq.pmtaUnit || 'kgf/cm²')) : 'Não informado'}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Temp. de Projeto:</span> <strong>${formatMeas(eq.designTemperature, '°C')}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Temp. de Operação:</span> <strong>${formatMeas(eq.operatingTemperature, '°C')}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Sobreespessura:</span> <strong>${formatMeas(eq.corrosionAllowance, 'mm')}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Espessura:</span> <strong>${formatMeas(eq.nominalThickness, 'mm')}</strong></div>
      `;
    } else if (eq.equipmentType === 'TUBULACAO' || eq.equipmentType === 'PIPING' || eq.equipmentType === 'PIPING_LINE') {
      html += `
        <div><span style="color: var(--color-text-secondary);">Serviço:</span> <strong>${formatVal(eq.service)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Fluido:</span> <strong>${formatVal(eq.fluid)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Classe de Fluido:</span> <strong>${formatVal(eq.fluidClass)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Desenho de Referência:</span> <strong>${formatVal(eq.drawingReference || eq.projectDrawingFlowchart)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Fluxograma:</span> <strong>${formatVal(eq.flowDiagramReference || eq.projectDrawingFlowchart)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Material:</span> <strong>${formatVal(eq.material)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Pressão de Projeto:</span> <strong>${formatMeas(eq.designPressure, 'kgf/cm²')}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Pressão Operação:</span> <strong>${formatMeas(eq.operatingPressure, 'kgf/cm²')}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Temp. de Projeto:</span> <strong>${formatMeas(eq.designTemperature, '°C')}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Temp. de Operação:</span> <strong>${formatMeas(eq.operatingTemperature, '°C')}</strong></div>
        <div><span style="color: var(--color-text-secondary);">PMTA:</span> <strong>${eq.pmtaValue ? this.escapeHtml(eq.pmtaValue + ' ' + (eq.pmtaUnit || 'kgf/cm²')) : 'Não informado'}</strong></div>
        <div><span style="color: var(--color-text-secondary);">DE:</span> <strong>${formatVal(eq.fromReference || eq.fromEquipmentId)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">PARA:</span> <strong>${formatVal(eq.toReference || eq.toEquipmentId)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Diâmetro:</span> <strong>${formatVal(eq.nps)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Schedule/Espessura:</span> <strong>${formatVal(eq.schedule)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Sobreespessura:</span> <strong>${formatMeas(eq.corrosionAllowance, 'mm')}</strong></div>
      `;
    } else {
      // TANQUE, CALDEIRA, etc. Genérico
      html += `
        <div><span style="color: var(--color-text-secondary);">Serviço:</span> <strong>${formatVal(eq.service)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Fluido:</span> <strong>${formatVal(eq.fluid)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Categoria NR-13:</span> <strong>${formatVal(eq.nr13Category)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Classe de Fluido:</span> <strong>${formatVal(eq.fluidClass)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Norma de Fabricação:</span> <strong>${formatVal(eq.fabricationStandard)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Desenho de Referência:</span> <strong>${formatVal(eq.drawingReference || eq.projectDrawingFlowchart)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Fluxograma:</span> <strong>${formatVal(eq.flowDiagramReference || eq.projectDrawingFlowchart)}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Pressão de Projeto:</span> <strong>${formatMeas(eq.designPressure, 'kgf/cm²')}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Pressão Operação:</span> <strong>${formatMeas(eq.operatingPressure, 'kgf/cm²')}</strong></div>
        <div><span style="color: var(--color-text-secondary);">PMTA:</span> <strong>${eq.pmtaValue ? this.escapeHtml(eq.pmtaValue + ' ' + (eq.pmtaUnit || 'kgf/cm²')) : 'Não informado'}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Temp. de Projeto:</span> <strong>${formatMeas(eq.designTemperature, '°C')}</strong></div>
        <div><span style="color: var(--color-text-secondary);">Temp. de Operação:</span> <strong>${formatMeas(eq.operatingTemperature, '°C')}</strong></div>
      `;
    }

    return html;
  }

  // NDT Modal State
  private showNdtModal: boolean = false;
  private ndtModalMethodId: string | null = null;
  private ndtModalMethodName: string = '';
  private ndtModalOtherName: string = '';
  private ndtModalResult: 'APPROVED' | 'REJECTED' | 'CLIENT_DISCRETION' | 'NOT_APPLICABLE' | 'PENDING' = 'APPROVED';
  private ndtModalComment: string = '';

  // Photo Modal State
  private showPhotoModal: boolean = false;
  private photoModalTargetItemId: string | null = null;
  private photoModalTargetItemName: string = '';
  private photoModalEditingIdx: number | null = null;
  private photoModalImageBase64: string = '';
  private photoModalComment: string = '';
  private photoModalFileName: string = '';

  // Inspection Header Data
  private isLocked: boolean = false;
  private bookNumber: string = '';
  private conditionState: string = 'Em parada';
  private startDate: string = todayLocalDay();
  private endDate: string = todayLocalDay();
  private natureState: string = 'Periódica';
  private modalityState: string = 'Geral';
  private generalObservations: string = '';
  private maintenanceServices: string = '';
  private damageMechanisms: string = '';
  private performedTests: PerformedTestRecord[] = [];
  private otherTestDescription: string = '';

  // Reference Documents State
  private referenceDocuments: InspectionReferenceDocument[] = [];

  // Additional modal/UI state properties
  private showManualCompModal: boolean = false;
  private activeNdtMethodId: string | null = null;
  private modalNdtResult: 'APPROVED' | 'REJECTED' | 'CLIENT_DISCRETION' | 'NOT_APPLICABLE' | 'PENDING' = 'APPROVED';
  private modalNdtComment: string = '';
  private modalNdtOtherName: string = '';
  private activePhotoCompId: string | null = null;
  private activePhotoIndex: number | null = null;
  private modalPhotoUrl: string = '';
  private modalPhotoComment: string = '';
  /**
   * EDITOR DE IMAGEM (comando 05 + correção): foto do Checklist aberta no
   * editor. Guarda só a referência de qual foto está aberta e o data URL da
   * ORIGINAL — as marcações vivem dentro do próprio componente até o usuário
   * salvar ou cancelar.
   */
  private annotatingCompId: string | null = null;
  private annotatingPhotoIndex: number | null = null;
  private annotatingOriginalUrl: string = '';
  private annotatingBusy: boolean = false;
  private canUnlock: boolean = false;
  private clientId: string = '';
  /** Painel de Engenharia: HOLD do Checklist/Inspeção e correções por item do RI (deep link do Inspetor). */
  private hold: DocumentHold | null = null;
  private riCorrections: TechnicalCorrectionRequest[] = [];
  private engineeringNotice: string = '';
  private showPdfPreviewModal: boolean = false;
  private currentStep: 1 | 2 | 3 | 4 = 1;

  private closeReportViewer = () => {
    this.showPdfPreviewModal = false;
    this.render();
  };

  public get isStep1Locked(): boolean {
    return !!(this.reportNumber && this.reportNumber !== '-' && !this.reportNumber.includes('RI-NOVO'));
  }

  public getMissingInspectionFields(): string[] {
    const missing: string[] = [];
    if (!this.selectedClientId || this.selectedClientId === 'undefined' || this.selectedClientId === 'null') missing.push('Cliente');
    if (!this.selectedInspectorId || this.selectedInspectorId === 'undefined' || this.selectedInspectorId === 'null') missing.push('Inspetor Cadastrado (Elaborador)');
    if (!this.selectedWorkOrderId || this.selectedWorkOrderId === 'undefined' || this.selectedWorkOrderId === 'null') missing.push('Ordem de Serviço (OS)');
    if (!this.selectedSubWorkOrderId || this.selectedSubWorkOrderId === 'undefined' || this.selectedSubWorkOrderId === 'null') missing.push('Sub-OS do Inspetor');
    if (!this.selectedProjectId || this.selectedProjectId === 'undefined' || this.selectedProjectId === 'null') missing.push('Projeto');
    if (!this.selectedUnitAreaId || this.selectedUnitAreaId === 'undefined' || this.selectedUnitAreaId === 'null') missing.push('Unidade / Área');
    if (!this.equipmentId || this.equipmentId === 'undefined' || this.equipmentId === 'null') missing.push('Equipamento / Ativo');
    if (!this.conditionState || this.conditionState === 'undefined' || this.conditionState === 'null') missing.push('Condição');
    if (!this.natureState || this.natureState === 'undefined' || this.natureState === 'null') missing.push('Natureza');
    if (!this.modalityState || this.modalityState === 'undefined' || this.modalityState === 'null') missing.push('Modalidade');
    if (!this.startDate) missing.push('Data Inicial');
    if (!this.endDate) missing.push('Data Final');
    return missing;
  }

  public getInspectionValidationErrors(): string[] {
    const errors: string[] = [];
    
    // Validate Dates
    if (this.startDate && this.endDate) {
      if (new Date(this.endDate) < new Date(this.startDate)) {
        errors.push('A Data Final não pode ser anterior à Data Inicial.');
      }
    }
    
    // Validar relacionamento SubOS -> OS
    if (this.selectedSubWorkOrderId && this.selectedWorkOrderId) {
      const sub = this.subWorkOrders.find(s => s.id === this.selectedSubWorkOrderId);
      if (sub && sub.workOrderId !== this.selectedWorkOrderId) {
        errors.push('A Sub-OS selecionada não pertence à Ordem de Serviço (OS) selecionada.');
      }
    }
    
    // Validar relacionamento Project -> Client
    if (this.selectedProjectId && this.selectedClientId) {
      const proj = this.projects.find(p => p.id === this.selectedProjectId);
      if (proj && proj.clientId && proj.clientId !== this.selectedClientId) {
        errors.push('O Projeto selecionado não pertence ao Cliente selecionado.');
      }
    }

    errors.push(...this.getUnresolvedContextRefs());

    return errors;
  }

  /**
   * VÍNCULO GRAVADO QUE NÃO EXISTE MAIS.
   *
   * Os ids do contexto voltam da Inspeção/do Relatório e as listas são
   * carregadas — mas **nada conferia se o id reposto ainda está na lista**.
   * `getMissingInspectionFields` só olha se o campo está vazio, e um id
   * morto não está vazio: a validação passava, o `<select>` aparecia em
   * branco (atribuir um `value` sem `<option>` correspondente não seleciona
   * nada e não avisa) e o Relatório de Integridade era emitido apontando
   * para um Projeto, uma OS ou um Ativo que já não existem.
   *
   * Lista ainda não carregada não é lista vazia: nesse caso o nível é
   * marcado indisponível e o valor gravado é preservado, em vez de virar
   * erro por causa do tempo de carregamento.
   */
  public getUnresolvedContextRefs(): string[] {
    const loaded = (list: any[], ready: boolean): 'ok' | 'failed' => (ready && Array.isArray(list) ? 'ok' : 'failed');
    const clientsReady = this.clientsLoadState === 'LOADED';
    const report = verifyRestoredContext([
      { label: 'o Cliente', persistedId: this.selectedClientId, options: this.clients, status: loaded(this.clients, clientsReady) },
      { label: 'o Projeto', persistedId: this.selectedProjectId, options: this.projects, status: loaded(this.projects, this.projects.length > 0) },
      { label: 'a Unidade / Área', persistedId: this.selectedUnitAreaId, options: this.unitAreas, status: loaded(this.unitAreas, this.unitAreas.length > 0) },
      { label: 'a Ordem de Serviço', persistedId: this.selectedWorkOrderId, options: this.workOrders, status: loaded(this.workOrders, this.workOrders.length > 0) },
      { label: 'a Sub-OS', persistedId: this.selectedSubWorkOrderId, options: this.subWorkOrders, status: loaded(this.subWorkOrders, this.subWorkOrders.length > 0) },
      { label: 'o Equipamento / Ativo', persistedId: this.equipmentId, options: this.registeredEquipments, status: loaded(this.registeredEquipments, this.registeredEquipments.length > 0) }
    ]);
    // Só o que sumiu vira erro de validação. Indisponível é advertência de
    // leitura, não motivo para impedir o trabalho.
    return missingRestoredLevels(report).map(level =>
      `${level.label.charAt(0).toUpperCase()}${level.label.slice(1)} gravado nesta inspeção não está mais no cadastro (${level.persistedId}). Selecione novamente antes de emitir o Relatório.`);
  }

  public isInspectionDataComplete(): boolean {
    return this.getMissingInspectionFields().length === 0 && this.getInspectionValidationErrors().length === 0;
  }

  private extractNdts() {
    return this.performedTests.map(pt => {
      let name = pt.methodId;
      if (pt.methodId === 'OTHER') {
        name = pt.otherName || 'Outros';
      } else {
        const catalogItem = InspectionTestMethodCatalog.find(c => c.id === pt.methodId);
        if (catalogItem) name = catalogItem.name;
      }
      let resultLabel = 'APROVADO';
      if (pt.result === 'APPROVED') resultLabel = 'APROVADO';
      else if (pt.result === 'REJECTED') resultLabel = 'REPROVADO';
      else if (pt.result === 'CLIENT_DISCRETION') resultLabel = 'A CRITÉRIO DO CLIENTE';
      else if (pt.result === 'NOT_APPLICABLE') resultLabel = 'NÃO APLICÁVEL';
      else if (pt.result === 'PENDING') resultLabel = 'PENDENTE';
      else if (pt.result) resultLabel = String(pt.result);

      return {
        name,
        result: resultLabel,
        comment: pt.comment || ''
      };
    });
  }

  // Filter & Search State
  private searchQuery: string = '';
  private currentFilter: 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'NOT_APPLICABLE' | 'WITH_RECOMMENDATION' | 'WITH_PHOTOS' = 'ALL';

  // Confirmation Modal State
  private showBulkConfirmModal: boolean = false;

  private showConsistencyModal: boolean = false;
  private consistencyErrors: string[] = [];

  private showValidationModal: boolean = false;
  private validationModalMessage: string[] = [];

  // Dynamic components loaded based on Equipment Type
  private items: VisualChecklistItem[] = [];

  static get observedAttributes() {
    return ['report-id', 'inspection-id', 'id', 'equipment-id'];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'report-id' && newValue) {
      this.reportId = newValue;
      this.loadData();
    } else if (name === 'inspection-id' && newValue) {
      this.inspectionId = newValue;
      this.loadData();
    } else if (name === 'equipment-id' && newValue) {
      this.equipmentTag = newValue;
      this.render();
    }
  }

  async connectedCallback() {
    window.addEventListener('MASTER_DATA_UPDATED', () => this.loadMinutaContext());
    await this.loadData();
    await this.loadEngineeringState();
    this.render();
    this.handleDeepLink();
  }

  /** HOLD ativo do Checklist e correções pendentes do RI — lidos do Painel de Engenharia. */
  private async loadEngineeringState() {
    if (!this.inspectionId) return;
    this.hold = await loadActiveHold("CHECKLIST", this.inspectionId, this.clientId || this.selectedClientId || null);
    try {
      const svc = (window as any).app?.platform?.technicalCorrectionService;
      this.riCorrections = svc && this.reportId ? [...await svc.listByDocument(this.engCtx(), "INTEGRITY", this.reportId)] : [];
    } catch { this.riCorrections = []; }
  }

  private engCtx(): any {
    const c = contextStore.get();
    return { activeClientId: this.clientId || this.selectedClientId || c.activeClientId || undefined, permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null };
  }

  private holdSummary() {
    const prof = (this.professionals || []).find((p: any) => String(p.id) === String(this.selectedInspectorId));
    const os = this.workOrders.find((w: any) => w.id === this.selectedWorkOrderId);
    const sub = this.subWorkOrders.find((w: any) => w.id === this.selectedSubWorkOrderId);
    const project = this.projects.find((p: any) => p.id === this.selectedProjectId);
    const unit = this.unitAreas.find((u: any) => u.id === this.selectedUnitAreaId);
    return {
      documentNumber: this.reportNumber || `Checklist ${this.inspectionId}`, revision: "00", clientId: String(this.clientId || this.selectedClientId || ""), client: this.clientName || "",
      project: project ? formatProjectLabel(project) : "", unitArea: unit ? formatUnitAreaLabel(unit) : "", tag: this.equipmentTag || "",
      osNumber: os ? (os.code || os.number || this.selectedOsCode || "") : this.selectedOsCode || "", subOsNumber: sub ? (sub.code || sub.number || this.selectedSubOsCode || "") : this.selectedSubOsCode || "",
      inspectorProfessionalId: this.selectedInspectorId || null, inspectorName: prof?.name || "", route: `/inspections/checklist/${this.inspectionId}`
    };
  }

  /** Zona do HOLD/correções — fica clicável mesmo com o resto do Checklist bloqueado pelo HOLD. */
  private renderEngineeringZone(): string {
    const editable = !!this.inspectionId && !this.isLocked;
    const target = parseDeepLinkTarget(typeof location !== "undefined" ? location.search : "");
    const canResolve = contextStore.hasPermission(PermissionCode.ChecklistUpdate) || contextStore.hasPermission(PermissionCode.TechnicalReviewSubmitToPlh);
    return `
      <div class="hold-zone">
        ${this.engineeringNotice ? `<div class="plh-note" style="margin-bottom:10px;">${this.escapeHtml(this.engineeringNotice)}</div>` : ""}
        ${this.hold ? `<div style="display:flex; justify-content:flex-end; margin-bottom:8px;">${holdButton(this.hold, editable, "btn-sub-action")}</div>` : ""}
        ${holdBanner(this.hold, target.sectionId === "hold")}
        ${pendingCorrectionsBanner(this.riCorrections, canResolve, target.itemId)}
      </div>`;
  }

  private bindEngineeringEvents() {
    this.querySelector("#btn-put-on-hold")?.addEventListener("click", () => {
      if (!this.inspectionId) return;
      openHoldModal({ documentType: "CHECKLIST", documentId: this.inspectionId, summary: this.holdSummary(), currentStatus: { status: this.isLocked ? "LOCKED" : "IN_PROGRESS", label: this.isLocked ? "Somente leitura (salvo)" : "Em Elaboração" } }, async hold => {
        this.engineeringNotice = `Checklist EM HOLD. Motivo: ${hold.reason}. Aparece em ENGENHARIA → RELATÓRIOS EM HOLD.`;
        await this.loadEngineeringState(); this.render();
      });
    });
    this.querySelector("#btn-release-hold")?.addEventListener("click", () => {
      if (!this.hold) return;
      openHoldReleaseModal(this.hold, { status: this.isLocked ? "LOCKED" : "IN_PROGRESS", label: this.isLocked ? "Somente leitura (salvo)" : "Em Elaboração" }, async hold => {
        this.engineeringNotice = `Checklist liberado do HOLD. Status restaurado: ${hold.previousStatusLabel}.`;
        await this.loadEngineeringState(); this.render();
      });
    });
    this.querySelectorAll("[data-resolve-correction]").forEach(btn => btn.addEventListener("click", async () => {
      const id = (btn as HTMLElement).dataset["resolveCorrection"];
      if (!id) return;
      try {
        await (window as any).app.platform.technicalCorrectionService.markResolved(this.engCtx(), id, "");
        this.engineeringNotice = "Item marcado como corrigido. O PLH foi notificado.";
      } catch (e: any) { this.engineeringNotice = e?.safeMessage || e?.message || "Erro ao marcar como corrigido."; }
      await this.loadEngineeringState(); this.render();
    }));
  }

  private handleDeepLink() {
    const params = new URLSearchParams(window.location.search);
    const target = parseDeepLinkTarget(window.location.search);
    // Correção de item do Checklist (componente) ou HOLD: rola até o alvo e destaca.
    if (target.sectionId === "checklist" && target.itemId) {
      setTimeout(() => {
        const item = (this.items || []).find((i: any) => String(i.id) === target.itemId || String(i.name).toLowerCase() === String(target.itemId).toLowerCase());
        if (item) { item.isExpanded = true; this.currentStep = 3; this.render(); }
        setTimeout(() => { const el = this.querySelector(`[data-comp-id="${CSS.escape(String(target.itemId))}"]`) as HTMLElement | null; if (el) highlightElement(el); }, 300);
      }, 500);
    } else if (target.sectionId === "hold") {
      setTimeout(() => { const el = this.querySelector(".hold-banner") as HTMLElement | null; if (el) highlightElement(el); }, 500);
    }
    const recId = params.get('recommendation');
    if (recId) {
      this.targetRec = recId;
      setTimeout(() => {
        const item = (this.items || []).find((i: any) => i.recommendations?.some((r: any) => r.id === recId));
        if (item) {
          item.isExpanded = true;
          this.render();
          
          setTimeout(() => {
            const el = this.querySelector(`[data-rec-id="${recId}"]`);
            if (el) {
              const rect = el.getBoundingClientRect();
              window.scrollTo({
                top: rect.top + window.scrollY - 100,
                behavior: 'smooth'
              });
              (el as HTMLElement).style.border = '2px solid #d97706';
              (el as HTMLElement).style.backgroundColor = '#fffbeb';
              (el as HTMLElement).style.borderRadius = '8px';
              (el as HTMLElement).style.padding = '12px';
            }
          }, 300);
        }
      }, 500);
    }
  }

  
  public async generateChecklistForSelectedEquipment(force: boolean = false): Promise<void> {
    if (!this.equipmentId && !this.equipmentTag) return;
    
    // If not forced and we already have items for this exact inspection from repo or storage, don't overwrite
    const matchEq = this.registeredEquipments.find(e => e.id === this.equipmentId || e.tag === this.equipmentTag);
    if (!matchEq) {
      console.error("COMPONENT_NOT_RESOLVED: Equipamento não encontrado no cadastro.");
      return;
    }

    const eqType = matchEq.equipmentType || 'OTHER';
    let checklistDefItems: any[] = [];
    let parsedPayload: any = {};
    
    if (matchEq.payloadJson) {
      try {
        parsedPayload = typeof matchEq.payloadJson === 'string' ? JSON.parse(matchEq.payloadJson) : matchEq.payloadJson;
      } catch (e) {
        console.warn("Could not parse equipment payloadJson", e);
      }
    }

    try {
      const mod = await import('../../catalogs/equipment-checklists.js');
      const catalog = mod.EQUIPMENT_CHECKLIST_CATALOG;
      
      let realComponents: any[] = [];
      if (parsedPayload.components && Array.isArray(parsedPayload.components) && parsedPayload.components.length > 0) {
        realComponents = parsedPayload.components;
      }

      if (realComponents.length > 0) {
        checklistDefItems = realComponents
          .filter(c => {
            if (!c || !c.name || typeof c.name !== 'string' || c.name.trim() === '' || c.name.trim().toLowerCase() === 'default') {
              console.error("COMPONENT_NOT_RESOLVED: Componente inválido ou sem nome no cadastro do ativo.");
              return false;
            }
            return true;
          })
          .map(c => ({
            id: c.id || `comp-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
            name: c.name.trim(),
            category: c.category || c.type || 'Corpo'
          }));
      } else {
        const catalogDef = catalog[eqType as keyof typeof catalog] || catalog['OTHER'];
        checklistDefItems = catalogDef.items.filter((item: any) => {
          if (item.condition && typeof item.condition === 'function') {
            return item.condition(parsedPayload);
          }
          return true;
        });
      }
    } catch (e) {
      console.error("COMPONENT_NOT_RESOLVED: Falha ao carregar catálogo de checklists:", e);
      return;
    }

    // Preserve manual components if any
    const manualItems = (this.items || []).filter(it => it.isManual);

    // Map new items, retaining existing status/comments if item with same name already existed
    const newItems = checklistDefItems.map((def, i) => {
      const existing = (this.items || []).find(it => it.name.trim().toLowerCase() === def.name.trim().toLowerCase());
      if (existing && !force) {
        return {
          ...existing,
          index: i + 1,
          category: def.category || existing.category
        };
      }
      return {
        id: def.id || `comp-${i + 1}`,
        index: i + 1,
        name: def.name,
        category: def.category || 'Geral',
        status: "PENDING",
        condition: "",
        technicalComment: "",
        photos: [],
        recommendations: [],
        isExpanded: false,
        isManual: false
      };
    });

    // Append manual items at the end with updated indices
    manualItems.forEach((mItem, mIdx) => {
      newItems.push({
        ...mItem,
        index: newItems.length + 1
      } as any);
    });

    this.items = newItems as any;
  }

  private async loadData() {
    this.isHydratingContext = true;
    try {
      const p = window.app.platform;
      const ctx = {
        userId: contextStore.get().userId || 'USER-1',
        correlationId: `cid-${Date.now()}`,
        permissions: contextStore.get().permissions || new Set(),
        activeClientId: contextStore.get().activeClientId
      } as any;

      if (p?.adminService) {
        this.clients = await this.ctxLoad.load('Clientes', () => p.adminService.listAuthorizedClients(ctx));
        this.clientsLoadState = this.ctxLoad.statusOf('Clientes') === 'ok' ? 'LOADED' : 'ERROR';
        this.professionals = await this.ctxLoad.load('Profissionais', () => p.adminService.listProfessionals(ctx, { role: 'INSPECTOR', status: 'ACTIVE' }));
        this.workOrders = await this.ctxLoad.load('Ordens de Serviço', () => p.adminService.listWorkOrdersForDocument(ctx));
        this.subWorkOrders = await this.ctxLoad.load('Sub-OS', () => p.adminService.listSubWorkOrdersForDocument(ctx));
        try {
          this.subOsAccesses = (await (p.adminService as any).listUserAccesses?.(ctx)) || [];
        } catch {
          this.subOsAccesses = [];
        }
      }

      if (p?.equipmentService) {
        this.registeredEquipments = await this.ctxLoad.load('Equipamentos', () => p.equipmentService.listEquipments(ctx));
      }

      if (p?.projectService) {
        this.projects = await this.ctxLoad.load('Projetos', () => p.projectService.listProjects(undefined, undefined, ctx));
        this.unitAreas = await this.ctxLoad.load('Unidades / Áreas', () => p.projectService.listUnitAreas(undefined, undefined, ctx));
      }

      if (this.inspectionId && !this.reportId) {
        const inspection = await p.inspectionService.getInspectionById(ctx, this.inspectionId as InspectionId);
        if (inspection) {
          this.equipmentId = (inspection.equipmentId as string) || '';
          this.selectedProjectId = inspection.projectId || '';
          this.selectedUnitAreaId = inspection.unitAreaId || '';
          this.inspectionType = inspection.inspectionType || 'Inspeção Externa';
          this.natureState = inspection.nature === 'INITIAL' ? 'Inicial' : inspection.nature === 'EXTRAORDINARY' ? 'Extraordinária' : 'Periódica';
          this.modalityState = inspection.modality === 'INTERNAL' ? 'Interna' : inspection.modality === 'EXTERNAL' ? 'Externa' : 'Geral';
          this.conditionState = inspection.operationalCondition || 'Em parada';
          if (inspection.startDate) {
            this.startDate = inspection.startDate.slice(0, 10);
          }
          if (inspection.endDate) {
            this.endDate = inspection.endDate.slice(0, 10);
          }
          if (inspection.inspectorId) this.selectedInspectorId = inspection.inspectorId;
          if (inspection.workOrderId) this.selectedWorkOrderId = inspection.workOrderId;
          if (inspection.subWorkOrderId) this.selectedSubWorkOrderId = inspection.subWorkOrderId;
          if (this.selectedWorkOrderId) {
            const wo = this.workOrders.find(w => w.id === this.selectedWorkOrderId);
            if (wo?.code) this.selectedOsCode = wo.code;
          }
          if (this.selectedSubWorkOrderId) {
            const sub = this.subWorkOrders.find(s => s.id === this.selectedSubWorkOrderId);
            if (sub?.code) this.selectedSubOsCode = sub.code;
          }
          if (inspection.notes) {
            this.generalObservations = inspection.notes;
          }
        }
        const existingReport = await p.reportService.getReportByInspection(ctx, this.inspectionId as InspectionId);
        if (existingReport) {
          this.reportId = existingReport.id;
          if (existingReport.reportNumber && existingReport.reportNumber !== '-' && !existingReport.reportNumber.includes('RI-NOVO') && !existingReport.reportNumber.includes('Elaboração')) {
            this.reportNumber = existingReport.reportNumber;
          }
          if ((existingReport as any).clientReportNumber) {
            this.clientReportNumber = (existingReport as any).clientReportNumber;
          }
          if (existingReport.inspectorId) this.selectedInspectorId = existingReport.inspectorId;
          if ((existingReport as any).workOrderId) this.selectedWorkOrderId = (existingReport as any).workOrderId;
          if ((existingReport as any).subWorkOrderId) this.selectedSubWorkOrderId = (existingReport as any).subWorkOrderId;
          if ((existingReport as any).osNumber) this.selectedOsCode = (existingReport as any).osNumber;
          if ((existingReport as any).subOsNumber) this.selectedSubOsCode = (existingReport as any).subOsNumber;
          if (existingReport.inspectionObservations) {
            this.generalObservations = existingReport.inspectionObservations;
          }
          if ((existingReport as any).damageMechanisms) {
            this.damageMechanisms = (existingReport as any).damageMechanisms;
          }
          if ((existingReport as any).maintenanceServices) {
            this.maintenanceServices = (existingReport as any).maintenanceServices;
          }
          if ((existingReport as any).performedInspectionMethods && Array.isArray((existingReport as any).performedInspectionMethods)) {
            this.performedTests = (existingReport as any).performedInspectionMethods;
          }
          if (existingReport.status === 'FINALIZED_BY_INSPECTOR' || existingReport.status === 'APPROVED' || existingReport.status === 'SIGNED' || existingReport.status === 'COMPLETED') {
            this.isLocked = true;
          }
        }
      } else if (this.reportId) {
        const report = await p.reportService.getReportById(ctx, this.reportId as IntegrityReportId);
        if (report) {
          this.inspectionId = report.inspectionId;
          if (report.reportNumber && report.reportNumber !== '-' && !report.reportNumber.includes('RI-NOVO') && !report.reportNumber.includes('Elaboração')) {
            this.reportNumber = report.reportNumber;
          }
          if ((report as any).clientReportNumber) {
            this.clientReportNumber = (report as any).clientReportNumber;
          }
          if (report.inspectorId) this.selectedInspectorId = report.inspectorId;
          if ((report as any).workOrderId) this.selectedWorkOrderId = (report as any).workOrderId;
          if ((report as any).subWorkOrderId) this.selectedSubWorkOrderId = (report as any).subWorkOrderId;
          if ((report as any).osNumber) this.selectedOsCode = (report as any).osNumber;
          if ((report as any).subOsNumber) this.selectedSubOsCode = (report as any).subOsNumber;

          const inspection = await p.inspectionService.getInspectionById(ctx, report.inspectionId as any);
          if (inspection) {
            this.equipmentId = (inspection.equipmentId as string) || '';
            this.equipmentTag = (inspection.equipmentId as string) || (this.registeredEquipments[0]?.tag || '');
            this.selectedProjectId = inspection.projectId || this.selectedProjectId || '';
            this.selectedUnitAreaId = inspection.unitAreaId || this.selectedUnitAreaId || '';
            if (inspection.workOrderId) this.selectedWorkOrderId = inspection.workOrderId;
            if (inspection.subWorkOrderId) this.selectedSubWorkOrderId = inspection.subWorkOrderId;
            if (this.selectedWorkOrderId) {
              const wo = this.workOrders.find(w => w.id === this.selectedWorkOrderId);
              if (wo?.code) this.selectedOsCode = wo.code;
            }
            if (this.selectedSubWorkOrderId) {
              const sub = this.subWorkOrders.find(s => s.id === this.selectedSubWorkOrderId);
              if (sub?.code) this.selectedSubOsCode = sub.code;
            }
            this.inspectionType = inspection.inspectionType || 'Inspeção Externa';
            this.natureState = inspection.nature === 'INITIAL' ? 'Inicial' : inspection.nature === 'EXTRAORDINARY' ? 'Extraordinária' : 'Periódica';
            this.modalityState = inspection.modality === 'INTERNAL' ? 'Interna' : inspection.modality === 'EXTERNAL' ? 'Externa' : 'Geral';
            this.conditionState = inspection.operationalCondition || 'Em parada';
            if (inspection.startDate) {
              this.startDate = inspection.startDate.slice(0, 10);
            }
            if (inspection.endDate) {
              this.endDate = inspection.endDate.slice(0, 10);
            }
            if (!this.selectedInspectorId && inspection.inspectorId) this.selectedInspectorId = inspection.inspectorId;
          }
          if (report.inspectionObservations) {
            this.generalObservations = report.inspectionObservations;
          }
          if ((report as any).damageMechanisms) {
            this.damageMechanisms = (report as any).damageMechanisms;
          }
          if ((report as any).maintenanceServices) {
            this.maintenanceServices = (report as any).maintenanceServices;
          }
          if ((report as any).performedInspectionMethods && Array.isArray((report as any).performedInspectionMethods)) {
            this.performedTests = (report as any).performedInspectionMethods;
          }

          this.reportStatus = report.status || null;

          if (report.status === 'FINALIZED_BY_INSPECTOR' || report.status === 'SUBMITTED_TO_ENGINEERING' || report.status === 'APPROVED' || report.status === 'SIGNED' || report.status === 'COMPLETED' || report.status === 'UNDER_ENGINEERING_REVIEW') {
            this.isLocked = true;
          }
        }
      }

      // Check local storage metadata cache if available
      if (this.inspectionId) {
        const cachedMeta = localStorage.getItem(`ri_meta_${this.inspectionId}`);
        if (cachedMeta) {
          try {
            const parsedMeta = JSON.parse(cachedMeta);
            if (parsedMeta.inspectorId) this.selectedInspectorId = parsedMeta.inspectorId;
            if (parsedMeta.workOrderId) this.selectedWorkOrderId = parsedMeta.workOrderId;
            if (parsedMeta.subWorkOrderId) this.selectedSubWorkOrderId = parsedMeta.subWorkOrderId;
            if (parsedMeta.osNumber) this.selectedOsCode = parsedMeta.osNumber;
            if (parsedMeta.subOsNumber) this.selectedSubOsCode = parsedMeta.subOsNumber;
            if (parsedMeta.reportNumber && parsedMeta.reportNumber !== '-' && !parsedMeta.reportNumber.includes('RI-NOVO') && !parsedMeta.reportNumber.includes('Elaboração')) {
              this.reportNumber = parsedMeta.reportNumber;
            }
            if (parsedMeta.clientReportNumber) this.clientReportNumber = parsedMeta.clientReportNumber;
            if (parsedMeta.damageMechanisms) this.damageMechanisms = parsedMeta.damageMechanisms;
            if (parsedMeta.generalObservations) this.generalObservations = parsedMeta.generalObservations;
            if (parsedMeta.maintenanceServices) this.maintenanceServices = parsedMeta.maintenanceServices;
            
            // New fields persistence
            if (parsedMeta.projectId) this.selectedProjectId = parsedMeta.projectId;
            if (parsedMeta.unitAreaId) this.selectedUnitAreaId = parsedMeta.unitAreaId;
            if (parsedMeta.equipmentId) this.equipmentId = parsedMeta.equipmentId;
            if (parsedMeta.equipmentTag) this.equipmentTag = parsedMeta.equipmentTag;
            if (parsedMeta.conditionState) this.conditionState = parsedMeta.conditionState;
            if (parsedMeta.natureState) this.natureState = parsedMeta.natureState;
            if (parsedMeta.modalityState) this.modalityState = parsedMeta.modalityState;
            if (parsedMeta.startDate) this.startDate = parsedMeta.startDate;
            if (parsedMeta.endDate) this.endDate = parsedMeta.endDate;
          } catch {}
        }
      }

      // Default inspector if not yet assigned
      if (!this.selectedInspectorId) {
        const auth = contextStore.get();
        const userProfId = auth.professionalId || auth.userId;
        const matchUser = this.professionals.find(p => p.id === userProfId || p.userId === userProfId || (p.email && auth.userEmail && p.email.toLowerCase() === auth.userEmail.toLowerCase()) || (p.name && auth.userName && p.name.toLowerCase() === auth.userName.toLowerCase()));
        if (matchUser) {
          this.selectedInspectorId = matchUser.id;
        } else if (this.professionals.length > 0) {
          this.selectedInspectorId = this.professionals[0].id;
        } else if (userProfId) {
          this.selectedInspectorId = userProfId;
        }
      }

      // Resolve selectedClientId from equipment or first client
      if (this.equipmentTag && !this.equipmentId) {
        const matchEq = this.registeredEquipments.find(e => e.tag === this.equipmentTag || e.id === this.equipmentTag);
        if (matchEq) {
          this.equipmentId = matchEq.id;
          this.activeEquipmentSnapshot = matchEq;
          if (matchEq.clientId) {
            this.selectedClientId = matchEq.clientId;
            this.equipmentName = matchEq.name || matchEq.description || 'Equipamento';
          }
          if (matchEq.projectId && !this.selectedProjectId) this.selectedProjectId = matchEq.projectId;
          if (matchEq.unitAreaId && !this.selectedUnitAreaId) this.selectedUnitAreaId = matchEq.unitAreaId;
        }
      }
      if (this.equipmentId && !this.equipmentTag) {
        const matchEq = this.registeredEquipments.find(e => e.id === this.equipmentId);
        if (matchEq) {
          this.equipmentTag = matchEq.tag || matchEq.id;
          this.activeEquipmentSnapshot = matchEq;
          if (matchEq.clientId) {
            this.selectedClientId = matchEq.clientId;
            this.equipmentName = matchEq.name || matchEq.description || 'Equipamento';
          }
          if (matchEq.projectId && !this.selectedProjectId) this.selectedProjectId = matchEq.projectId;
          if (matchEq.unitAreaId && !this.selectedUnitAreaId) this.selectedUnitAreaId = matchEq.unitAreaId;
        }
      }
      if ((this.equipmentId || this.equipmentTag) && !this.activeEquipmentSnapshot) {
        const match = this.registeredEquipments.find(e => e.id === this.equipmentId || e.tag === this.equipmentTag || e.id === this.equipmentTag || e.tag === this.equipmentId);
        if (match) {
          this.activeEquipmentSnapshot = match;
          this.equipmentId = match.id;
          this.equipmentTag = match.tag || match.id;
          if (!this.equipmentName) this.equipmentName = match.name || match.description || 'Equipamento';
        }
      }
      if (!this.selectedClientId && this.clients.length > 0) {
        this.selectedClientId = this.clients[0].id;
      }
      const activeCli = this.clients.find(c => c.id === this.selectedClientId);
      this.clientName = activeCli?.name || activeCli?.legalName || '';

      if (this.selectedClientId && (!this.equipmentTag && !this.equipmentId)) {
        const clientEquips = this.registeredEquipments.filter(e => e.clientId === this.selectedClientId);
        if (clientEquips.length > 0) {
          this.equipmentId = clientEquips[0].id;
          this.equipmentTag = clientEquips[0].tag || clientEquips[0].id;
          this.equipmentName = clientEquips[0].name || clientEquips[0].description || 'Equipamento';
        }
      }

      let loadedFromStorage = false;
      let loadedFromRepo = false;

      if (this.inspectionId) {
        try {
          const res = p.inspectionService?.getChecklistState
            ? await p.inspectionService.getChecklistState(ctx, this.inspectionId as InspectionId)
            : (p.inspectionService?.getChecklistForInspection
                ? await p.inspectionService.getChecklistForInspection(ctx, this.inspectionId as InspectionId)
                : null);

          if (res && res.checklist && res.components && res.components.length > 0) {
            const mappedItems = res.components.map((c: any, idx: number) => {
              const compPhotos = (res.photos || [])
                .filter((ph: any) => ph.componentId === c.id)
                .map((ph: any) => ({
                  id: ph.id,
                  url: ph.fileUrl || ph.url || ph.image,
                  comment: ph.comment || ''
                }));

              return {
                id: c.id || `comp-${idx + 1}`,
                index: idx + 1,
                name: c.definitionComponentId || c.groupId || `Componente ${idx + 1}`,
                status: (['APPROVED','REJECTED','NOT_APPLICABLE','CLIENT_DISCRETION','PENDING'].includes(c.result) ? c.result : 'PENDING') as VisualChecklistItem['status'],
                condition: c.description || (c.result === 'APPROVED' ? 'Sem anomalias (Aprovado)' : ''),
                technicalComment: c.inspectionNotes || '',
                photos: compPhotos,
                recommendations: Array.isArray(c.recommendations) ? c.recommendations : [],
                isExpanded: c.result === 'REJECTED'
              };
            });
            this.items = mappedItems;
            loadedFromRepo = true;
          }
        } catch (errCheck) {
          console.warn("Checklist load fallback:", errCheck);
        }
      }

      // Clean up any legacy laudo/report checklist keys to ensure strict 1 INSPEÇÃO -> 1 CHECKLIST rule
      if (this.reportId) {
        try {
          localStorage.removeItem(`ri_checklist_${this.reportId}`);
          localStorage.removeItem(`ri_cache_${this.reportId}`);
        } catch {
          // ignore
        }
      }

      // Load or merge inspection-level local storage cache
      if (this.inspectionId) {
        const storedInspChk = localStorage.getItem(`ri_checklist_insp_${this.inspectionId}`);
        if (storedInspChk) {
          try {
            const parsed = JSON.parse(storedInspChk);
            if (Array.isArray(parsed) && parsed.length > 0) {
              if (!loadedFromRepo) {
                this.items = (parsed.map((item: any) => {
                  if (item.status === 'CONFORMING') item.status = 'APPROVED';
                  if (item.status === 'NON_CONFORMING') item.status = 'REJECTED';
                  if (item.status === 'NOT_INSPECTED') item.status = 'PENDING';
                  if (!['APPROVED','REJECTED','NOT_APPLICABLE','CLIENT_DISCRETION','PENDING'].includes(item.status)) item.status = 'PENDING';
                  return item;
                }) as VisualChecklistItem[]);
                loadedFromStorage = true;

                // Automatic idempotent migration of legacy localStorage checklist to SQLite backend
                if (p.inspectionService?.saveChecklistState) {
                  try {
                    const saveRes = await p.inspectionService.saveChecklistState(ctx, {
                      inspectionId: this.inspectionId as InspectionId,
                      components: this.items
                    });
                    if (saveRes && saveRes.components && saveRes.components.length > 0) {
                      loadedFromRepo = true;
                    }
                  } catch (migErr) {
                    console.warn("[RI-CHECKLIST-MIGRATION] Failed to sync legacy checklist to backend:", migErr);
                  }
                }
              } else {
                // Merge cached recommendations, comments, and photos with repo items
                for (const item of this.items) {
                  const matchCached = parsed.find((p: any) => p.id === item.id || p.name === item.name);
                  if (matchCached) {
                    if (matchCached.recommendations && matchCached.recommendations.length > 0) {
                      // --- PROTECTED CONFIGURATION ---
                      // DO NOT CHANGE THIS MERGE LOGIC. 
                      // It guarantees that unsaved edits made in the "Recommendations" page 
                      // (which live in the ri_checklist_insp_* cache) take precedence over 
                      // the stale data in checklistRepo. 
                      // -------------------------------
                      // Merge recommendations from cache, prioritizing cached text since it may have been edited in Recommendations Page
                      if (!item.recommendations || item.recommendations.length === 0) {
                        item.recommendations = matchCached.recommendations;
                      } else {
                        for (const cachedRec of matchCached.recommendations) {
                          const existingRec = item.recommendations.find((r: any) => r.id === cachedRec.id || r.code === cachedRec.code);
                          if (existingRec) {
                            if (cachedRec.description) existingRec.description = cachedRec.description;
                            if (cachedRec.scope) existingRec.scope = cachedRec.scope;
                            if (cachedRec.zr) existingRec.zr = cachedRec.zr;
                          } else {
                            item.recommendations.push(cachedRec);
                          }
                        }
                      }
                    }
                    if (!item.technicalComment && matchCached.technicalComment) {
                      item.technicalComment = matchCached.technicalComment;
                    }
                    if ((!item.photos || item.photos.length === 0) && matchCached.photos && matchCached.photos.length > 0) {
                      item.photos = matchCached.photos;
                    }
                  }
                }
              }
            }
          } catch (e) {
            console.warn("Could not parse stored inspection checklist", e);
          }
        }
      }

      // Merge any existing report recommendations from reportService
      if (this.reportId && p.reportService?.listRecommendations) {
        try {
          const reportRecs = await p.reportService.listRecommendations(ctx, this.reportId as IntegrityReportId);
          if (reportRecs && reportRecs.length > 0) {
            for (const item of this.items) {
              const itemNorm = (item.name || '').toLowerCase().trim();
              const itemIdNorm = (item.id || '').toLowerCase();
              const matchRecs = reportRecs.filter(r => {
                if (r.checklistItemId && (r.checklistItemId === item.id || (r.checklistItemId as string).toLowerCase() === itemIdNorm)) return true;
                if (r.componentId && (r.componentId === item.id || (r.componentId as string).toLowerCase() === itemIdNorm)) return true;
                if (r.componentName && r.componentName.toLowerCase().trim() === itemNorm) return true;
                if (r.scope && itemNorm && (r.scope.toLowerCase().startsWith(itemNorm + ':') || r.scope.toLowerCase().startsWith(itemNorm + ' -'))) return true;
                return false;
              });
              if (matchRecs.length > 0) {
                if (!item.recommendations) item.recommendations = [];
                for (const mr of matchRecs) {
                  const existingRec = item.recommendations.find(ir => ir.id === mr.id || ir.code === mr.code || (mr.recommendationNumber && ir.code === mr.recommendationNumber));
                  if (!existingRec) {
                    item.recommendations.push({
                      id: mr.id,
                      code: mr.code || mr.recommendationNumber || '',
                      criticality: mr.criticality === 'HIGH' || mr.criticality === 'CRITICAL' ? 'ALTA' : (mr.criticality === 'MODERATE' ? 'MÉDIA' : 'BAIXA'),
                      discipline: mr.discipline as any,
                      execution: mr.executeIn === 'SHUTDOWN' ? 'PARADA' : 'ROTINA',
                      description: mr.scope && item.name && mr.scope.startsWith(item.name + ':') ? mr.scope.replace(item.name + ':', '').trim() : mr.scope,
                      zr: mr.zr || ''
                    });
                  } else {
                    // Sync updated ZR and description from the official source back to the checklist view
                    if (existingRec.zr !== (mr.zr || '')) {
                      existingRec.zr = mr.zr || '';
                    }
                    const updatedDesc = mr.scope && item.name && mr.scope.startsWith(item.name + ':') ? mr.scope.replace(item.name + ':', '').trim() : (mr.scope || '');
                    if (updatedDesc && existingRec.description !== updatedDesc) {
                      existingRec.description = updatedDesc;
                    }
                  }
                }
              }
            }
          }
        } catch (eRecs) {
          console.warn("Could not load existing recommendations for report:", eRecs);
        }
      }

      // removed redundant repo fetch

      if (!loadedFromRepo && !loadedFromStorage) {
        await this.generateChecklistForSelectedEquipment();
      }

      // Persist to ensure active keys are initialized
      this.persistLocally();
      
      if (this.inspectionId) {
        const storedLock = localStorage.getItem(`ri_locked_${this.inspectionId}`);
        if (storedLock === 'true') {
          this.isLocked = true;
        }

        let storedBookNum = localStorage.getItem(`ri_book_num_${this.inspectionId}`);
        if (!storedBookNum) {
          const yr = new Date().getFullYear();
          const cleanId = this.inspectionId.replace(/[^a-zA-Z0-9]/g, '').slice(-6).toUpperCase() || '0001';
          storedBookNum = `BOOK-${yr}-${cleanId}`;
          localStorage.setItem(`ri_book_num_${this.inspectionId}`, storedBookNum);
        }
        this.bookNumber = storedBookNum;

        const storedTests = localStorage.getItem(`ri_performed_tests_${this.inspectionId}`);
        if (storedTests && (!this.performedTests || this.performedTests.length === 0)) {
          try {
            const data = JSON.parse(storedTests);
            if (data.performedTests && Array.isArray(data.performedTests)) {
              if (data.performedTests.length > 0 && typeof data.performedTests[0] === 'string') {
                // Migration from string[] to PerformedTestRecord[]
                this.performedTests = data.performedTests.map((t: string) => ({
                  methodId: t,
                  result: undefined,
                  comment: '',
                  otherName: t === 'OTHER' ? data.otherTestDescription : undefined
                }));
              } else {
                this.performedTests = data.performedTests;
              }
            } else {
              this.performedTests = [];
            }
            this.otherTestDescription = data.otherTestDescription || '';
          } catch {
            /* ignore parse error */
          }
        }
        
        const storedDocs = localStorage.getItem(`ri_ref_docs_${this.inspectionId}`);
        if (storedDocs) {
          try {
            this.referenceDocuments = JSON.parse(storedDocs);
          } catch {
            /* ignore parse error */
          }
        }
      }
    } catch (e) {
      console.warn("Could not load backend report data, using active workspace state", e);
    } finally {
      this.isHydratingContext = false;
    }
    if (this.inspectionId) {
      await this.loadMinutaContext();
    }
    this.render();
  }

  private async loadMinutaContext() {
    if (!this.inspectionId) return;
    try {
      const p = (window as any).app?.platform;
      const ctx = sessionRequestContext();
      if (p?.engineeringService) {
        const minutas = await p.minutaService.listByInspection(ctx, this.inspectionId);
        if (minutas && minutas.length > 0) {
          const workspace = await p.engineeringService.getMinutaWorkspaceContext(ctx, minutas[0].id);
          this.currentMinuta = workspace.minuta;
          this.minutaCorrections = workspace.corrections || [];
          this.currentMinutaSnapshotRecIds.clear();
          
          const revs = await p.minutaService.getRevisions(ctx, this.currentMinuta.id);
          const currentRev = revs.find((r: any) => r.status === 'ATUAL') || revs[revs.length - 1];
          if (currentRev?.snapshotData) {
            const model = currentRev.snapshotData;
            if (Array.isArray(model.groups)) {
              for (const g of model.groups) {
                if (Array.isArray(g.recommendations)) {
                  for (const r of g.recommendations) {
                    const key = r.recommendationId || r.id;
                    if (key) this.currentMinutaSnapshotRecIds.add(key);
                  }
                }
              }
            }
          }
        }
      }
    } catch (e) {
      console.warn("Could not load minuta for visual checklist:", e);
    }
  }

  private renderMinutaRecBadge(recId: string): string {
    if (!this.currentMinuta) return '';
    const isIncluded = this.currentMinutaSnapshotRecIds.has(recId);
    const revNum = this.currentMinuta.currentRevision || '00';

    if (isIncluded) {
      return `
        <span style="font-size: 0.7rem; background: #dcfce7; color: #166534; padding: 2px 6px; border-radius: 4px; font-weight: 700; display: inline-flex; align-items: center; gap: 4px;" title="Contemplada na Minuta REV. ${revNum}">
          <i data-lucide="check" style="width: 10px; height: 10px;"></i> MINUTA: REV ${revNum} — INCLUÍDA
        </span>
      `;
    } else {
      return `
        <div style="display: flex; flex-direction: column; gap: 2px; width: 100%; margin-top: 4px;">
          <div style="display: flex; align-items: center; gap: 6px;">
            <span style="font-size: 0.7rem; background: #fef3c7; color: #92400e; padding: 2px 6px; border-radius: 4px; font-weight: 700; display: inline-flex; align-items: center; gap: 4px;" title="Pendente de inclusão documental">
              <i data-lucide="clock" style="width: 10px; height: 10px;"></i> MINUTA: PENDENTE DE INCLUSÃO
            </span>
            <span style="font-size: 0.72rem; color: #b45309; font-style: italic; font-weight: 500;">
              “Esta recomendação ainda não está contemplada na Minuta REV. ${revNum}.”
            </span>
          </div>
          <div style="font-size: 0.68rem; font-weight: 700; color: #92400e;">
            Status: PENDENTE DE ATUALIZAÇÃO DOCUMENTAL.
          </div>
        </div>
      `;
    }
  }

  private persistLocally() {
    this.hasUnsavedChanges = true;
    try {
      if (this.reportId) {
        // Enforce removal of any parallel report-level checklist
        localStorage.removeItem(`ri_checklist_${this.reportId}`);
        localStorage.removeItem(`ri_cache_${this.reportId}`);
      }
      if (this.inspectionId) {
        localStorage.setItem(`ri_locked_${this.inspectionId}`, this.isLocked ? 'true' : 'false');
        if (this.bookNumber) {
          localStorage.setItem(`ri_book_num_${this.inspectionId}`, this.bookNumber);
        }
        localStorage.setItem(`ri_checklist_insp_${this.inspectionId}`, JSON.stringify(this.items));
        localStorage.setItem(`ri_performed_tests_${this.inspectionId}`, JSON.stringify({
          performedTests: this.performedTests,
          otherTestDescription: this.otherTestDescription
        }));
        localStorage.setItem(`ri_ref_docs_${this.inspectionId}`, JSON.stringify(this.referenceDocuments));
        localStorage.setItem(`ri_meta_${this.inspectionId}`, JSON.stringify({
          inspectorId: this.selectedInspectorId,
          workOrderId: this.selectedWorkOrderId,
          subWorkOrderId: this.selectedSubWorkOrderId,
          osNumber: this.selectedOsCode,
          subOsNumber: this.selectedSubOsCode,
          reportNumber: this.reportNumber,
          clientReportNumber: this.clientReportNumber,
          damageMechanisms: this.damageMechanisms,
          generalObservations: this.generalObservations,
          maintenanceServices: this.maintenanceServices,
          projectId: this.selectedProjectId,
          unitAreaId: this.selectedUnitAreaId,
          equipmentId: this.equipmentId,
          equipmentTag: this.equipmentTag,
          conditionState: this.conditionState,
          natureState: this.natureState,
          modalityState: this.modalityState,
          startDate: this.startDate,
          endDate: this.endDate
        }));
      }
      localStorage.setItem('ri_checklist_current', JSON.stringify(this.items));
      window.dispatchEvent(new CustomEvent('ri_checklist_updated', {
        detail: { reportId: this.reportId, inspectionId: this.inspectionId, items: this.items }
      }));

      // Check if Minuta should be marked as ATUALIZAÇÃO PENDENTE
      if (this.currentMinuta && this.inspectionId) {
        let hasUncoveredRec = false;
        for (const item of this.items) {
          if (item.recommendations) {
            for (const r of item.recommendations) {
              if (r.id && !this.currentMinutaSnapshotRecIds.has(r.id)) {
                hasUncoveredRec = true;
                break;
              }
            }
          }
          if (hasUncoveredRec) break;
        }

        // Só existe "ATUALIZAÇÃO PENDENTE" quando uma Minuta JÁ EMITIDA ficou
        // desatualizada (mesma regra de recommendations-draft-page.ts). Uma
        // Minuta ainda em elaboração/análise (EM ANÁLISE, AGUARDANDO INSPETOR,
        // CONFORME, APROVADA, AGUARDANDO VALIDAÇÃO, CORREÇÃO NECESSÁRIA,
        // AGUARDANDO ANÁLISE, EM CORREÇÃO) não pode ser forçada para esse
        // status só porque o checklist tem uma recomendação nova ainda não
        // incluída no snapshot — persistLocally() roda a cada edição.
        if (hasUncoveredRec && this.currentMinuta.status === 'EMITIDA') {
          this.currentMinuta.status = 'ATUALIZAÇÃO PENDENTE';
          const p = (window as any).app?.platform;
          const ctx = sessionRequestContext();
          if (p?.minutaService) {
            p.minutaService.markUpdatePending(ctx, this.currentMinuta.id).catch(() => {});
          }
        }
      }
    } catch (e) {
      console.warn("Error persisting checklist locally:", e);
    }
  }

  // Calculation of KPIs
  private getKpis() {
    const total = this.items.length;
    const approved = this.items.filter(i => i.status === 'APPROVED' || (i.status as any) === 'CONFORMING').length;
    const rejected = this.items.filter(i => i.status === 'REJECTED' || (i.status as any) === 'NON_CONFORMING').length;
    const notApplicable = this.items.filter(i => i.status === 'NOT_APPLICABLE').length;
    const clientDiscretion = this.items.filter(i => (i.status as any) === 'CLIENT_DISCRETION').length;
    const withRecommendation = this.items.filter(i => (i.recommendations && i.recommendations.length > 0)).length;
    const pending = this.items.filter(i => !i.status || i.status === 'PENDING' || (i.status as any) === 'NOT_INSPECTED').length;

    return { total, approved, rejected, notApplicable, clientDiscretion, withRecommendation, pending };
  }

  // Filter items based on filter tabs and search query
  private getFilteredItems(): VisualChecklistItem[] {
    return this.items.filter(item => {
      // 1. Filter by search query
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase().trim();
        const matchName = item.name.toLowerCase().includes(q);
        const matchCond = item.condition.toLowerCase().includes(q);
        const matchComm = item.technicalComment.toLowerCase().includes(q);
        const matchRec = item.recommendations?.some(r => r.description.toLowerCase().includes(q) || r.discipline.toLowerCase().includes(q));
        if (!matchName && !matchCond && !matchComm && !matchRec) return false;
      }

      // 2. Filter by status
      if (this.currentFilter === 'PENDING') return item.status === 'PENDING';
      if (this.currentFilter === 'APPROVED') return item.status === 'APPROVED';
      if (this.currentFilter === 'REJECTED') return item.status === 'REJECTED';
      if (this.currentFilter === 'NOT_APPLICABLE') return item.status === 'NOT_APPLICABLE';
      if (this.currentFilter === 'WITH_RECOMMENDATION') return item.recommendations && item.recommendations.length > 0;
      if (this.currentFilter === 'WITH_PHOTOS') return item.photos && item.photos.length > 0;
      return true;
    });
  }

  private render() {
    const kpis = this.getKpis();
    const filtered = this.getFilteredItems();
    const clientProjects = this.projects.filter(p => !this.selectedClientId || p.clientId === this.selectedClientId);
    const projectUnitAreas = this.unitAreas.filter(u => !this.selectedProjectId || u.projectId === this.selectedProjectId);
    const clientEquipments = this.registeredEquipments.filter(e => {
      const matchClient = !this.selectedClientId || e.clientId === this.selectedClientId;
      const matchProj = !this.selectedProjectId || !e.projectId || e.projectId === this.selectedProjectId;
      const matchUnit = !this.selectedUnitAreaId || !e.unitAreaId || e.unitAreaId === this.selectedUnitAreaId || e.areaId === this.selectedUnitAreaId;
      return matchClient && matchProj && matchUnit;
    });
    const clientWorkOrders = this.workOrders.filter(wo => {
      if (this.selectedClientId && wo.clientId && wo.clientId !== this.selectedClientId) return false;
      return true;
    });

    // Compute Sub-OS for selected Inspector and Work Order
    const inspectorSubWorkOrders = this.subWorkOrders.filter(sub => {
      if (this.selectedClientId && sub.clientId && sub.clientId !== this.selectedClientId) return false;
      if (this.selectedWorkOrderId && sub.workOrderId !== this.selectedWorkOrderId) return false;
      if (this.selectedInspectorId) {
        const isDirect = sub.professionalId === this.selectedInspectorId;
        const isAccess = this.subOsAccesses.some(a =>
          a.professionalId === this.selectedInspectorId &&
          a.subWorkOrderId === sub.id &&
          (a.status === 'ACTIVE' || !a.status)
        );
        return isDirect || isAccess;
      }
      return true;
    });

    const displaySubWorkOrders = inspectorSubWorkOrders.length > 0 ? inspectorSubWorkOrders : this.subWorkOrders.filter(sub => {
      if (this.selectedClientId && sub.clientId && sub.clientId !== this.selectedClientId) return false;
      if (this.selectedWorkOrderId && sub.workOrderId !== this.selectedWorkOrderId) return false;
      return true;
    });

    const isHeaderLocked = this.isLocked;
    const missingInspectionFields = this.getMissingInspectionFields();
    const currentUserName = contextStore.get().userName.toLowerCase();
    const canEditClientReportNumber = currentUserName.includes('engenheir') || currentUserName.includes('doc') || currentUserName.includes('téc') || contextStore.hasPermission('ADMIN_MANAGE_USERS');

    this.innerHTML = `
      <style>${CONTEXT_LOAD_STYLES}</style>
      ${this.ctxLoad.banner()}
      <style>
        .checklist-page-container {
          display: flex;
          flex-direction: column;
          gap: 20px;
          max-width: 1240px;
          margin: 0 auto;
          padding: 8px 16px 100px 16px;
          font-family: var(--font-sans);
          color: var(--color-text-primary);
        }

        /* Top Header */
        .chk-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 16px;
          padding-bottom: 12px;
        }
        .chk-title-group h1 {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0;
          letter-spacing: -0.02em;
        }
        .chk-title-group p {
          margin: 4px 0 0 0;
          font-size: 0.8125rem;
          color: var(--color-text-secondary);
        }
        .chk-header-badges {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .chk-client-badge {
          background: var(--color-surface-secondary);
          color: var(--color-text-secondary);
          border: 1px solid var(--color-border-default);
          padding: 4px 10px;
          border-radius: var(--radius-sm);
          font-size: 0.6875rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }
        .chk-asset-pill {
          background: var(--color-surface-secondary);
          color: var(--color-text-primary);
          padding: 4px 10px;
          border-radius: var(--radius-sm);
          font-size: 0.6875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        /* Card container */
        .chk-card {
          background: var(--color-surface-primary);
          border: 1px solid var(--color-border-default);
          border-radius: var(--radius-sm);
          padding: 20px;
          box-shadow: var(--shadow-sm);
        }
        .chk-card-title {
          font-size: 0.9375rem;
          font-weight: 600;
          color: var(--color-text-primary);
          margin-bottom: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        /* Inputs Grid */
        .chk-form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
          margin-bottom: 14px;
        }
        .chk-field-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .chk-field-label {
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--color-text-secondary);
        }
        .chk-select, .chk-input, .chk-textarea {
          background: var(--color-surface-primary);
          border: 1px solid var(--color-border-default);
          border-radius: var(--radius-sm);
          padding: 8px 11px;
          font-size: 0.875rem;
          color: var(--color-text-primary);
          font-family: inherit;
          transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
          width: 100%;
        }
        .chk-select:focus, .chk-input:focus, .chk-textarea:focus {
          outline: none;
          border-color: var(--color-text-muted);
          box-shadow: 0 0 0 3px rgba(60, 60, 60, 0.1);
        }
        .chk-select::placeholder, .chk-input::placeholder, .chk-textarea::placeholder {
          color: var(--ia-fg-soft, #AEAEAE);
          opacity: 1;
        }
        .chk-textarea {
          width: 100%;
          min-height: 65px;
          resize: vertical;
        }
        .char-counter {
          font-size: 0.75rem;
          color: var(--ia-fg-soft, #AEAEAE);
          text-align: right;
          margin-top: 2px;
        }

        /* KPI Metric Cards Grid */
        .kpi-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
        }
        .kpi-card {
          background: var(--color-surface-primary);
          border: 1px solid var(--color-border-default);
          border-radius: var(--radius-sm);
          padding: 16px;
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: var(--shadow-sm);
          transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
        }
        .kpi-card:hover {
          border-color: var(--color-graphite-300);
          box-shadow: var(--shadow-md);
        }
        .kpi-icon-wrap {
          width: 44px;
          height: 44px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .kpi-icon-wrap.total { background: var(--color-surface-secondary); color: var(--color-text-secondary); }
        .kpi-icon-wrap.conforming { background: var(--status-success-bg); color: var(--status-success-text); }
        .kpi-icon-wrap.non-conforming { background: var(--status-danger-bg); color: var(--status-danger-solid); }
        .kpi-icon-wrap.recommendation { background: var(--status-warning-bg); color: var(--status-warning-text); }
        .kpi-icon-wrap.pending { background: var(--color-surface-secondary); color: var(--color-text-secondary); }

        .kpi-content {
          display: flex;
          flex-direction: column;
        }
        .kpi-number {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-text-muted);
          line-height: 1.2;
        }
        .kpi-label {
          font-size: 0.8rem;
          color: var(--color-text-secondary);
          font-weight: 500;
        }

        /* Filter and Action Bar */
        .filter-action-bar {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 12px;
          margin-bottom: 24px;
          background: var(--color-surface-secondary);
          border: 1px solid var(--color-border-default);
          border-radius: var(--radius-sm);
          padding: 14px;
        }
        .search-and-filters {
          display: flex;
          flex-direction: column;
          gap: 12px;
          width: 100%;
        }
        @media (min-width: 768px) {
          .search-and-filters {
            flex-direction: row;
            align-items: center;
          }
        }
        .search-box-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          min-width: 280px;
        }
        .search-box-wrapper i {
          position: absolute;
          left: 12px;
          width: 16px;
          height: 16px;
          color: var(--color-graphite-400);
          pointer-events: none;
        }
        .search-input {
          width: 100%;
          background: var(--color-surface-primary);
          border: 1px solid var(--color-border-default);
          border-radius: var(--radius-sm);
          padding: 8px 12px 8px 36px;
          font-size: 0.875rem;
          color: var(--color-text-primary);
          outline: none;
          transition: all var(--transition-fast);
        }
        .search-input:focus {
          border-color: var(--color-text-muted);
          box-shadow: 0 0 0 3px rgba(60, 60, 60, 0.1);
        }
        .filter-pill-group {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }
        .filter-pill-btn {
          border: 1px solid var(--color-border-default);
          background: var(--color-surface-primary);
          padding: 5px 12px;
          border-radius: var(--radius-sm);
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--color-text-secondary);
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all var(--transition-fast);
        }
        .filter-pill-btn:hover {
          background: var(--color-surface-secondary);
          border-color: var(--color-graphite-300);
          color: var(--color-text-primary);
        }
        .filter-pill-btn.active {
          background: var(--ia-action-bg, var(--color-primary));
          color: var(--ia-action-fg, #F7F8F8);
          border-color: var(--color-ink);
        }
        .btn-bulk-conforming {
          padding: 8px 16px;
          font-size: 0.775rem;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          border-radius: 8px;
          transition: all 0.15s ease;
          letter-spacing: 0.03em;
          text-transform: uppercase;
        }
        #btn-trigger-bulk {
          background: var(--status-success-bg);
          border: 1px solid var(--status-success-border);
          color: var(--status-success-text);
        }
        #btn-trigger-bulk:hover {
          background: var(--status-success-bg);
          border-color: var(--status-success-border);
        }
        #btn-add-manual-component {
          background: var(--color-graphite-800) !important;
          border: 1px solid var(--color-graphite-800) !important;
          color: var(--color-primary-text); !important;
        }
        #btn-add-manual-component:hover {
          background: var(--color-primary) !important;
          border-color: var(--color-primary) !important;
        }

        .filter-right-tools {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        .search-box-wrap {
          position: relative;
          display: flex;
          align-items: center;
        }
        .search-box-wrap i {
          position: absolute;
          left: 10px;
          width: 15px;
          height: 15px;
          color: var(--color-graphite-400);
          pointer-events: none;
        }
        .inp-search {
          background: var(--color-surface-primary);
          border: 1px solid var(--color-graphite-300);
          border-radius: 6px;
          padding: 6px 12px 6px 32px;
          font-size: 0.8rem;
          color: var(--ia-primary);
          width: 220px;
          outline: none;
        }
        .inp-search:focus {
          border-color: var(--color-text-muted);
        }

        .btn-bulk-approve {
          background: var(--status-success-bg);
          border: 1px solid var(--status-success-border);
          color: var(--status-success-text);
          padding: 6px 14px;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: background 0.15s ease;
        }
        .btn-bulk-approve:hover {
          background: var(--status-success-border);
        }

        /* Checklist Components Items List */
        .chk-items-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .chk-item-card {
          background: var(--color-surface-primary);
          border: 1px solid var(--color-border-default);
          border-radius: var(--radius-sm);
          overflow: hidden;
          transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
        }
        .chk-item-card.expanded {
          border-color: var(--color-graphite-400);
          box-shadow: var(--shadow-md);
        }

        .chk-item-main-row {
          padding: 14px 18px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
          background: var(--color-surface-primary);
        }
        .chk-item-left {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
          min-width: 260px;
        }
        .chk-item-index {
          width: 28px;
          height: 28px;
          background: var(--color-surface-secondary);
          color: var(--color-text-secondary);
          font-weight: 700;
          font-size: 0.8rem;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .chk-item-name {
          font-size: 0.95rem;
          font-weight: 600;
          color: var(--color-text-muted);
        }

        .chk-item-right {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        .status-select-wrap {
          position: relative;
          display: flex;
          align-items: center;
        }
        .status-select {
          appearance: none;
          padding: 7px 32px 7px 12px;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 700;
          cursor: pointer;
          border: 1px solid transparent;
          outline: none;
          transition: all 0.15s ease;
        }
        .status-select.status-pending {
          background: var(--color-surface-secondary);
          color: var(--color-text-secondary);
          border-color: var(--color-graphite-300);
        }
        .status-select.status-conforming {
          background: var(--status-success-bg);
          color: var(--status-success-text);
          border-color: var(--status-success-border);
        }
        .status-select.status-non-conforming {
          background: var(--status-danger-bg);
          color: var(--status-danger-text);
          border-color: var(--status-danger-border);
        }
        .status-select.status-na {
          background: var(--color-surface-secondary);
          color: var(--color-text-secondary);
          border-color: var(--color-graphite-300);
        }
        .status-select.recommendation {
          background: var(--status-warning-bg);
          color: var(--status-warning-text);
          border-color: var(--status-warning-border);
        }
        .status-chevron {
          position: absolute;
          right: 10px;
          width: 14px;
          height: 14px;
          pointer-events: none;
          opacity: 0.7;
        }

        .btn-toggle-details {
          background: var(--color-surface-primary);
          border: 1px solid var(--color-graphite-300);
          color: var(--color-text-secondary);
          padding: 7px 12px;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.15s ease;
        }
        .btn-toggle-details:hover {
          background: var(--color-bg-primary);
          color: var(--color-text-muted);
          border-color: var(--color-graphite-400);
        }

        /* Expanded Details Section */
        .chk-item-expanded-box {
          padding: 16px 18px 20px 18px;
          background: var(--color-bg-primary);
          border-top: 1px solid var(--color-graphite-200);
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .chk-detail-inputs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 16px;
        }

        .chk-actions-subbar {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          align-items: center;
        }
        .btn-sub-action {
          background: var(--color-surface-primary);
          border: 1px solid var(--color-graphite-300);
          border-radius: 6px;
          padding: 6px 12px;
          font-size: 0.775rem;
          font-weight: 600;
          color: var(--color-text-muted);
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.15s ease;
        }
        .btn-sub-action:hover {
          background: var(--color-surface-secondary);
        }
        .btn-sub-action.ai-btn {
          color: var(--ia-primary);
          border-color: var(--color-border-subtle);
          background: var(--status-info-bg);
        }
        .btn-sub-action.ai-btn:hover {
          background: var(--color-surface-secondary);
        }

        /* Photos Strip */
        .photos-preview-strip {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          margin-top: 6px;
        }
        .photo-thumb-card {
          width: 80px;
          height: 80px;
          border-radius: 6px;
          border: 1px solid var(--color-graphite-300);
          position: relative;
          overflow: hidden;
          background: var(--color-surface-secondary);
        }
        .photo-thumb-card img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .photo-thumb-delete {
          position: absolute;
          top: 2px;
          right: 2px;
          background: rgba(220, 38, 38, 0.8);
          color: var(--color-text-primary);
          border: none;
          border-radius: 4px;
          width: 20px;
          height: 20px;
          cursor: pointer;
        }

        /* Recommendations */
        .rec-section-container {
          background: var(--color-surface-primary);
          border: 1px solid var(--color-border-subtle);
          border-radius: 8px;
          padding: 14px 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .rec-section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .rec-section-title {
          font-size: 0.8rem;
          font-weight: 700;
          color: var(--status-info-text);
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }
        .rec-card {
          background: var(--color-bg-primary);
          border: 1px solid var(--color-graphite-300);
          border-radius: 8px;
          padding: 14px 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .rec-fields-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 12px;
        }
        .rec-crit-badge {
          padding: 3px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .rec-crit-badge.crit-alta { background: var(--status-danger-bg); color: var(--status-danger-solid); border: 1px solid var(--status-danger-border); }
        .rec-crit-badge.crit-média { background: var(--status-warning-bg); color: var(--status-warning-text); border: 1px solid var(--status-warning-border); }
        .rec-crit-badge.crit-baixa { background: var(--color-surface-secondary); color: var(--color-text-secondary); border: 1px solid var(--color-graphite-300); }
        .btn-del-rec {
          background: transparent;
          border: none;
          color: var(--status-danger-solid);
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .btn-del-rec:hover {
          text-decoration: underline;
        }

        /* Fixed Bottom Action Bar */
        .bottom-action-bar {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: var(--color-surface-primary);
          border-top: 1px solid var(--color-graphite-300);
          padding: 12px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          z-index: 100;
          box-shadow: 0 -4px 12px rgba(0,0,0,0.05);
        }
        .bottom-actions {
          display: flex;
          gap: 10px;
        }
        .btn-bottom {
          padding: 8px 16px;
          border-radius: 6px;
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
          border: 1px solid var(--color-graphite-300);
          background: var(--color-surface-primary);
        }
        .btn-bottom.primary {
          background: var(--color-graphite-800);
          color: var(--color-text-primary);
          border: none;
        }

        /* Modal */
        .custom-modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(15, 23, 42, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
        }
        .modal-card {
          background: var(--color-surface-primary);
          border-radius: 12px;
          padding: 24px;
          max-width: 480px;
          width: 100%;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }
        .btn-modal-cancel {
          flex: 1;
          padding: 10px 16px;
          border-radius: 6px;
          background: var(--color-surface-secondary);
          border: 1px solid var(--color-graphite-300);
          color: var(--color-text-secondary);
          font-weight: 600;
          cursor: pointer;
        }
        .btn-modal-confirm {
          flex: 1;
          padding: 10px 16px;
          border-radius: 6px;
          background: var(--color-graphite-800);
          border: 1px solid var(--color-graphite-800);
          color: var(--color-text-primary);
          font-weight: 600;
          cursor: pointer;
        }

        /* ===== WIZARD STEPPER ===== */
        .wizard-stepper {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px 24px;
          background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
          border-radius: 12px;
          margin-bottom: 24px;
          gap: 0;
          position: relative;
          overflow: hidden;
          border: 1px solid rgba(148, 163, 184, 0.15);
        }
        .wizard-stepper::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: 
            linear-gradient(90deg, transparent 49.5%, rgba(148,163,184,0.04) 49.5%, rgba(148,163,184,0.04) 50.5%, transparent 50.5%) 0 0 / 40px 40px,
            linear-gradient(0deg, transparent 49.5%, rgba(148,163,184,0.04) 49.5%, rgba(148,163,184,0.04) 50.5%, transparent 50.5%) 0 0 / 40px 40px;
          pointer-events: none;
        }
        .wizard-step {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.25s ease;
          position: relative;
          z-index: 1;
          user-select: none;
        }
        .wizard-step:hover {
          background: rgba(148, 163, 184, 0.1);
        }
        .wizard-step.active {
          background: rgba(59, 130, 246, 0.15);
          border: 1px solid rgba(59, 130, 246, 0.4);
        }
        .wizard-step.completed {
          5;
        }
        .wizard-step-number {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.85rem;
          flex-shrink: 0;
          transition: all 0.25s ease;
        }
        .wizard-step.future .wizard-step-number {
          background: rgba(148, 163, 184, 0.15);
          color: rgba(148, 163, 184, 0.5);
          border: 1px solid rgba(148, 163, 184, 0.2);
        }
        .wizard-step.active .wizard-step-number {
          background: var(--color-primary, #3b82f6);
          color: var(--color-text-primary);
          box-shadow: 0 0 12px rgba(59, 130, 246, 0.4);
          border: none;
        }
        .wizard-step.completed .wizard-step-number {
          background: rgba(34, 197, 94, 0.2);
          color: var(--ia-success-fg, #22c55e);
          border: 1px solid rgba(34, 197, 94, 0.3);
        }
        .wizard-step-info {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }
        .wizard-step-title {
          font-size: 0.8rem;
          font-weight: 700;
          color: var(--color-text-primary);
          white-space: nowrap;
        }
        .wizard-step.future .wizard-step-title {
          color: rgba(148, 163, 184, 0.5);
        }
        .wizard-step-subtitle {
          font-size: 0.68rem;
          color: var(--color-text-primary);
          white-space: nowrap;
        }
        .wizard-step.future .wizard-step-subtitle {
          color: rgba(148, 163, 184, 0.35);
        }
        .wizard-step-connector {
          width: 40px;
          height: 2px;
          background: rgba(148, 163, 184, 0.2);
          flex-shrink: 0;
          position: relative;
          z-index: 1;
        }
        .wizard-step-connector.completed {
          background: rgba(34, 197, 94, 0.4);
        }

        /* ===== STEP NAVIGATION BAR ===== */
        .step-nav-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
          border: 1px solid rgba(148, 163, 184, 0.15);
          border-radius: 10px;
          margin-top: 24px;
          flex-wrap: wrap;
          gap: 12px;
          position: sticky;
          bottom: 16px;
          z-index: 900;
        }
        .step-nav-bar .btn-step {
          padding: 10px 22px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.875rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s ease;
          border: none;
        }
        .step-nav-bar .btn-step-back {
          background: rgba(148, 163, 184, 0.15);
          color: var(--color-text-primary);
          border: 1px solid rgba(148, 163, 184, 0.25);
        }
        .step-nav-bar .btn-step-back:hover {
          background: rgba(148, 163, 184, 0.25);
        }
        .step-nav-bar .btn-step-save {
          background: rgba(59, 130, 246, 0.15);
          color: var(--ia-info-fg, #93c5fd);
          border: 1px solid rgba(59, 130, 246, 0.3);
        }
        .step-nav-bar .btn-step-save:hover {
          background: rgba(59, 130, 246, 0.25);
        }
        .step-nav-bar .btn-step-next {
          background: var(--color-primary, #3b82f6);
          color: var(--color-text-primary);
          box-shadow: 0 2px 10px rgba(59, 130, 246, 0.35);
        }
        .step-nav-bar .btn-step-next:hover {
          filter: brightness(1.1);
        }
        .step-nav-bar .btn-step-summary {
          background: linear-gradient(135deg, #059669 0%, #10b981 100%);
          color: var(--color-text-primary);
          box-shadow: 0 2px 10px rgba(16, 185, 129, 0.35);
        }
        .step-nav-bar .btn-step-summary:hover {
          filter: brightness(1.1);
        }
        .step-nav-bar .btn-step-report {
          background: #0284c7;
          color: var(--color-text-primary);
          box-shadow: 0 2px 10px rgba(2, 132, 199, 0.35);
        }
        .step-nav-bar .btn-step-report:hover {
          filter: brightness(1.1);
        }
        .step-nav-bar .btn-step-plh {
          background: linear-gradient(135deg, #475569 0%, #1e293b 100%);
          color: var(--color-text-primary);
          border: 1px solid rgba(148, 163, 184, 0.2);
        }
        .step-nav-bar .btn-step-plh:hover {
          background: linear-gradient(135deg, #64748b 0%, #334155 100%);
        }
        .step-nav-hint {
          font-size: 0.75rem;
          color: var(--color-text-primary);
          display: flex;
          align-items: center;
          gap: 6px;
        }

        /* ===== SUMMARY PAGE (ETAPA 4) ===== */
        .summary-section {
          margin-bottom: 24px;
        }
        .summary-section-title {
          font-size: 1rem;
          font-weight: 700;
          color: var(--color-ink);
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 8px;
          padding-bottom: 8px;
          border-bottom: 2px solid var(--color-graphite-200);
        }
        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 12px;
          background: var(--color-bg-primary);
          border: 1px solid var(--color-graphite-200);
          border-radius: 8px;
          padding: 16px;
        }
        .summary-field {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .summary-field-label {
          font-size: 0.72rem;
          font-weight: 600;
          color: var(--color-text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .summary-field-value {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--color-ink);
        }
        .summary-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.85rem;
        }
        .summary-table th {
          padding: 10px 14px;
          text-align: left;
          font-weight: 700;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: var(--color-text-secondary);
          border-bottom: 2px solid var(--color-graphite-200);
          background: var(--color-bg-primary);
        }
        .summary-table td {
          padding: 10px 14px;
          border-bottom: 1px solid var(--color-graphite-200);
          color: var(--color-ink);
        }
        .summary-kpi-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 12px;
        }
        .summary-kpi-card {
          background: var(--color-bg-primary);
          border: 1px solid var(--color-graphite-200);
          border-radius: 8px;
          padding: 16px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }
        .summary-kpi-number {
          font-size: 1.75rem;
          font-weight: 800;
          line-height: 1;
        }
        .summary-kpi-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--color-text-secondary);
          text-transform: uppercase;
        }

        @media (max-width: 768px) {
          .wizard-stepper {
            flex-direction: column;
            gap: 8px;
            padding: 16px;
          }
          .wizard-step-connector {
            width: 2px;
            height: 16px;
          }
          .wizard-step-info {
            display: none;
          }
          .step-nav-bar {
            flex-direction: column;
            gap: 8px;
          }
        }
        ${PLH_REVIEW_UI_STYLES}
        ${HOLD_UI_STYLES}
        ${CORRECTION_UI_STYLES}
        .checklist-page-container.on-hold > *:not(.hold-zone) { pointer-events: none; opacity: 0.55; filter: grayscale(0.3); }
        .hold-zone .hold-btn, .hold-zone .hold-release-btn { padding: 6px 14px; border-radius: 6px; font-size: 0.8rem; font-weight: 700; cursor: pointer; border: none; }
      </style>


      <div class="checklist-page-container ${this.hold ? "on-hold" : ""}">
        ${this.renderEngineeringZone()}
        <!-- RI Synchronization Banner -->
        <div style="background: linear-gradient(135deg, var(--color-graphite-800) 0%, var(--color-primary) 100%); border: 1px solid var(--color-graphite-700); border-radius: 8px; padding: 12px 18px; color: var(--color-text-primary); display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px;">
          <div style="display: flex; align-items: center; gap: 12px;">
            <div style="background: var(--color-primary); color: var(--ia-fg, #F7F8F8); width: 38px; height: 38px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 1rem; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">
              RI
            </div>
            <div>
              <div style="font-weight: 700; font-size: 0.95rem; display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                <span>Checklist Técnico do Relatório de Integridade (RI)</span>
                <span style="background: var(--status-success-bg); color: var(--status-success-text); border: 1px solid var(--status-success-border); font-size: 0.7rem; font-weight: 700; padding: 2px 8px; border-radius: 999px; text-transform: uppercase;">100% Sincronizado</span>
                ${this.isLocked ? `
                  <span style="background: var(--status-warning-bg); color: var(--status-warning-text); border: 1px solid var(--status-warning-border); font-size: 0.7rem; font-weight: 700; padding: 2px 8px; border-radius: 999px; text-transform: uppercase; display: flex; align-items: center; gap: 4px;">
                    <i data-lucide="lock" style="width: 11px; height: 11px;"></i> Modo Somente Leitura (Salvo)
                  </span>
                ` : ''}
              </div>
              <div style="font-size: 0.8rem; color: var(--color-graphite-400); margin-top: 2px;">
                Book de Inspeção Vinculado: <strong style="color: var(--color-text-primary);">${this.bookNumber || `BOOK-${new Date().getFullYear()}-${this.inspectionId ? this.inspectionId.slice(-6).toUpperCase() : '0001'}`}</strong> • Inspeção — <strong style="color: var(--color-text-primary);">${this.reportNumber || (this.inspectionId ? `RI-${this.inspectionId.slice(-6).toUpperCase()}` : 'RI-0001')}</strong> • Ativo: <strong style="color: var(--color-text-muted);">${this.equipmentTag || '-'}</strong>
              </div>
            </div>
          </div>

          <div style="display: flex; gap: 8px; align-items: center; flex-wrap: wrap;">
            ${!this.hold && this.inspectionId ? holdButton(null, !this.isLocked, "btn-sub-action") : ""}
            ${this.reportId ? `
              <button type="button" id="btn-top-view-ri" style="background: var(--color-primary); color: var(--color-text-muted); border: 1px solid var(--color-ink); padding: 6px 14px; border-radius: 6px; font-size: 0.8rem; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px;">
                <i data-lucide="eye" style="width: 14px; height: 14px;"></i> Visualizar Relatório (RI)
              </button>
            ` : `
              <button type="button" id="btn-top-goto-ris" style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border: none; padding: 6px 14px; border-radius: 6px; font-size: 0.8rem; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px;">
                <i data-lucide="file-text" style="width: 14px; height: 14px;"></i> Ir para Relatórios (RI) →
              </button>
            `}
          </div>
        </div>

        <!-- Wizard Stepper -->
        <div class="wizard-stepper">
          <div class="wizard-step ${this.currentStep === 1 ? 'active' : (this.currentStep > 1 ? 'completed' : 'future')}" data-step="1">
            <div class="wizard-step-number">${this.currentStep > 1 ? '✓' : '1'}</div>
            <div class="wizard-step-info">
              <span class="wizard-step-title">Dados do Equipamento</span>
              <span class="wizard-step-subtitle">Dados da inspeção e ativo</span>
            </div>
          </div>
          <div class="wizard-step-connector ${this.currentStep > 1 ? 'completed' : ''}"></div>
          <div class="wizard-step ${this.currentStep === 2 ? 'active' : (this.currentStep > 2 ? 'completed' : 'future')}" data-step="2">
            <div class="wizard-step-number">${this.currentStep > 2 ? '✓' : '2'}</div>
            <div class="wizard-step-info">
              <span class="wizard-step-title">Inspeções e Ensaios</span>
              <span class="wizard-step-subtitle">Defina os ensaios aplicáveis</span>
            </div>
          </div>
          <div class="wizard-step-connector ${this.currentStep > 2 ? 'completed' : ''}"></div>
          <div class="wizard-step ${this.currentStep === 3 ? 'active' : (this.currentStep > 3 ? 'completed' : 'future')}" data-step="3">
            <div class="wizard-step-number">${this.currentStep > 3 ? '✓' : '3'}</div>
            <div class="wizard-step-info">
              <span class="wizard-step-title">Check List</span>
              <span class="wizard-step-subtitle">Preencha os itens de verificação</span>
            </div>
          </div>
          <div class="wizard-step-connector ${this.currentStep > 3 ? 'completed' : ''}"></div>
          <div class="wizard-step ${this.currentStep === 4 ? 'active' : 'future'}" data-step="4">
            <div class="wizard-step-number">4</div>
            <div class="wizard-step-info">
              <span class="wizard-step-title">Resumo</span>
              <span class="wizard-step-subtitle">Revise e finalize</span>
            </div>
          </div>
        </div>

        <!-- Hidden header buttons kept for event handler compatibility -->
        <div style="display:none;">
          <button type="button" id="btn-header-back"></button>
          <button type="button" id="btn-footer-verify"></button>
          <button type="button" id="btn-footer-ai"></button>
          <button type="button" id="btn-header-release-plh"></button>
          <button type="button" id="btn-header-save"></button>
          <button type="button" id="btn-header-generate-ri"></button>
          <button type="button" id="btn-header-edit-checklist"></button>
        </div>

        <!-- ETAPA 1: Dados do Equipamento -->
        <div id="wizard-step-1" style="display: ${this.currentStep === 1 ? 'block' : 'none'};">
        <div class="chk-header" style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 12px; margin-bottom: 24px;">
          <div>
            <h1 class="chk-title"><i data-lucide="clipboard-check" style="width: 28px; height: 28px;"></i> Dados da Inspeção</h1>
            <p style="margin: 4px 0 0 0; font-size: 0.8125rem; color: var(--color-text-secondary);">Preencha os campos obrigatórios (*) para continuar. Estas informações serão utilizadas em todo o processo de inspeção.</p>
          </div>
          <div style="display: flex; gap: 8px;">
            ${this.isStep1Locked ? `
              <div style="font-size: 0.75rem; font-weight: 700; color: var(--status-success-text); background: var(--status-success-bg); padding: 6px 12px; border-radius: 6px; border: 1px solid var(--status-success-border); display: flex; align-items: center; gap: 6px;">
                <i data-lucide="lock" style="width: 14px; height: 14px;"></i> BLOQUEADO PARA EDIÇÃO
              </div>
            ` : `
              <div style="font-size: 0.75rem; font-weight: 600; color: var(--color-text-secondary); background: var(--color-surface-secondary); padding: 6px 12px; border-radius: 6px; border: 1px solid var(--color-graphite-200);">
                CAMPOS OBRIGATÓRIOS INDICADOS POR *
              </div>
            `}
          </div>
        </div>


        <!-- Dados da Inspeção Card -->
        <div class="chk-card">
          <div class="chk-card-title" style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;">
            <div style="display: flex; align-items: center; gap: 8px;">
              <span>Dados da Inspeção</span>
              ${isHeaderLocked ? `
                <span style="font-size: 0.72rem; font-weight: 700; background: var(--status-warning-bg); color: var(--status-warning-text); border: 1px solid var(--status-warning-border); padding: 3px 10px; border-radius: 999px; display: inline-flex; align-items: center; gap: 5px;">
                  <i data-lucide="lock" style="width: 12px; height: 12px; color: var(--status-warning-text);"></i> Relatório Finalizado / Bloqueado ${this.reportNumber ? `(Nº ${this.escapeHtml(this.reportNumber)})` : ''}
                </span>
              ` : (this.reportNumber && this.reportNumber !== '-' && !this.reportNumber.includes('RI-NOVO') ? `
                <span style="font-size: 0.72rem; font-weight: 600; background: var(--status-success-bg); color: var(--status-success-text); border: 1px solid var(--status-success-border); padding: 3px 10px; border-radius: 999px; display: inline-flex; align-items: center; gap: 5px;">
                  <i data-lucide="check-circle-2" style="width: 12px; height: 12px; color: var(--status-success-text);"></i> Nº Atribuído: ${this.escapeHtml(this.reportNumber)}
                </span>
              ` : (missingInspectionFields.length > 0 ? `
                <span style="font-size: 0.72rem; font-weight: 600; background: var(--color-surface-secondary); color: var(--color-text-secondary); border: 1px solid var(--color-graphite-300); padding: 3px 10px; border-radius: 999px; display: inline-flex; align-items: center; gap: 5px;" title="Pendências: ${missingInspectionFields.join(', ')}">
                  <i data-lucide="alert-circle" style="width: 12px; height: 12px; color: var(--color-ink);"></i> Preencha os campos obrigatórios (*) para finalizar
                </span>
              ` : `
                <span style="font-size: 0.72rem; font-weight: 600; background: var(--status-success-bg); color: var(--status-success-text); border: 1px solid var(--status-success-border); padding: 3px 10px; border-radius: 999px; display: inline-flex; align-items: center; gap: 5px;">
                  <i data-lucide="check-circle-2" style="width: 12px; height: 12px; color: var(--status-success-text);"></i> Dados obrigatórios preenchidos
                </span>
              `))}
            </div>
          </div>
          <div class="chk-form-grid" style="grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));">
            <div class="chk-field-group">
              <label class="chk-field-label">Cliente *</label>
              <select class="chk-input ${this.validationErrors.includes('clientId') ? 'invalid-field' : ''}" id="sel-chk-client" ${(this.inspectionId && this.isLocked) || this.isStep1Locked ? 'disabled' : ''}>
                <option value="">Selecione...</option>
                ${this.clients.map(c => `
                  <option value="${c.id}" ${this.selectedClientId === c.id ? 'selected' : ''}>
                    ${c.name || c.legalName}
                  </option>
                `).join('')}
              </select>
              ${this.validationErrors.includes('clientId') ? '<div style="color: var(--status-danger-text); font-size: 0.75rem; margin-top: 4px;">Campo obrigatório.</div>' : ''}
            </div>

            <div class="chk-field-group">
              <label class="chk-field-label">Inspetor Cadastrado (Elaborador) *</label>
              ${(() => {
                const perms = contextStore.get().permissions;
                const canAssign = perms && (perms.has(PermissionCode.ReportAssignInspector) || perms.has(PermissionCode.ReportReadAll));
                const isDisabled = (this.inspectionId && this.isLocked) || this.isStep1Locked;
                if (canAssign) {
                  return `
                    <select class="chk-input ${this.validationErrors.includes('inspectorId') ? 'invalid-field' : ''}" id="sel-chk-inspector" ${isDisabled ? 'disabled' : ''} title="Designar o Inspetor Responsável exige permissão de Engenharia/Administração.">
                      <option value="">Selecione o inspetor...</option>
                      ${this.professionals.map(p => `
                        <option value="${p.id}" ${this.selectedInspectorId === p.id ? 'selected' : ''}>
                          ${this.escapeHtml(p.name)}${p.snqc ? ` (SNQC: ${p.snqc})` : p.cft ? ` (CFT: ${p.cft})` : p.crea ? ` (CREA: ${p.crea})` : ''}
                        </option>
                      `).join('')}
                    </select>
                  `;
                }
                const auth = contextStore.get();
                const prof = (this.professionals || []).find((p: any) => String(p.id) === String(this.selectedInspectorId) || String(p.userId) === String(this.selectedInspectorId));
                const name = prof?.name || (this as any).inspectorName || auth.userName || 'Inspetor Responsável';
                const cred = prof ? (prof.snqc ? ` (SNQC: ${prof.snqc})` : prof.cft ? ` (CFT: ${prof.cft})` : prof.crea ? ` (CREA: ${prof.crea})` : prof.professionalType ? ` (${prof.professionalType})` : '') : '';
                return `<input class="chk-input ${this.validationErrors.includes('inspectorId') ? 'invalid-field' : ''}" id="sel-chk-inspector" readonly value="${this.escapeHtml(name)}${cred}" style="background: var(--color-surface-sunken, #f1f5f9); cursor: not-allowed;" title="Resolvido automaticamente pela sua conta de Inspetor. Designar outro Inspetor exige permissão de Engenharia/Administração." />`;
              })()}
              ${this.validationErrors.includes('inspectorId') ? '<div style="color: var(--status-danger-text); font-size: 0.75rem; margin-top: 4px;">Campo obrigatório.</div>' : ''}
            </div>

            <div class="chk-field-group">
              <label class="chk-field-label">Ordem de Serviço (OS) *</label>
              <select class="chk-input ${this.validationErrors.includes('workOrderId') ? 'invalid-field' : ''}" id="sel-chk-os" ${(this.inspectionId && this.isLocked) || this.isStep1Locked ? 'disabled' : ''}>
                <option value="">Selecione...</option>
                ${clientWorkOrders.map(wo => `<option value="${wo.id}" ${this.selectedWorkOrderId === wo.id ? 'selected' : ''}>${formatWorkOrderLabel(wo)}</option>`).join('')}
              </select>
              ${this.validationErrors.includes('workOrderId') ? '<div style="color: var(--status-danger-text); font-size: 0.75rem; margin-top: 4px;">Campo obrigatório.</div>' : ''}
            </div>

            <div class="chk-field-group">
              <label class="chk-field-label">Sub-OS do Inspetor *</label>
              <select class="chk-input ${this.validationErrors.includes('subWorkOrderId') ? 'invalid-field' : ''}" id="sel-chk-subos" ${(this.inspectionId && this.isLocked) || this.isStep1Locked ? 'disabled' : ''}>
                <option value="">Selecione...</option>
                ${displaySubWorkOrders.map(sub => `<option value="${sub.id}" ${this.selectedSubWorkOrderId === sub.id ? 'selected' : ''}>${formatSubWorkOrderLabel(sub)}</option>`).join('')}
              </select>
              ${this.validationErrors.includes('subWorkOrderId') ? '<div style="color: var(--status-danger-text); font-size: 0.75rem; margin-top: 4px;">Campo obrigatório.</div>' : ''}
            </div>

            <div class="chk-field-group">
              <label class="chk-field-label">Projeto *</label>
              <select class="chk-input ${this.validationErrors.includes('projectId') ? 'invalid-field' : ''}" id="sel-chk-project" ${(this.inspectionId && this.isLocked) || this.isStep1Locked ? 'disabled' : ''}>
                <option value="">Selecione...</option>
                ${clientProjects.map(p => `<option value="${p.id}" ${this.selectedProjectId === p.id ? 'selected' : ''}>${formatProjectLabel(p)}</option>`).join('')}
              </select>
              ${this.validationErrors.includes('projectId') ? '<div style="color: var(--status-danger-text); font-size: 0.75rem; margin-top: 4px;">Campo obrigatório.</div>' : ''}
            </div>

            <div class="chk-field-group">
              <label class="chk-field-label">Unidade / Área *</label>
              <select class="chk-input ${this.validationErrors.includes('unitAreaId') ? 'invalid-field' : ''}" id="sel-chk-unit" ${(this.inspectionId && this.isLocked) || this.isStep1Locked ? 'disabled' : ''}>
                <option value="">Selecione...</option>
                <option value="ALL" ${this.selectedUnitAreaId === 'ALL' ? 'selected' : ''}>Todas as Unidades / Áreas</option>
                ${projectUnitAreas.map(u => `<option value="${u.id}" ${this.selectedUnitAreaId === u.id ? 'selected' : ''}>${formatUnitAreaLabel(u)}</option>`).join('')}
              </select>
              ${this.validationErrors.includes('unitAreaId') ? '<div style="color: var(--status-danger-text); font-size: 0.75rem; margin-top: 4px;">Campo obrigatório.</div>' : ''}
            </div>

            <div class="chk-field-group" style="grid-column: span 2;">
              <label class="chk-field-label">Equipamento / Ativo *</label>
              <div style="display: flex; gap: 8px;">
                <select class="chk-input ${this.validationErrors.includes('equipmentId') ? 'invalid-field' : ''}" id="sel-chk-equipment" style="flex: 1;" ${(this.inspectionId && this.isLocked) || this.isStep1Locked ? 'disabled' : ''}>
                  <option value="">Selecione o equipamento...</option>
                  ${clientEquipments.map(eq => `<option value="${eq.id}" ${this.equipmentId === eq.id ? 'selected' : ''}>${eq.tag} — ${eq.name}</option>`).join('')}
                </select>
                <button type="button" class="btn-sub-action ai-btn" title="Buscar via IA" style="padding: 0 12px; height: 100%;" ${(this.inspectionId && this.isLocked) || this.isStep1Locked ? 'disabled' : ''}>
                  <i data-lucide="sparkles" style="width: 14px; height: 14px;"></i>
                </button>
              </div>
              ${this.validationErrors.includes('equipmentId') ? '<div style="color: var(--status-danger-text); font-size: 0.75rem; margin-top: 4px;">Campo obrigatório.</div>' : ''}
            </div>

            <div class="chk-field-group">
              <label class="chk-field-label">Condição *</label>
              <select class="chk-input ${this.validationErrors.includes('condition') ? 'invalid-field' : ''}" id="sel-chk-condition" ${(this.inspectionId && this.isLocked) || this.isStep1Locked ? 'disabled' : ''}>
                <option value="">Selecione...</option>
                <option value="Em operação" ${this.conditionState === 'Em operação' ? 'selected' : ''}>Em operação</option>
                <option value="Em parada" ${this.conditionState === 'Em parada' ? 'selected' : ''}>Em parada</option>
              </select>
              ${this.validationErrors.includes('condition') ? '<div style="color: var(--status-danger-text); font-size: 0.75rem; margin-top: 4px;">Campo obrigatório.</div>' : ''}
            </div>

            <div class="chk-field-group">
              <label class="chk-field-label">Natureza *</label>
              <select class="chk-input ${this.validationErrors.includes('nature') ? 'invalid-field' : ''}" id="sel-chk-nature" ${(this.inspectionId && this.isLocked) || this.isStep1Locked ? 'disabled' : ''}>
                <option value="">Selecione...</option>
                <option value="Inicial" ${this.natureState === 'Inicial' ? 'selected' : ''}>Inicial</option>
                <option value="Periódica" ${this.natureState === 'Periódica' ? 'selected' : ''}>Periódica</option>
                <option value="Extraordinária" ${this.natureState === 'Extraordinária' ? 'selected' : ''}>Extraordinária</option>
              </select>
              ${this.validationErrors.includes('nature') ? '<div style="color: var(--status-danger-text); font-size: 0.75rem; margin-top: 4px;">Campo obrigatório.</div>' : ''}
            </div>

            <div class="chk-field-group">
              <label class="chk-field-label">Modalidade *</label>
              <select class="chk-input ${this.validationErrors.includes('modality') ? 'invalid-field' : ''}" id="sel-chk-modality" ${(this.inspectionId && this.isLocked) || this.isStep1Locked ? 'disabled' : ''}>
                <option value="">Selecione...</option>
                <option value="Interna" ${this.modalityState === 'Interna' ? 'selected' : ''}>Interna</option>
                <option value="Externa" ${this.modalityState === 'Externa' ? 'selected' : ''}>Externa</option>
                <option value="Geral" ${this.modalityState === 'Geral' ? 'selected' : ''}>Geral</option>
              </select>
              ${this.validationErrors.includes('modality') ? '<div style="color: var(--status-danger-text); font-size: 0.75rem; margin-top: 4px;">Campo obrigatório.</div>' : ''}
            </div>

            <div class="chk-field-group">
              <label class="chk-field-label">Data Inicial *</label>
              <input type="date" class="chk-input ${this.validationErrors.includes('startDate') ? 'invalid-field' : ''}" id="inp-chk-start-date" value="${this.startDate || ''}" ${(this.inspectionId && this.isLocked) || this.isStep1Locked ? 'disabled' : ''}>
              ${this.validationErrors.includes('startDate') ? '<div style="color: var(--status-danger-text); font-size: 0.75rem; margin-top: 4px;">Campo obrigatório.</div>' : ''}
            </div>

            <div class="chk-field-group">
              <label class="chk-field-label">Data Final *</label>
              <input type="date" class="chk-input ${this.validationErrors.includes('endDate') ? 'invalid-field' : ''}" id="inp-chk-end-date" value="${this.endDate || ''}" ${(this.inspectionId && this.isLocked) || this.isStep1Locked ? 'disabled' : ''}>
              ${this.validationErrors.includes('endDate') ? '<div style="color: var(--status-danger-text); font-size: 0.75rem; margin-top: 4px;">Data inválida.</div>' : ''}
            </div>

            <div class="chk-field-group">
              <label class="chk-field-label">Nº do Relatório</label>
              <div style="display: flex; gap: 8px;">
                <input class="chk-input" id="inp-chk-report-number" value="${this.reportNumber || ''}" placeholder="Gerar Nº..." style="flex: 1; font-weight: 700; ${this.reportNumber ? 'background: var(--status-success-bg); color: var(--status-success-text); border-color: var(--status-success-border);' : ''}" readonly>
                ${this.isStep1Locked ? '' : `
                  <button type="button" class="btn-sub-action ai-btn" id="btn-generate-chk-ri" title="${this.isLocked ? 'Relatório finalizado e bloqueado' : 'Gerar número automático'}" style="padding: 0 12px; height: 100%; ${this.isLocked ? '; cursor: not-allowed;' : ''}" ${this.isLocked ? 'disabled' : ''}>
                    <i data-lucide="zap" style="width: 14px; height: 14px;"></i>
                  </button>
                `}
              </div>
            </div>
            
            <div class="chk-field-group" style="grid-column: span 2;">
              <label class="chk-field-label" title="Apenas Engenharia ou Documentação pode editar">Relatório Cliente Nº <i data-lucide="info" style="width: 12px; height: 12px; display: inline-block;"></i></label>
              <input type="text" class="chk-input" id="inp-chk-client-report-number" value="${this.clientReportNumber || ''}" placeholder="Ex: RI-CLI-001" style="font-weight: 700; ${!canEditClientReportNumber ? 'background: var(--color-bg-primary); cursor: not-allowed; 5;' : ''}" ${!canEditClientReportNumber ? 'readonly' : ''}>
            </div>
          </div>

          <!-- Equipment Snapshot Preview Panel -->
          ${this.equipmentId ? `
            <div style="margin-top: 14px; padding: 12px 16px; background: var(--color-bg-primary); border: 1px solid var(--color-graphite-200); border-radius: 8px; font-size: 0.8rem; display: flex; flex-direction: column; gap: 6px;">
              <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 6px;">
                <span style="font-weight: 700; color: var(--color-text-muted); display: flex; align-items: center; gap: 6px;">
                  <i data-lucide="shield-check" style="width: 14px; height: 14px; color: var(--color-ink);"></i>
                  Snapshot Técnico Cadastral do Ativo
                </span>
                <span style="font-size: 0.725rem; background: var(--color-surface-secondary); color: var(--ia-primary); padding: 2px 8px; border-radius: 4px; font-weight: 700;">
                  TAG: ${this.escapeHtml(this.equipmentTag || '-')}
                </span>
              </div>
              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 8px; color: var(--color-text-muted); margin-top: 4px;">
                ${this.renderEquipmentSnapshotFields(this.activeEquipmentSnapshot, this.equipmentName)}
              </div>
            </div>
          ` : ''}

          <div class="chk-field-group">
            <label class="chk-field-label">Observações Gerais da Inspeção</label>
            <textarea class="chk-textarea" id="txt-general-obs" maxlength="500" placeholder="Adicione observações gerais sobre esta inspeção...">${this.escapeHtml(this.generalObservations)}</textarea>
            <div class="char-counter" id="lbl-general-obs-counter">${this.generalObservations.length} / 500</div>
          </div>
          
          <div style="border-top: 1px dashed var(--color-graphite-300); margin-top: 16px; padding-top: 16px;">
          </div>
        </div>
        </div> <!-- FIM ETAPA 1 -->

        <!-- ETAPA 2: Inspeções e Ensaios -->
        <div id="wizard-step-2" style="display: ${this.currentStep === 2 ? 'block' : 'none'};">
        <div class="chk-header" style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 12px; margin-bottom: 24px;">
          <div>
            <h1 class="chk-title"><i data-lucide="activity" style="width: 28px; height: 28px;"></i> Inspeções e Ensaios</h1>
            <p style="margin: 4px 0 0 0; font-size: 0.8125rem; color: var(--color-text-secondary);">Marque os ensaios aplicados e adicione documentos de referência relevantes.</p>
          </div>
        </div>
        <div class="chk-card">
          <div class="chk-card-title" style="margin-bottom: 16px;">Inspeções e Ensaios Realizados</div>
            <div class="chk-field-group" style="margin-bottom: 16px;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                <div>
                  <label class="chk-field-label" style="font-weight: 700; color: var(--color-text-muted); margin-bottom: 0;">INSPEÇÕES E ENSAIOS REALIZADOS</label>
                  <span style="font-size: 0.75rem; color: var(--color-text-secondary);">Marque os ensaios aplicados e utilize o botão <strong>Salvar</strong> para definir o resultado e comentários em janela dedicada.</span>
                </div>
              </div>
              <div style="display: flex; flex-direction: column; gap: 10px; background: var(--color-bg-primary); padding: 16px; border-radius: 6px; border: 1px solid var(--color-graphite-200);">
                ${[...InspectionTestMethodCatalog, { id: 'OTHER', name: 'OUTROS ENSAIOS', linkedModule: null }].map(method => {
                  const pt = this.performedTests.find(t => t.methodId === method.id);
                  const isChecked = !!pt;
                  let resultLabel = 'Pendente';
                  // Solid colors (not the translucent/white-on-white dark-theme
                  // status tokens) — this badge always needs to stay legible
                  // regardless of what card background it sits on (Bloco 9,
                  // Parte D: "APROVADO quase invisível").
                  let badgeBg = '#5C5C5C';
                  let badgeColor = '#FFFFFF';
                  let badgeBorder = '#909090';

                  if (pt) {
                    if (pt.result === 'APPROVED') {
                      resultLabel = 'APROVADO';
                      badgeBg = '#166534';
                      badgeColor = '#FFFFFF';
                      badgeBorder = '#22c55e';
                    } else if (pt.result === 'REJECTED') {
                      resultLabel = 'REPROVADO';
                      badgeBg = '#7f1d1d';
                      badgeColor = '#FFFFFF';
                      badgeBorder = '#dc2626';
                    } else if (pt.result === 'CLIENT_DISCRETION') {
                      resultLabel = 'A CRITÉRIO DO CLIENTE';
                      badgeBg = '#5C5C5C';
                      badgeColor = '#EEEEEE';
                      badgeBorder = '#909090';
                    } else if (pt.result === 'NOT_APPLICABLE') {
                      resultLabel = 'NÃO APLICÁVEL';
                      badgeBg = '#454545';
                      badgeColor = '#AEAEAE';
                      badgeBorder = '#5C5C5C';
                    }
                  }

                  return `
                    <div style="display: flex; flex-direction: column; gap: 8px; padding: 12px; border: 1px solid ${isChecked ? 'var(--color-border-default)' : 'var(--color-graphite-200)'}; background: ${isChecked ? 'var(--color-surface-secondary)' : 'var(--color-bg-primary)'}; border-radius: 6px;">
                      <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;">
                        <label style="display: flex; align-items: center; gap: 10px; font-size: 0.875rem; font-weight: 600; cursor: pointer; color: var(--ia-primary);">
                          <input type="checkbox" class="chk-ndt-method" value="${method.id}" ${isChecked ? 'checked' : ''} style="width: 17px; height: 17px; cursor: pointer; accent-color: var(--color-ink);">
                          <span>${method.name}${pt?.otherName ? ` (${this.escapeHtml(pt.otherName)})` : ''}</span>
                          ${method.linkedModule ? `<span style="font-size: 0.65rem; background: var(--color-surface-secondary); color: var(--color-ink); padding: 2px 6px; border-radius: 4px; font-weight: 700; border: 1px solid var(--color-border-default);">MÓDULO ${method.linkedModule}</span>` : ''}
                        </label>
                        
                        <div style="display: flex; align-items: center; gap: 8px;">
                          ${isChecked ? `
                            <span style="font-size: 0.72rem; font-weight: 700; background: ${badgeBg}; color: ${badgeColor}; border: 1px solid ${badgeBorder}; padding: 3px 8px; border-radius: 4px;">
                              ${resultLabel}
                            </span>
                            <button type="button" class="btn-save-ndt-test" data-id="${method.id}" style="background: var(--ia-action-bg, var(--color-primary)); border: 1px solid var(--color-ink); color: var(--ia-action-fg, #F7F8F8); font-size: 0.75rem; font-weight: 600; padding: 4px 12px; border-radius: 4px; cursor: pointer; display: flex; align-items: center; gap: 5px; transition: all 0.15s ease;" title="Abrir janela para comentar e salvar resultado">
                              <i data-lucide="save" style="width: 13px; height: 13px;"></i> Salvar / Editar
                            </button>
                          ` : `
                            <button type="button" class="btn-open-ndt-modal-direct" data-id="${method.id}" style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-300); color: var(--color-text-secondary); font-size: 0.75rem; font-weight: 600; padding: 4px 10px; border-radius: 4px; cursor: pointer; display: flex; align-items: center; gap: 5px;">
                              <i data-lucide="save" style="width: 13px; height: 13px; color: var(--color-ink);"></i> Salvar
                            </button>
                          `}
                          ${method.linkedModule ? `
                            <button type="button" class="btn-sub-action btn-goto-ndt-module" data-module="${method.linkedModule}" style="font-size: 0.72rem; padding: 4px 8px; background: var(--status-success-bg); border: 1px solid var(--status-success-border); color: var(--status-success-text); border-radius: 4px; cursor: pointer;" title="Acessar laudo deste ensaio técnico">
                              Acessar Laudo ↗
                            </button>
                          ` : ''}
                        </div>
                      </div>

                      ${isChecked ? `
                        <div style="margin-top: 8px;">
                          <textarea class="chk-textarea txt-ndt-inline-comment" data-id="${method.id}" rows="2" placeholder="Comentário/Observação (Opcional)..." style="font-size: 0.8rem; padding: 6px; width: 100%; border: 1px solid var(--color-graphite-300); border-radius: 4px; resize: vertical;" ${this.isLocked ? 'readonly' : ''}>${this.escapeHtml(pt?.comment || '')}</textarea>
                        </div>
                      ` : ''}
                    </div>
                  `;
                }).join('')}
              </div>
            </div>
          
          <div class="chk-field-group" style="margin-top: 14px;">
            <label class="chk-field-label">Serviços de Manutenção & Intervenções</label>
            <textarea class="chk-textarea" id="chk-manut" rows="2" placeholder="Descreva os serviços de manutenção e intervenções realizados...">${this.escapeHtml(this.maintenanceServices)}</textarea>
          </div>

            <div class="chk-field-group" style="margin-top: 14px;">
              <label class="chk-field-label" style="font-weight: 700; color: var(--color-text-muted); display: flex; align-items: center; gap: 6px;">
                <i data-lucide="activity" style="width: 15px; height: 15px; color: var(--color-ink);"></i>
                Discussão sobre Mecanismos de Dano (Item 13 do Relatório)
              </label>
              <textarea class="chk-textarea" id="txt-damage-mechanisms" maxlength="2000" rows="3" placeholder="Descreva os mecanismos de danos avaliados, ativos, potenciais ou históricos (ex: corrosão sob tensão, fadiga térmica/mecânica, corrosão alveolar, erosão, trincas, fragilização por hidrogênio, etc.)...">${this.escapeHtml(this.damageMechanisms)}</textarea>
              <div class="char-counter" id="lbl-damage-mechanisms-counter">${this.damageMechanisms.length} / 2000</div>
            </div>
          </div>
        </div>

        <!-- Documentos de Referência Card -->
        <div class="chk-card">
          <div class="chk-card-title" style="display: flex; justify-content: space-between; align-items: center;">
            <span>Documentos de Referência</span>
            <button class="btn-primary" id="btn-add-ref-doc" style="padding: 4px 12px; font-size: 0.75rem;">
              <i data-lucide="plus" style="width: 14px; height: 14px; margin-right: 4px;"></i> Adicionar Documento
            </button>
          </div>
          ${this.referenceDocuments.length === 0 ? `
            <div style="padding: 24px; text-align: center; color: var(--color-text-secondary); font-size: 0.9rem; background: var(--color-bg-primary); border: 1px dashed var(--color-graphite-300); border-radius: 6px;">
              Nenhum documento de referência anexado.
            </div>
          ` : `
            <div style="overflow-x: auto;">
              <table style="width: 100%; border-collapse: collapse; font-size: 0.85rem;">
                <thead>
                  <tr style="border-bottom: 2px solid var(--color-graphite-200); background: var(--color-bg-primary);">
                    <th style="padding: 12px; text-align: left; font-weight: 600; color: var(--color-text-secondary); width: 30%;">NOME</th>
                    <th style="padding: 12px; text-align: left; font-weight: 600; color: var(--color-text-secondary); width: 40%;">DESCRIÇÃO</th>
                    <th style="padding: 12px; text-align: left; font-weight: 600; color: var(--color-text-secondary); width: 20%;">ARQUIVO</th>
                    <th style="padding: 12px; text-align: center; font-weight: 600; color: var(--color-text-secondary); width: 10%;">AÇÕES</th>
                  </tr>
                </thead>
                <tbody>
                  ${this.referenceDocuments.map((doc, idx) => `
                    <tr style="border-bottom: 1px solid var(--color-graphite-200);">
                      <td style="padding: 12px;">
                        <input type="text" class="chk-input ref-doc-name" data-id="${doc.id}" value="${this.escapeHtml(doc.name)}" placeholder="Ex: Desenho de Fabricação..." style="font-weight: 500; font-size: 0.85rem; padding: 4px 8px;">
                      </td>
                      <td style="padding: 12px;">
                        <input type="text" class="chk-input ref-doc-desc" data-id="${doc.id}" value="${this.escapeHtml(doc.description)}" placeholder="Descrição complementar..." style="font-size: 0.85rem; padding: 4px 8px;">
                      </td>
                      <td style="padding: 12px;">
                        ${doc.fileData ? `
                          <div style="display: flex; align-items: center; gap: 8px;">
                            <div style="background: var(--color-surface-secondary); color: var(--color-ink); padding: 4px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; text-overflow: ellipsis; overflow: hidden; white-space: nowrap; max-width: 120px;" title="${this.escapeHtml(doc.fileName || 'Arquivo anexado')}">
                              <i data-lucide="file-text" style="width: 12px; height: 12px; display: inline-block; vertical-align: middle; margin-right: 4px;"></i>
                              ${this.escapeHtml(doc.fileName || 'anexo.pdf')}
                            </div>
                            <label style="cursor: pointer; color: var(--color-text-secondary); margin-left: 4px;" title="Substituir arquivo">
                              <i data-lucide="upload" style="width: 14px; height: 14px;"></i>
                              <input type="file" class="ref-doc-file" data-id="${doc.id}" style="display: none;">
                            </label>
                          </div>
                        ` : `
                          <label class="btn-sub-action" style="padding: 4px 8px; font-size: 0.75rem; display: inline-flex; cursor: pointer;">
                            <i data-lucide="paperclip" style="width: 12px; height: 12px; margin-right: 4px;"></i> Anexar Arquivo
                            <input type="file" class="ref-doc-file" data-id="${doc.id}" style="display: none;">
                          </label>
                        `}
                      </td>
                      <td style="padding: 12px; text-align: center;">
                        <div style="display: flex; justify-content: center; gap: 8px;">
                          <button class="btn-icon ref-doc-view" data-id="${doc.id}" title="Visualizar Anexo" ${!doc.fileData ? 'disabled style=";"' : ''}>
                            <i data-lucide="eye" style="width: 16px; height: 16px; color: var(--color-ink);"></i>
                          </button>
                          <button class="btn-icon ref-doc-delete" data-id="${doc.id}" title="Remover Documento">
                            <i data-lucide="trash-2" style="width: 16px; height: 16px; color: var(--status-danger-solid);"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
            </div>
          `}
        </div>
        </div> <!-- FIM ETAPA 2 -->

        <!-- ETAPA 3: Check List -->
        <div id="wizard-step-3" style="display: ${this.currentStep === 3 ? 'block' : 'none'};">
        <div class="chk-header" style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 12px; margin-bottom: 24px;">
          <div>
            <h1 class="chk-title"><i data-lucide="list-checks" style="width: 28px; height: 28px;"></i> Check List</h1>
            <p style="margin: 4px 0 0 0; font-size: 0.8125rem; color: var(--color-text-secondary);">Avalie os componentes, registre não conformidades e adicione recomendações ou fotos.</p>
          </div>
        </div>

        <!-- KPI Metric Cards Grid -->
        <div class="kpi-row">
          <div class="kpi-card">
            <div class="kpi-icon-wrap total">
              <i data-lucide="box" style="width: 22px; height: 22px;"></i>
            </div>
            <div class="kpi-content">
              <span class="kpi-number">${kpis.total}</span>
              <span class="kpi-label">Componentes</span>
            </div>
          </div>

          <div class="kpi-card">
            <div class="kpi-icon-wrap conforming">
              <i data-lucide="check-circle-2" style="width: 22px; height: 22px;"></i>
            </div>
            <div class="kpi-content">
              <span class="kpi-number" style="color: var(--status-success-text);">${kpis.approved}</span>
              <span class="kpi-label">Aprovados</span>
            </div>
          </div>

          <div class="kpi-card">
            <div class="kpi-icon-wrap non-conforming">
              <i data-lucide="x-circle" style="width: 22px; height: 22px;"></i>
            </div>
            <div class="kpi-content">
              <span class="kpi-number" style="color: var(--status-danger-solid);">${kpis.rejected}</span>
              <span class="kpi-label">Reprovados</span>
            </div>
          </div>

          <div class="kpi-card">
            <div class="kpi-icon-wrap" style="background: rgba(100, 116, 139, 0.1); color: var(--color-text-secondary);">
              <i data-lucide="minus-circle" style="width: 22px; height: 22px;"></i>
            </div>
            <div class="kpi-content">
              <span class="kpi-number" style="color: var(--color-text-secondary);">${kpis.notApplicable}</span>
              <span class="kpi-label">Não Aplicáveis</span>
            </div>
          </div>

          <div class="kpi-card">
            <div class="kpi-icon-wrap pending">
              <i data-lucide="clock" style="width: 22px; height: 22px;"></i>
            </div>
            <div class="kpi-content">
              <span class="kpi-number" style="color: var(--color-text-secondary);">${kpis.pending}</span>
              <span class="kpi-label">Pendentes</span>
            </div>
          </div>
        </div>

        ${kpis.withRecommendation > 0 ? `
          <div style="margin-top: 12px; margin-bottom: 24px; display: inline-flex; align-items: center; gap: 8px; background: rgba(217, 119, 6, 0.1); border: 1px solid rgba(217, 119, 6, 0.3); padding: 8px 12px; border-radius: 6px;">
            <i data-lucide="alert-triangle" style="width: 18px; height: 18px; color: var(--status-warning-text);"></i>
            <span style="color: var(--status-warning-text); font-weight: 600; font-size: 0.875rem;">${kpis.withRecommendation} Recomendações registradas</span>
          </div>
        ` : ''}

        <!-- Filter, Search and Bulk Action Bar -->
        <div class="filter-action-bar">
          <div class="search-and-filters">
            <div class="search-box-wrapper">
              <i data-lucide="search"></i>
              <input type="text" class="search-input" id="inp-search-comp" placeholder="Buscar componente..." value="${this.escapeHtml(this.searchQuery)}">
            </div>

            <div class="filter-pill-group">
              <button type="button" class="filter-pill-btn ${this.currentFilter === 'ALL' ? 'active' : ''}" data-filter="ALL">
                Todos
              </button>
              <button type="button" class="filter-pill-btn ${this.currentFilter === 'PENDING' ? 'active' : ''}" data-filter="PENDING">
                Pendentes (${kpis.pending})
              </button>
              <button type="button" class="filter-pill-btn ${this.currentFilter === 'APPROVED' ? 'active' : ''}" data-filter="APPROVED">
                Aprovados (${kpis.approved})
              </button>
              <button type="button" class="filter-pill-btn ${this.currentFilter === 'REJECTED' ? 'active' : ''}" data-filter="REJECTED">
                Reprovados (${kpis.rejected})
              </button>
              <button type="button" class="filter-pill-btn ${this.currentFilter === 'NOT_APPLICABLE' ? 'active' : ''}" data-filter="NOT_APPLICABLE">
                Não Aplicáveis (${kpis.notApplicable})
              </button>
              <button type="button" class="filter-pill-btn ${this.currentFilter === 'WITH_RECOMMENDATION' ? 'active' : ''}" data-filter="WITH_RECOMMENDATION">
                Com recomendação (${kpis.withRecommendation})
              </button>
            </div>
          </div>

          <div style="display: flex; gap: 8px;">
            <button type="button" class="btn-bulk-conforming" id="btn-trigger-bulk" ${this.isLocked ? 'disabled style="opacity: 0.5; cursor: not-allowed;"' : ''}>
              <i data-lucide="check" style="width: 16px; height: 16px;"></i> MARCAR TODOS PENDENTES COMO APROVADOS
            </button>
            <button type="button" class="btn-bulk-conforming" id="btn-add-manual-component" ${this.isLocked ? 'disabled style="background:var(--color-graphite-700); border:1px solid var(--color-graphite-600); opacity: 0.5; cursor: not-allowed;"' : 'style="background:var(--color-graphite-700); border:1px solid var(--color-graphite-600);"'} >
              <i data-lucide="plus" style="width: 16px; height: 16px;"></i> ADICIONAR COMPONENTE
            </button>
            <button type="button" class="btn-bulk-conforming" id="btn-save-checklist" ${this.isLocked ? 'disabled style="background:var(--color-primary); border:1px solid var(--color-primary); color: var(--color-text-primary); opacity: 0.5; cursor: not-allowed;"' : 'style="background:var(--color-primary); border:1px solid var(--color-primary); color: var(--color-text-primary);"'} >
              <i data-lucide="save" style="width: 16px; height: 16px;"></i> SALVAR CHECKLIST
            </button>
          </div>
        </div>

        <!-- Component List -->
        <div class="chk-items-list" id="component-items-container">
          ${filtered.length > 0 ? filtered.map(item => this.renderComponentCard(item)).join('') : `
            <div style="padding: 48px; text-align: center; color: var(--color-text-secondary); background: var(--color-surface-primary); border: 1px dashed var(--color-graphite-300); border-radius: 8px;">
              Nenhum componente encontrado com os critérios de busca / filtro selecionados.
            </div>
          `}
        </div>
      </div>

      <!-- Modal de Consistência Técnica -->
      ${this.showConsistencyModal ? `
        <div class="custom-modal-backdrop" id="modal-consistency">
          <div class="modal-card" style="max-width: 620px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 10px;">
              <h3 style="margin: 0; font-size: 1.15rem; font-weight: 700; color: var(--color-text-muted); display: flex; align-items: center; gap: 8px;">
                <i data-lucide="shield-check" style="width: 20px; height: 20px; color: var(--color-ink);"></i>
                Relatório de Consistência Técnica
              </h3>
              <button type="button" id="btn-close-consistency-modal" style="background: none; border: none; font-size: 1.25rem; color: var(--color-text-secondary); cursor: pointer;">&times;</button>
            </div>
            <div style="max-height: 60vh; overflow-y: auto; display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px;">
              ${this.consistencyErrors.length === 0 ? `
                <div style="padding: 16px; background: var(--status-success-bg); border: 1px solid var(--status-success-border); border-radius: 8px; color: var(--status-success-text); font-weight: 600; display: flex; align-items: center; gap: 10px;">
                  <i data-lucide="check-circle" style="width: 20px; height: 20px; color: var(--status-success-text);"></i>
                  Nenhuma inconsistência detectada. O checklist e o relatório estão 100% aptos para finalização!
                </div>
              ` : this.consistencyErrors.map(err => {
                const isBlocker = err.includes('[IMPEDITIVA]') || err.includes('Inconsistência matemática') || err.includes('constam como \'Pendente\'') || err.includes('não possuem comentário');
                return `
                  <div style="padding: 10px 14px; border-radius: 6px; font-size: 0.825rem; display: flex; align-items: flex-start; gap: 8px; background: ${isBlocker ? 'var(--status-danger-bg)' : 'var(--status-warning-bg)'}; border: 1px solid ${isBlocker ? 'var(--status-danger-bg)' : 'var(--status-warning-border)'}; color: ${isBlocker ? 'var(--status-danger-text)' : 'var(--status-warning-text)'};">
                    <i data-lucide="${isBlocker ? 'x-circle' : 'alert-triangle'}" style="width: 16px; height: 16px; flex-shrink: 0; margin-top: 1px;"></i>
                    <span>${this.escapeHtml(err)}</span>
                  </div>
                `;
              }).join('')}
            </div>
            <div style="display: flex; justify-content: flex-end; gap: 10px;">
              <button type="button" class="btn-modal-cancel" id="btn-ok-consistency">Fechar</button>
            </div>
          </div>
        </div>
      ` : ''}

      <!-- Modal de Confirmação em Massa -->
      ${this.showBulkConfirmModal ? `
        <div class="custom-modal-backdrop" id="modal-bulk-confirm">
          <div class="modal-card">
            <h3 style="margin-top: 0; color: var(--color-text-muted); font-size: 1.1rem; font-weight: 700;">Confirmar Aprovação em Massa</h3>
            <p style="color: var(--color-text-secondary); font-size: 0.875rem; margin: 12px 0 20px 0;">
              Deseja marcar todos os componentes atualmente pendentes como <strong>APROVADO</strong> (Sem anomalias)?
            </p>
            <div style="display: flex; gap: 12px;">
              <button type="button" class="btn-modal-cancel" id="btn-cancel-bulk">Cancelar</button>
              <button type="button" class="btn-modal-confirm" id="btn-confirm-bulk">Confirmar</button>
            </div>
          </div>
        </div>
      ` : ''}

      <!-- Modal de Pré-visualização do Relatório / PDF -->
      ${this.showPdfPreviewModal ? `
        <div class="custom-modal-backdrop" id="modal-pdf-preview" style="z-index: 3000;">
          <div style="background: var(--color-surface-primary); border-radius: 12px; width: 95%; max-width: 1200px; height: 90vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);">
            <div style="padding: 12px 20px; background: var(--color-graphite-800); color: var(--color-text-primary); display: flex; justify-content: space-between; align-items: center;">
              <span style="font-weight: 700; font-size: 0.95rem; display: flex; align-items: center; gap: 8px;">
                <i data-lucide="file-text" style="width: 16px; height: 16px; color: var(--color-text-muted);"></i>
                Pré-visualização do Relatório de Integridade (RI)
              </span>
              <div style="display: flex; align-items: center; gap: 10px;">
                <button type="button" id="btn-modal-open-report-viewer" style="background: var(--color-primary); color: var(--color-text-primary); border: none; border-radius: 4px; padding: 6px 14px; font-size: 0.85rem; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px;">
                  <i data-lucide="eye" style="width: 14px; height: 14px;"></i> ABRIR RELATÓRIO COMPLETO
                </button>
                <button type="button" id="btn-close-pdf-modal" style="background: none; border: none; color: var(--color-graphite-400); font-size: 1.5rem; cursor: pointer; line-height: 1;">&times;</button>
              </div>
            </div>
            <div style="flex: 1; overflow-y: auto; background: var(--color-surface-secondary); padding: 20px;">
              <app-pdf-preview id="chk-pdf-preview-el"></app-pdf-preview>
            </div>
          </div>
        </div>
      ` : ''}

      <!-- Modal de Validação de Campos Obrigatórios -->
      <div class="custom-modal-backdrop" id="modal-validation-errors" style="display: none; z-index: 2500;">
        <div class="modal-card" style="max-width: 480px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 8px;">
            <h3 style="margin: 0; font-size: 1.05rem; font-weight: 700; color: var(--status-danger-solid); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="alert-triangle" style="width: 18px; height: 18px;"></i>
              Campos Obrigatórios Pendentes
            </h3>
            <button type="button" id="btn-close-validation-modal" style="background: none; border: none; font-size: 1.25rem; color: var(--color-text-secondary); cursor: pointer;">&times;</button>
          </div>
          <p style="font-size: 0.85rem; color: var(--color-text-muted); margin-bottom: 12px;">Por favor, preencha os seguintes campos obrigatórios antes de continuar:</p>
          <ul id="validation-errors-list" style="margin: 0 0 16px 20px; font-size: 0.825rem; color: var(--status-danger-text); display: flex; flex-direction: column; gap: 4px;"></ul>
          <div style="display: flex; justify-content: flex-end;">
            <button type="button" class="btn-modal-confirm" id="btn-ack-validation" style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8);">Entendido</button>
          </div>
        </div>
      </div>

      <!-- Modal de Criação de Recomendação Técnica -->
      ${this.showCreateRecModal && this.recTargetItem ? `
        <div class="custom-modal-backdrop" id="modal-create-rec" style="z-index: 2500;">
          <div class="modal-card" style="max-width: 580px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 10px;">
              <h3 style="margin: 0; font-size: 1.1rem; font-weight: 700; color: var(--color-text-muted); display: flex; align-items: center; gap: 8px;">
                <i data-lucide="wrench" style="width: 18px; height: 18px; color: var(--color-ink);"></i>
                Criar Recomendação Técnica
              </h3>
              <button type="button" id="btn-close-rec-modal" style="background: none; border: none; font-size: 1.25rem; color: var(--color-text-secondary); cursor: pointer;">&times;</button>
            </div>

            <div style="display: flex; flex-direction: column; gap: 12px;">
              <div style="background: var(--color-bg-primary); border: 1px solid var(--color-graphite-200); border-radius: 6px; padding: 10px 14px; font-size: 0.8rem; color: var(--color-text-muted);">
                <div><strong>Componente:</strong> ${this.escapeHtml(this.recTargetItem.name)}</div>
                <div><strong>Equipamento:</strong> ${this.escapeHtml(this.equipmentTag)} (${this.escapeHtml(this.equipmentName || 'Ativo')})</div>
                <div><strong>Inspeção:</strong> ${this.escapeHtml(this.inspectionId || 'Nova Inspeção')}</div>
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                <div class="chk-field-group">
                  <label class="chk-field-label">Disciplina *</label>
                  <select class="chk-select" id="inp-rec-discipline">
                    <option value="CALDEIRARIA" ${this.recDiscipline === 'CALDEIRARIA' ? 'selected' : ''}>Caldeiraria</option>
                    <option value="TUBULAÇÃO" ${this.recDiscipline === 'TUBULAÇÃO' ? 'selected' : ''}>Tubulação</option>
                    <option value="MECNICA" ${this.recDiscipline === 'MECNICA' ? 'selected' : ''}>Mecânica</option>
                    <option value="PINTURA" ${this.recDiscipline === 'PINTURA' ? 'selected' : ''}>Pintura / Revestimento</option>
                    <option value="INSTRUMENTAÇÃO" ${this.recDiscipline === 'INSTRUMENTAÇÃO' ? 'selected' : ''}>Instrumentação</option>
                    <option value="CIVIL" ${this.recDiscipline === 'CIVIL' ? 'selected' : ''}>Civil / Suporte</option>
                    <option value="ELÉTRICA" ${this.recDiscipline === 'ELÉTRICA' ? 'selected' : ''}>Elétrica</option>
                    <option value="ISOLAMENTO TÉRMICO" ${this.recDiscipline === 'ISOLAMENTO TÉRMICO' ? 'selected' : ''}>Isolamento Térmico</option>
                  </select>
                </div>
                <div class="chk-field-group">
                  <label class="chk-field-label">Criticidade *</label>
                  <select class="chk-select" id="inp-rec-criticality">
                    <option value="BAIXA" ${this.recCriticality === 'BAIXA' ? 'selected' : ''}>Baixa</option>
                    <option value="MÉDIA" ${this.recCriticality === 'MÉDIA' ? 'selected' : ''}>Média</option>
                    <option value="ALTA" ${this.recCriticality === 'ALTA' ? 'selected' : ''}>Alta</option>
                    <option value="CRÍTICA" ${this.recCriticality === 'CRÍTICA' ? 'selected' : ''}>Crítica</option>
                  </select>
                </div>
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                <div class="chk-field-group">
                  <label class="chk-field-label">Execução *</label>
                  <select class="chk-select" id="inp-rec-execution">
                    <option value="ROTINA" ${this.recExecution === 'ROTINA' ? 'selected' : ''}>Rotina Operacional</option>
                    <option value="PARADA" ${this.recExecution === 'PARADA' ? 'selected' : ''}>Parada de Manutenção</option>
                  </select>
                </div>
                <div class="chk-field-group">
                  <label class="chk-field-label">Número da OS / ZR (Opcional)</label>
                  <div class="chk-input" style="background: var(--color-graphite-700); color: var(--color-text-muted); cursor: not-allowed; display: flex; align-items: center;">
                    Gerido na aba Recomendações
                  </div>
                </div>
              </div>

              <div class="chk-field-group">
                <label class="chk-field-label">Descritivo da Recomendação *</label>
                <textarea class="chk-textarea" id="inp-rec-scope" rows="3" placeholder="Descreva tecnicamente a ação corretiva ou preventiva necessária...">${this.escapeHtml(this.recScope)}</textarea>
              </div>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px;">
              <button type="button" class="btn-modal-cancel" id="btn-cancel-rec-modal">Cancelar</button>
              <button type="button" class="btn-modal-confirm" id="btn-save-rec-modal" style="background: var(--ia-action-bg, var(--color-primary)); border-color: var(--color-primary); color: var(--ia-action-fg, #F7F8F8);">
                Salvar Recomendação
              </button>
            </div>
          </div>
        </div>
      ` : ''} </div> <!-- FIM ETAPA 3 -->

        <!-- ETAPA 4: Resumo -->
        <div id="wizard-step-4" style="display: ${this.currentStep === 4 ? 'block' : 'none'};">
          <div class="chk-header" style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 12px; margin-bottom: 24px;">
            <div>
              <h1 class="chk-title"><i data-lucide="file-check-2" style="width: 28px; height: 28px;"></i> Resumo da Inspeção</h1>
              <p style="margin: 4px 0 0 0; font-size: 0.8125rem; color: var(--color-text-secondary);">Revise os dados antes de gerar o relatório ou enviar para análise.</p>
            </div>
            ${this.reportStatus === 'SUBMITTED_TO_ENGINEERING' || this.reportStatus === 'UNDER_ENGINEERING_REVIEW' || this.isLocked ? `
              <span style="display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 6px; background: var(--status-success-bg); color: var(--status-success-text); font-size: 0.8rem; font-weight: 700; border: 1px solid var(--status-success-border);">
                <i data-lucide="check" style="width: 14px; height: 14px;"></i> ✓ AGUARDANDO ANÁLISE DO PLH
              </span>
            ` : ''}
          </div>

          <!-- Seção 1: Dados do Equipamento (Leitura) -->
          <div class="summary-section">
            <h3 class="summary-section-title">
              <i data-lucide="info" style="width: 18px; height: 18px; color: var(--ia-primary);"></i> 
              1. Dados Principais
            </h3>
            <div class="summary-grid">
              <div class="summary-field">
                <span class="summary-field-label">Cliente</span>
                <span class="summary-field-value">${this.clientName || '-'}</span>
              </div>
              <div class="summary-field">
                <span class="summary-field-label">Equipamento</span>
                <span class="summary-field-value">${this.equipmentName || '-'}</span>
              </div>
              <div class="summary-field">
                <span class="summary-field-label">TAG</span>
                <span class="summary-field-value">${this.equipmentTag || '-'}</span>
              </div>
              <div class="summary-field">
                <span class="summary-field-label">Nº Relatório</span>
                <span class="summary-field-value">${this.reportNumber || '-'}</span>
              </div>
              <div class="summary-field">
                <span class="summary-field-label">Data</span>
                <span class="summary-field-value">${this.startDate || '-'}</span>
              </div>
              <div class="summary-field">
                <span class="summary-field-label">Inspetor</span>
                <span class="summary-field-value">${this.professionals.find(p => p.id === this.selectedInspectorId)?.name || '-'}</span>
              </div>
            </div>
          </div>

          <!-- Seção 2: Ensaios (Leitura) -->
          <div class="summary-section">
            <h3 class="summary-section-title">
              <i data-lucide="activity" style="width: 18px; height: 18px; color: var(--ia-primary);"></i> 
              2. Ensaios Realizados
            </h3>
            ${this.performedTests.length > 0 ? `
              <div style="background: var(--color-bg-primary); border: 1px solid var(--color-graphite-200); border-radius: 8px; overflow: hidden;">
                <table class="summary-table">
                  <thead>
                    <tr>
                      <th>Ensaio</th>
                      <th>Resultado</th>
                      <th>Laudo</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${this.performedTests.map(t => {
                      const method = [...InspectionTestMethodCatalog, { id: 'OTHER', name: 'OUTROS ENSAIOS', linkedModule: null }].find(m => m.id === t.methodId);
                      const isApproved = t.result === 'APPROVED';
                      const resultColor = isApproved ? 'var(--status-success-solid)' : (t.result === 'REJECTED' ? 'var(--status-danger-solid)' : 'var(--color-graphite-600)');
                      return `
                        <tr>
                          <td style="font-weight: 600;">${method?.name || t.methodId}</td>
                          <td style="color: ${resultColor}; font-weight: 700;">
                            ${t.result === 'APPROVED' ? 'Aprovado' : (t.result === 'REJECTED' ? 'Reprovado' : 'Pendente')}
                          </td>
                          <td style="font-size: 0.8rem;">${this.escapeHtml((t as any).reportNumber || '-')}</td>
                        </tr>
                      `;
                    }).join('')}
                  </tbody>
                </table>
              </div>
            ` : `
              <div style="padding: 16px; background: var(--color-surface-secondary); border-radius: 8px; font-size: 0.85rem; color: var(--color-text-secondary); text-align: center;">
                Nenhum ensaio marcado como realizado.
              </div>
            `}
          </div>

          <!-- Seção 3: Check List (Leitura) -->
          <div class="summary-section">
            <h3 class="summary-section-title">
              <i data-lucide="list-checks" style="width: 18px; height: 18px; color: var(--ia-primary);"></i> 
              3. Status do Checklist
            </h3>
            
            <div class="summary-kpi-row">
              <div class="summary-kpi-card">
                <span class="summary-kpi-number" style="color: var(--color-white-pure);">${kpis.total}</span>
                <span class="summary-kpi-label">Componentes</span>
              </div>
              <div class="summary-kpi-card" style="border-color: rgba(34, 197, 94, 0.3); background: rgba(34, 197, 94, 0.05);">
                <span class="summary-kpi-number" style="color: var(--ia-success-fg, #22c55e);">${kpis.approved}</span>
                <span class="summary-kpi-label">Aprovados</span>
              </div>
              <div class="summary-kpi-card" style="border-color: rgba(239, 68, 68, 0.3); background: rgba(239, 68, 68, 0.05);">
                <span class="summary-kpi-number" style="color: var(--status-danger-text);">${kpis.rejected}</span>
                <span class="summary-kpi-label">Reprovados</span>
              </div>
              <div class="summary-kpi-card">
                <span class="summary-kpi-number" style="color: var(--status-warning-text);">${kpis.pending}</span>
                <span class="summary-kpi-label">Pendentes</span>
              </div>
            </div>

            ${kpis.pending > 0 ? `
              <div style="margin-top: 12px; background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3); padding: 12px 16px; border-radius: 8px; display: flex; gap: 12px; align-items: center;">
                <i data-lucide="alert-circle" style="color: var(--status-danger-text); width: 24px; height: 24px;"></i>
                <div>
                  <div style="font-weight: 700; color: var(--status-danger-text); font-size: 0.9rem;">ATENÇÃO: Existem componentes pendentes</div>
                  <div style="font-size: 0.8rem; color: var(--color-ink);">O relatório não pode ser finalizado até que todos os componentes sejam avaliados. Retorne à Etapa 3.</div>
                </div>
              </div>
            ` : ''}
          </div>
        </div> <!-- FIM ETAPA 4 -->

        <!-- Bottom Navigation Bar (Contextual by Step) -->
        <div class="step-nav-bar">
          <div style="display: flex; gap: 12px; align-items: center;">
            <button type="button" class="btn-step btn-step-back" id="btn-step-prev" style="display: ${this.currentStep > 1 ? 'inline-flex' : 'none'};">
              <i data-lucide="arrow-left" style="width: 16px; height: 16px;"></i> VOLTAR
            </button>
            <span class="step-nav-hint" style="display: ${this.currentStep === 1 ? 'flex' : 'none'};">
              <i data-lucide="info" style="width: 14px; height: 14px;"></i> Complete os dados para avançar
            </span>
          </div>

          <div style="display: flex; gap: 12px; align-items: center; flex-wrap: wrap;">
            <!-- Salvar (Visível etapas 1, 2, 3) -->
            ${this.currentStep < 4 ? `
              <button type="button" class="btn-step btn-step-save" id="btn-step-save-btn">
                <i data-lucide="save" style="width: 16px; height: 16px;"></i> SALVAR
              </button>
            ` : ''}

            <!-- Próximo (Visível etapas 1, 2) -->
            ${this.currentStep < 3 ? `
              <button type="button" class="btn-step btn-step-next" id="btn-step-next">
                PRÓXIMO <i data-lucide="arrow-right" style="width: 16px; height: 16px;"></i>
              </button>
            ` : ''}

            <!-- Ir para Resumo (Visível etapa 3) -->
            ${this.currentStep === 3 ? `
              <button type="button" class="btn-step btn-step-summary" id="btn-step-next">
                VER RESUMO <i data-lucide="file-check" style="width: 16px; height: 16px;"></i>
              </button>
            ` : ''}

            <!-- Ações Finais (Visível etapa 4) -->
            ${this.currentStep === 4 ? `
              <button type="button" class="btn-step btn-step-report" id="btn-step-generate-ri">
                <i data-lucide="file-text" style="width: 16px; height: 16px;"></i> GERAR RELATÓRIO
              </button>
              
              ${this.reportStatus === 'SUBMITTED_TO_ENGINEERING' || this.reportStatus === 'UNDER_ENGINEERING_REVIEW' || this.isLocked ? '' : `
                <button type="button" class="btn-step btn-step-plh" id="btn-step-release-plh" ${kpis.pending > 0 ? 'disabled style="; cursor: not-allowed;"' : ''}>
                  <i data-lucide="send" style="width: 16px; height: 16px;"></i> LIBERAR PLH
                </button>
              `}
            ` : ''}
          </div>
        </div>

        ${this.showValidationModal ? `
          <div class="modal-backdrop" id="backdrop-validation-modal" style="position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(0,0,0,0.5); z-index: 10000; display: flex; align-items: center; justify-content: center;">
            <div class="modal-content" style="background: var(--color-surface-primary); width: 90%; max-width: 500px; border-radius: 8px; box-shadow: 0 4px 16px rgba(0,0,0,0.2); overflow: hidden; display: flex; flex-direction: column;">
              <div style="background: var(--status-warning-bg); border-bottom: 1px solid var(--status-warning-solid); padding: 16px 20px; display: flex; align-items: center; gap: 10px;">
                <i data-lucide="alert-triangle" style="width: 20px; height: 20px; color: var(--status-warning-text);"></i>
                <h3 style="margin: 0; font-size: 1.1rem; color: var(--color-ink);">Atenção</h3>
              </div>
              <div style="padding: 20px; overflow-y: auto; max-height: 60vh;">
                <p style="margin-top: 0; font-weight: 600; color: var(--color-text-muted);">Preencha os seguintes campos antes de prosseguir:</p>
                <ul style="margin: 12px 0 0 20px; color: var(--color-ink); padding: 0;">
                  ${this.validationModalMessage.map(msg => `<li style="margin-bottom: 6px;">${this.escapeHtml(msg)}</li>`).join('')}
                </ul>
              </div>
              <div style="background: var(--color-bg-primary); border-top: 1px solid var(--color-graphite-200); padding: 16px 20px; display: flex; justify-content: flex-end;">
                <button type="button" id="btn-ok-validation-modal" style="padding: 8px 24px; font-weight: 600; cursor: pointer; background: var(--ia-action-bg, var(--color-primary)); border: none; border-radius: 6px; color: var(--ia-action-fg, #F7F8F8);">OK</button>
              </div>
            </div>
          </div>
        ` : ''}

      ${this.renderManualCompModal()}
      ${this.renderNdtModal()}
      ${this.renderPhotoModal()}
      ${this.renderPhotoEditorModal()}
    `;

    createIcons({
      icons: {
        Box,
        CheckCircle2,
        XCircle,
        AlertTriangle,
        Clock,
        Search,
        Check,
        HelpCircle,
        FileText,
        ChevronDown,
        ChevronUp,
        Camera,
        MinusCircle,
        Sparkles,
        ArrowLeft,
        Save,
        Printer,
        Eye,
        Trash2,
        ShieldCheck,
        CheckCircle,
        PlusCircle,
        AlertCircle,
        Wrench
      },
      nameAttr: "data-lucide",
      root: this
    });

    this.attachEvents();
  }


  private renderManualCompModal(): string {
    if (!this.showManualCompModal) return '';
    return `
      <div style="position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(15, 23, 42, 0.7); z-index: 9999; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(4px);">
        <div style="background: var(--color-surface-primary); width: 100%; max-width: 500px; border-radius: 12px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1); overflow: hidden; animation: modal-slide-in 0.3s cubic-bezier(0.16, 1, 0.3, 1);">
          
          <div style="padding: 20px 24px; border-bottom: 1px solid var(--color-graphite-200); display: flex; justify-content: space-between; align-items: center; background: var(--color-bg-primary);">
            <div style="display: flex; align-items: center; gap: 12px;">
              <div style="width: 40px; height: 40px; background: var(--color-surface-secondary); border-radius: 8px; display: flex; align-items: center; justify-content: center; color: var(--color-ink);">
                <i data-lucide="plus" style="width: 20px; height: 20px;"></i>
              </div>
              <div>
                <h3 style="margin: 0; font-size: 1.125rem; font-weight: 700; color: var(--color-text-muted);">Adicionar Componente</h3>
                <p style="margin: 4px 0 0 0; font-size: 0.8125rem; color: var(--color-text-secondary);">Insira um componente manualmente nesta inspeção.</p>
              </div>
            </div>
            <button class="btn-close-manual-modal" style="background: none; border: none; cursor: pointer; color: var(--color-text-secondary); padding: 4px;">
              <i data-lucide="x" style="width: 20px; height: 20px;"></i>
            </button>
          </div>

          <div style="padding: 24px; display: flex; flex-direction: column; gap: 16px;">
            <div class="chk-field-group">
              <label class="chk-field-label">Nome do Componente *</label>
              <input type="text" class="chk-input" id="inp-manual-comp-name" placeholder="Ex: Bocal N3, Dreno D-01">
            </div>
            <div class="chk-field-group">
              <label class="chk-field-label">Identificação / TAG</label>
              <input type="text" class="chk-input" id="inp-manual-comp-tag" placeholder="Opcional">
            </div>
            <!-- TIPO removido na Fase 15 -->
            <div class="chk-field-group" style="display: none;">
              <label class="chk-field-label">Tipo / Grupo</label>
              <input type="text" class="chk-input" id="inp-manual-comp-group" placeholder="Ex: Acessórios, Conexões">
            </div>
            <div class="chk-field-group">
              <label class="chk-field-label">Descrição / Localização</label>
              <input type="text" class="chk-input" id="inp-manual-comp-desc" placeholder="Opcional">
            </div>
          </div>

          <div style="padding: 16px 24px; border-top: 1px solid var(--color-graphite-200); background: var(--color-bg-primary); display: flex; justify-content: flex-end; gap: 12px;">
            <button type="button" class="btn-close-manual-modal" style="padding: 8px 16px; border: 1px solid var(--color-graphite-300); background: var(--color-surface-primary); border-radius: 6px; font-weight: 600; color: var(--color-text-secondary); font-size: 0.875rem; cursor: pointer;">Cancelar</button>
            <button type="button" id="btn-save-manual-comp" style="padding: 8px 16px; border: none; background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border-radius: 6px; font-weight: 600; font-size: 0.875rem; cursor: pointer; display: flex; align-items: center; gap: 8px;">
              <i data-lucide="save" style="width: 16px; height: 16px;"></i> Adicionar Componente
            </button>
          </div>
        </div>
      </div>
    `;
  }


  private renderNdtModal(): string {
    if (!this.showNdtModal || !this.activeNdtMethodId) return '';
    const method = [...InspectionTestMethodCatalog, { id: 'OTHER', name: 'OUTROS ENSAIOS', linkedModule: null }].find(m => m.id === this.activeNdtMethodId);
    const methodName = method ? method.name : 'Inspeção / Ensaio';
    const isOther = this.activeNdtMethodId === 'OTHER';

    return `
      <div class="chk-modal-backdrop" id="backdrop-ndt-modal" style="display: flex; align-items: center; justify-content: center; position: fixed; inset: 0; background: rgba(15, 23, 42, 0.7); z-index: 9999; padding: 16px; backdrop-filter: blur(4px);">
        <div class="chk-modal" style="background: var(--color-surface-primary); width: 100%; max-width: 520px; border-radius: 10px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2); overflow: hidden; animation: fadeIn 0.2s ease-out; border: 1px solid var(--color-graphite-200);">
          <div style="background: var(--color-graphite-800); color: var(--color-text-primary); padding: 16px 20px; display: flex; justify-content: space-between; align-items: center;">
            <div style="display: flex; align-items: center; gap: 10px;">
              <div style="background: var(--color-ink); padding: 6px; border-radius: 6px; display: flex;">
                <i data-lucide="wrench" style="width: 18px; height: 18px; color: var(--color-text-primary);"></i>
              </div>
              <div>
                <h3 style="margin: 0; font-size: 1rem; font-weight: 700; color: var(--color-text-primary);">Ensaio: ${this.escapeHtml(methodName)}</h3>
                <span style="font-size: 0.75rem; color: var(--color-graphite-400);">Defina o resultado e comentários técnicos da inspeção</span>
              </div>
            </div>
            <button type="button" id="btn-close-ndt-modal" style="background: transparent; border: none; color: var(--color-graphite-400); cursor: pointer; padding: 4px; display: flex; align-items: center; justify-content: center; font-size: 1.25rem; line-height: 1;">&times;</button>
          </div>

          <div style="padding: 20px; display: flex; flex-direction: column; gap: 16px;">
            ${isOther ? `
              <div class="chk-field-group" style="margin-bottom: 0;">
                <label class="chk-field-label">Nome do Ensaio / Método Especial *</label>
                <input type="text" class="chk-input" id="inp-modal-ndt-other-name" placeholder="Ex: Termografia, Emissão Acústica..." value="${this.escapeHtml(this.modalNdtOtherName)}">
              </div>
            ` : ''}

            <div class="chk-field-group" style="margin-bottom: 0;">
              <label class="chk-field-label">Resultado da Inspeção / Ensaio *</label>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
                <label style="display: flex; align-items: center; gap: 8px; padding: 10px; border-radius: 6px; border: 1px solid ${this.modalNdtResult === 'APPROVED' ? 'var(--status-success-solid)' : 'var(--color-graphite-200)'}; background: ${this.modalNdtResult === 'APPROVED' ? 'var(--status-success-bg)' : '#ffffff'}; cursor: pointer; font-size: 0.825rem; font-weight: 600;">
                  <input type="radio" name="modal-ndt-result-rad" value="APPROVED" ${this.modalNdtResult === 'APPROVED' ? 'checked' : ''} style="accent-color: var(--status-success-text);">
                  <span style="color: var(--status-success-text);">APROVADO</span>
                </label>

                <label style="display: flex; align-items: center; gap: 8px; padding: 10px; border-radius: 6px; border: 1px solid ${this.modalNdtResult === 'REJECTED' ? 'var(--status-danger-solid)' : 'var(--color-graphite-200)'}; background: ${this.modalNdtResult === 'REJECTED' ? 'var(--status-danger-bg)' : '#ffffff'}; cursor: pointer; font-size: 0.825rem; font-weight: 600;">
                  <input type="radio" name="modal-ndt-result-rad" value="REJECTED" ${this.modalNdtResult === 'REJECTED' ? 'checked' : ''} style="accent-color: var(--status-danger-solid);">
                  <span style="color: var(--status-danger-text);">REPROVADO</span>
                </label>

                <label style="display: flex; align-items: center; gap: 8px; padding: 10px; border-radius: 6px; border: 1px solid ${this.modalNdtResult === 'CLIENT_DISCRETION' ? 'var(--status-warning-solid)' : 'var(--color-graphite-200)'}; background: ${this.modalNdtResult === 'CLIENT_DISCRETION' ? 'var(--status-warning-bg)' : '#ffffff'}; cursor: pointer; font-size: 0.825rem; font-weight: 600;">
                  <input type="radio" name="modal-ndt-result-rad" value="CLIENT_DISCRETION" ${this.modalNdtResult === 'CLIENT_DISCRETION' ? 'checked' : ''} style="accent-color: var(--status-warning-text);">
                  <span style="color: var(--status-warning-text);">A CRITÉRIO DO CLIENTE</span>
                </label>

                <label style="display: flex; align-items: center; gap: 8px; padding: 10px; border-radius: 6px; border: 1px solid ${this.modalNdtResult === 'NOT_APPLICABLE' ? 'var(--color-graphite-500)' : 'var(--color-graphite-200)'}; background: ${this.modalNdtResult === 'NOT_APPLICABLE' ? 'var(--color-bg-secondary)' : '#ffffff'}; cursor: pointer; font-size: 0.825rem; font-weight: 600;">
                  <input type="radio" name="modal-ndt-result-rad" value="NOT_APPLICABLE" ${this.modalNdtResult === 'NOT_APPLICABLE' ? 'checked' : ''} style="accent-color: var(--color-text-secondary);">
                  <span style="color: var(--color-text-secondary);">NÃO APLICÁVEL</span>
                </label>
              </div>
            </div>

            <div class="chk-field-group" style="margin-bottom: 0;">
              <label class="chk-field-label">Comentário Técnico / Observações</label>
              <textarea class="chk-textarea" id="inp-modal-ndt-comment" rows="4" placeholder="Descreva os achados técnicos, trecho inspecionado, norma utilizada ou justificativa...">${this.escapeHtml(this.modalNdtComment)}</textarea>
            </div>
          </div>

          <div style="background: var(--color-bg-primary); border-top: 1px solid var(--color-graphite-200); padding: 12px 20px; display: flex; justify-content: space-between; align-items: center; gap: 10px;">
            <div>
              <button type="button" id="btn-remove-ndt-modal" style="padding: 8px 14px; font-weight: 600; cursor: pointer; background: var(--status-danger-bg); border: 1px solid var(--status-danger-bg); border-radius: 6px; color: var(--status-danger-solid); display: flex; align-items: center; gap: 6px;" title="Remover e desmarcar este ensaio">
                <i data-lucide="trash-2" style="width: 14px; height: 14px;"></i> Desmarcar Ensaio
              </button>
            </div>
            <div style="display: flex; gap: 10px;">
              <button type="button" class="btn-modal-cancel" id="btn-cancel-ndt-modal" style="padding: 8px 16px; font-weight: 600; cursor: pointer; background: var(--color-surface-primary); border: 1px solid var(--color-graphite-300); border-radius: 6px; color: var(--color-text-secondary);">Cancelar</button>
              <button type="button" class="btn-modal-confirm" id="btn-save-ndt-modal" style="padding: 8px 18px; font-weight: 600; cursor: pointer; background: var(--ia-action-bg, var(--color-primary)); border: 1px solid var(--color-ink); border-radius: 6px; color: var(--ia-action-fg, #F7F8F8); display: flex; align-items: center; gap: 6px;">
                <i data-lucide="save" style="width: 14px; height: 14px;"></i> Salvar Ensaio
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private openNdtModal(methodId: string) {
    this.activeNdtMethodId = methodId;
    const existing = this.performedTests.find(t => t.methodId === methodId);
    if (existing) {
      this.modalNdtResult = existing.result || 'APPROVED';
      this.modalNdtComment = existing.comment || '';
      this.modalNdtOtherName = existing.otherName || '';
    } else {
      this.modalNdtResult = 'APPROVED';
      this.modalNdtComment = '';
      this.modalNdtOtherName = '';
    }
    this.showNdtModal = true;
    this.render();
  }

  private saveNdtFromModal() {
    if (!this.activeNdtMethodId) return;
    const existingIndex = this.performedTests.findIndex(t => t.methodId === this.activeNdtMethodId);
    
    if (this.activeNdtMethodId === 'OTHER' && !this.modalNdtOtherName.trim()) {
      alert('Por favor, informe o nome do ensaio/método especial.');
      return;
    }

    const newRecord: PerformedTestRecord = {
      methodId: this.activeNdtMethodId,
      result: this.modalNdtResult,
      comment: this.modalNdtComment.trim(),
      otherName: this.activeNdtMethodId === 'OTHER' ? this.modalNdtOtherName.trim() : undefined
    } as PerformedTestRecord;

    if (existingIndex >= 0) {
      this.performedTests[existingIndex] = newRecord;
    } else {
      this.performedTests.push(newRecord);
    }

    this.showNdtModal = false;
    this.activeNdtMethodId = null;
    this.persistLocally();
    this.render();

    // Sync remote update to database asynchronously so it is persisted immediately
    if (this.reportId) {
      try {
        const p = window.app.platform;
        const ctx = {
          activeClientId: this.selectedClientId || contextStore.get().activeClientId,
          userId: contextStore.get().userId || 'USER-1',
          correlationId: `cid-${Date.now()}`,
          permissions: contextStore.get().permissions
        } as any;
        p.reportService.getReportById(ctx, this.reportId as any).then((report: any) => {
          if (report) {
            report.performedInspectionMethods = this.performedTests;
            p.reportService.updateReport(ctx, report).then(() => {
              console.log('NDT tests successfully synchronized to report in database.');
            });
          }
        });
      } catch (err) {
        console.warn('NDT test saved locally (remote sync failed):', err);
      }
    }
  }

  private renderPhotoModal(): string {
    if (!this.showPhotoModal || !this.activePhotoCompId) return '';
    const comp = this.items.find(i => i.id === this.activePhotoCompId);
    const compName = comp ? comp.name : 'Componente';
    const isEditing = this.activePhotoIndex !== null;

    return `
      <div class="chk-modal-backdrop" id="backdrop-photo-modal" style="display: flex; align-items: center; justify-content: center; position: fixed; inset: 0; background: rgba(15, 23, 42, 0.7); z-index: 9999; padding: 16px; backdrop-filter: blur(4px);">
        <div class="chk-modal" style="background: var(--color-surface-primary); width: 100%; max-width: 540px; border-radius: 10px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2); overflow: hidden; animation: fadeIn 0.2s ease-out; border: 1px solid var(--color-graphite-200);">
          <div style="background: var(--color-graphite-800); color: var(--color-text-primary); padding: 16px 20px; display: flex; justify-content: space-between; align-items: center;">
            <div style="display: flex; align-items: center; gap: 10px;">
              <div style="background: var(--color-ink); padding: 6px; border-radius: 6px; display: flex;">
                <i data-lucide="camera" style="width: 18px; height: 18px; color: var(--color-text-primary);"></i>
              </div>
              <div>
                <h3 style="margin: 0; font-size: 1rem; font-weight: 700; color: var(--color-text-primary);">${isEditing ? 'Editar Registro Fotográfico' : 'Adicionar Registro Fotográfico'}</h3>
                <span style="font-size: 0.75rem; color: var(--color-graphite-400);">Componente: ${this.escapeHtml(compName)}</span>
              </div>
            </div>
            <button type="button" id="btn-close-photo-modal" style="background: transparent; border: none; color: var(--color-graphite-400); cursor: pointer; padding: 4px; display: flex; align-items: center; justify-content: center; font-size: 1.25rem; line-height: 1;">&times;</button>
          </div>

          <div style="padding: 20px; display: flex; flex-direction: column; gap: 16px;">
            <!-- Upload Area & Preview -->
            <div class="chk-field-group" style="margin-bottom: 0;">
              <label class="chk-field-label">Foto / Evidência Fotográfica *</label>
              
              <div id="photo-modal-dropzone" style="border: 2px dashed ${this.modalPhotoUrl ? 'var(--color-ink)' : 'var(--color-graphite-300)'}; border-radius: 8px; padding: 16px; text-align: center; background: ${this.modalPhotoUrl ? '#f0f9ff' : 'var(--color-bg-primary)'}; cursor: pointer; transition: all 0.2s ease; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; min-height: 140px;">
                ${this.modalPhotoUrl ? `
                  <div style="position: relative; max-height: 200px; width: 100%; display: flex; justify-content: center; overflow: hidden; border-radius: 6px;">
                    <img src="${this.modalPhotoUrl}" alt="Preview" style="max-height: 200px; width: auto; object-fit: contain; border-radius: 4px;">
                  </div>
                  <button type="button" id="btn-modal-change-photo" style="font-size: 0.75rem; color: var(--color-ink); background: var(--color-surface-primary); border: 1px solid var(--color-border-default); padding: 4px 12px; border-radius: 4px; font-weight: 600; cursor: pointer; margin-top: 4px;">Trocar Foto</button>
                ` : `
                  <i data-lucide="image" style="width: 36px; height: 36px; color: var(--color-graphite-400);"></i>
                  <div style="font-size: 0.85rem; font-weight: 600; color: var(--color-text-muted);">Clique para selecionar ou arraste uma foto aqui</div>
                  <div style="font-size: 0.75rem; color: var(--color-text-secondary);">Formatos aceitos: JPG, PNG, WEBP</div>
                `}
                <input type="file" id="inp-modal-photo-file" accept="image/*" style="display: none;">
              </div>
            </div>

            <!-- Comment -->
            <div class="chk-field-group" style="margin-bottom: 0;">
              <label class="chk-field-label">Comentário / Legenda da Foto *</label>
              <textarea class="chk-textarea" id="inp-modal-photo-comment" rows="3" placeholder="Descreva a evidência fotográfica, localização exata da anomalia ou condição visual observada...">${this.escapeHtml(this.modalPhotoComment)}</textarea>
            </div>
          </div>

          <div style="background: var(--color-bg-primary); border-top: 1px solid var(--color-graphite-200); padding: 12px 20px; display: flex; justify-content: flex-end; gap: 10px;">
            <button type="button" class="btn-modal-cancel" id="btn-cancel-photo-modal" style="padding: 8px 16px; font-weight: 600; cursor: pointer; background: var(--color-surface-primary); border: 1px solid var(--color-graphite-300); border-radius: 6px; color: var(--color-text-secondary);">Cancelar</button>
            <button type="button" class="btn-modal-confirm" id="btn-save-photo-modal" style="padding: 8px 18px; font-weight: 600; cursor: pointer; background: var(--ia-action-bg, var(--color-primary)); border: 1px solid var(--color-ink); border-radius: 6px; color: var(--ia-action-fg, #F7F8F8); display: flex; align-items: center; gap: 6px;">
              <i data-lucide="save" style="width: 14px; height: 14px;"></i> Salvar Foto
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private openPhotoModal(componentId: string, photoIndex?: number) {
    this.activePhotoCompId = componentId;
    this.activePhotoIndex = photoIndex !== undefined ? photoIndex : null;
    
    const comp = this.items.find(i => i.id === componentId);
    if (comp && photoIndex !== undefined && comp.photos && comp.photos[photoIndex]) {
      const ph = comp.photos[photoIndex];
      this.modalPhotoUrl = typeof ph === 'string' ? ph : (ph.url || ph.fileUrl || ph.image || '');
      this.modalPhotoComment = typeof ph === 'string' ? '' : (ph.comment || '');
    } else {
      this.modalPhotoUrl = '';
      this.modalPhotoComment = comp?.technicalComment || '';
    }

    this.showPhotoModal = true;
    this.render();
  }

  private savePhotoFromModal() {
    if (!this.activePhotoCompId) return;
    const comp = this.items.find(i => i.id === this.activePhotoCompId);
    if (!comp) return;

    if (!this.modalPhotoUrl) {
      alert('Por favor, anexe uma foto antes de salvar.');
      return;
    }

    if (!comp.photos) comp.photos = [];

    const photoObj = {
      id: `photo-${comp.id}-${Date.now()}`,
      url: this.modalPhotoUrl,
      comment: this.modalPhotoComment.trim()
    };

    if (this.activePhotoIndex !== null && this.activePhotoIndex >= 0) {
      comp.photos[this.activePhotoIndex] = photoObj;
    } else {
      comp.photos.push(photoObj);
    }

    this.showPhotoModal = false;
    this.activePhotoCompId = null;
    this.activePhotoIndex = null;
    this.modalPhotoUrl = '';
    this.modalPhotoComment = '';
    this.persistLocally();
    this.render();
  }

  // ------------------------------------------------- EDITOR DE IMAGEM

  /** Serviço de anotação com as permissões do Checklist (checklist.read/update). */
  private get annotationService(): any {
    return (window as any).app?.platform?.checklistImageAnnotationService;
  }

  private annotationCtx(): any {
    const current = contextStore.get();
    return {
      activeClientId: current.activeClientId,
      correlationId: '',
      userId: current.userId,
      professionalId: current.professionalId || null,
      permissions: current.permissions
    };
  }

  private photoAt(componentId: string, photoIndex: number): any | null {
    const comp = this.items.find(i => i.id === componentId);
    const photo = comp?.photos?.[photoIndex];
    if (!photo) return null;
    // Foto legada podia ser só a string do data URL — normaliza para objeto
    // sem perder o conteúdo (comando, itens 10/11).
    if (typeof photo === 'string') {
      const normalized = { id: `photo-${componentId}-${photoIndex}`, url: photo, comment: '' };
      comp!.photos[photoIndex] = normalized;
      return normalized;
    }
    return photo;
  }

  /**
   * EDITAR FOTO. Migra a foto legada (base64 inline) para o armazenamento
   * oficial sob demanda — uma única vez, de forma idempotente: se já existe
   * `originalFileId`, nada é regravado. Só então abre o editor, sempre com a
   * imagem ORIGINAL e as marcações já salvas.
   */
  private async openPhotoEditor(componentId: string, photoIndex: number): Promise<void> {
    const photo = this.photoAt(componentId, photoIndex);
    const service = this.annotationService;
    if (!photo || !service) return;

    this.annotatingBusy = true;
    this.render();
    try {
      if (!photo.originalFileId) {
        const source = photo.url || photo.fileUrl || photo.image || '';
        if (!source.startsWith('data:image')) {
          alert('Esta foto não pôde ser preparada para edição (formato não reconhecido).');
          return;
        }
        const stored = await service.storeOriginal(this.annotationCtx(), source);
        photo.originalFileId = stored.originalFileId;
        photo.annotatedFileId = null;
        photo.annotations = [];
        this.persistLocally();
      }

      const originalUrl = await service.resolveDataUrl(this.annotationCtx(), photo.originalFileId);
      if (!originalUrl) {
        alert('A imagem original não foi encontrada no armazenamento.');
        return;
      }
      this.annotatingCompId = componentId;
      this.annotatingPhotoIndex = photoIndex;
      this.annotatingOriginalUrl = originalUrl;
    } catch (error) {
      alert((error as any)?.safeMessage || (error as Error)?.message || 'Não foi possível abrir o editor.');
    } finally {
      this.annotatingBusy = false;
      this.render();
    }
  }

  private closePhotoEditor(): void {
    // CANCELAR: nada é gravado — a foto continua exatamente como estava.
    this.annotatingCompId = null;
    this.annotatingPhotoIndex = null;
    this.annotatingOriginalUrl = '';
    this.render();
  }

  /**
   * SALVAR EDIÇÃO. Grava a versão anotada como arquivo NOVO, mantém a
   * original e atualiza a foto exibida no Checklist. `url` passa a ser a
   * versão efetiva (anotada) para que todo consumidor atual da foto — RI,
   * snapshot, PDF — receba a imagem marcada sem precisar mudar.
   */
  private async savePhotoAnnotation(annotations: any[], renderedDataUrl: string): Promise<void> {
    const componentId = this.annotatingCompId;
    const photoIndex = this.annotatingPhotoIndex;
    if (componentId === null || photoIndex === null) return;
    const photo = this.photoAt(componentId, photoIndex);
    const service = this.annotationService;
    if (!photo || !service) return;

    try {
      const saved = await service.saveAnnotation(this.annotationCtx(), {
        image: {
          originalFileId: photo.originalFileId,
          annotatedFileId: photo.annotatedFileId ?? null,
          annotations: photo.annotations ?? [],
          updatedBy: null,
          updatedAt: null
        },
        annotations,
        renderedDataUrl,
        contextLabel: `CHECKLIST:${this.inspectionId || ''}|ITEM:${componentId}|PHOTO:${photo.id || photoIndex}`
      });

      photo.annotatedFileId = saved.annotatedFileId;
      photo.annotations = saved.annotations;
      photo.url = saved.annotatedFileId ? renderedDataUrl : await service.resolveDataUrl(this.annotationCtx(), photo.originalFileId);
      this.persistLocally();
      this.closePhotoEditor();
    } catch (error) {
      alert((error as any)?.safeMessage || (error as Error)?.message || 'Não foi possível salvar a edição.');
    }
  }

  /** RESTAURAR ORIGINAL: volta a exibir a foto sem marcações (o arquivo anotado não é apagado). */
  private async restorePhotoOriginal(componentId: string, photoIndex: number): Promise<void> {
    const photo = this.photoAt(componentId, photoIndex);
    const service = this.annotationService;
    if (!photo || !service || !photo.originalFileId) return;
    if (!confirm('Voltar a usar a foto original, sem as marcações?')) return;

    try {
      const restored = await service.restoreOriginal(this.annotationCtx(), {
        originalFileId: photo.originalFileId,
        annotatedFileId: photo.annotatedFileId ?? null,
        annotations: photo.annotations ?? [],
        updatedBy: null,
        updatedAt: null
      });
      photo.annotatedFileId = restored.annotatedFileId;
      photo.annotations = [];
      photo.url = await service.resolveDataUrl(this.annotationCtx(), photo.originalFileId);
      this.persistLocally();
      this.render();
    } catch (error) {
      alert((error as any)?.safeMessage || (error as Error)?.message || 'Não foi possível restaurar a original.');
    }
  }

  /**
   * Entrega a imagem e as marcações ao componente e escuta o resultado.
   * O editor é sempre aberto com a ORIGINAL — as marcações chegam como
   * objetos, por isso reabrir mostra cada uma editável (item 16).
   */
  private mountPhotoEditor(): void {
    if (this.annotatingCompId === null || this.annotatingPhotoIndex === null) return;
    const element = this.querySelector('#checklist-photo-editor') as any;
    if (!element || typeof element.open !== 'function') return;

    // Um render da página no meio da edição não pode reabrir o editor: isso
    // apagaria as marcações ainda não salvas e duplicaria os listeners.
    const key = `${this.annotatingCompId}#${this.annotatingPhotoIndex}`;
    if (element.dataset['mountedFor'] === key) return;
    element.dataset['mountedFor'] = key;

    const photo = this.photoAt(this.annotatingCompId, this.annotatingPhotoIndex);
    element.open(this.annotatingOriginalUrl, photo?.annotations ?? []);
    element.addEventListener('annotation-save', (event: any) => {
      void this.savePhotoAnnotation(event.detail?.annotations ?? [], event.detail?.renderedDataUrl ?? '');
    });
    element.addEventListener('annotation-cancel', () => this.closePhotoEditor());
  }

  /** Modal do editor — só aparece quando há uma foto aberta para edição. */
  private renderPhotoEditorModal(): string {
    if (this.annotatingCompId === null || !this.annotatingOriginalUrl) return '';
    return `
      <div class="photo-editor-overlay" style="position: fixed; inset: 0; background: rgba(0,0,0,0.75); z-index: 10000; display: flex; align-items: center; justify-content: center; padding: 12px;">
        <div style="width: 100%; max-width: 1100px; max-height: 96vh; overflow: auto;">
          <app-image-annotation-editor id="checklist-photo-editor"></app-image-annotation-editor>
        </div>
      </div>`;
  }

  private renderComponentCard(item: VisualChecklistItem): string {
    const isItemUnlocked = this.targetRec && item.recommendations?.some((r: any) => r.id === this.targetRec);
    let statusClass = 'status-pending';
    if (item.status === 'APPROVED') statusClass = 'status-conforming';
    else if (item.status === 'REJECTED') statusClass = 'status-non-conforming';
    else if (item.status === 'NOT_APPLICABLE') statusClass = 'status-na';

    const hasRecommendations = (item.recommendations && item.recommendations.length > 0);

    return `
      <div class="chk-item-card" data-comp-id="${item.id}">
        <div class="chk-item-main-row">
          <div class="chk-item-left">
            <span class="chk-item-index">${item.index}</span>
            <span class="chk-item-name">${item.name}</span>
            ${hasRecommendations ? `
              <span style="font-size: 0.75rem; color: var(--status-warning-text); font-weight: 700; margin-left: 8px; background: var(--status-warning-bg); border: 1px solid var(--status-warning-border); padding: 2px 6px; border-radius: 4px; display: inline-flex; align-items: center; gap: 4px;">
                <i data-lucide="alert-circle" style="width: 12px; height: 12px;"></i>
                Recomendação: ${item.recommendations!.map(r => r.code || r.id).join(', ')}
              </span>
            ` : ''}
          </div>

          <div class="chk-item-right">
            <div class="status-select-wrap">
              <select class="status-select ${statusClass} sel-comp-status" data-comp-id="${item.id}" ${this.isLocked && !isItemUnlocked ? 'disabled' : ''}>
                <option value="PENDING" ${item.status === 'PENDING' ? 'selected' : ''}>Selecionar resultado</option>
                <option value="APPROVED" ${item.status === 'APPROVED' ? 'selected' : ''}>APROVADO</option>
                <option value="REJECTED" ${item.status === 'REJECTED' ? 'selected' : ''}>REPROVADO</option>
                <option value="NOT_APPLICABLE" ${item.status === 'NOT_APPLICABLE' ? 'selected' : ''}>NÃO APLICÁVEL</option>
              </select>
              <i data-lucide="chevron-down" class="status-chevron"></i>
            </div>

            <button type="button" class="btn-toggle-details" data-comp-id="${item.id}">
              <i data-lucide="file-text" style="width: 14px; height: 14px;"></i> Detalhes
              <i data-lucide="${item.isExpanded ? 'chevron-up' : 'chevron-down'}" style="width: 14px; height: 14px;"></i>
            </button>

            ${(!this.isLocked && item.isManual) ? `
              ${this.deleteConfirmingCompId === item.id ? `
                <div style="display: inline-flex; align-items: center; gap: 4px; background: rgba(239,68,68,0.1); border: 1px solid var(--status-danger-bg); padding: 4px 8px; border-radius: 6px;">
                  <span style="font-size: 0.75rem; color: var(--status-danger-text); font-weight: 600;">Confirmar?</span>
                  <button type="button" class="btn-confirm-delete-comp" data-comp-id="${item.id}" style="background: var(--status-danger-solid); border: none; color: var(--color-text-primary); padding: 4px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; cursor: pointer; display: flex; align-items: center;">Sim</button>
                  <button type="button" class="btn-cancel-delete-comp" style="background: var(--color-graphite-200); border: none; color: var(--color-text-secondary); padding: 4px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; cursor: pointer; display: flex; align-items: center;">Não</button>
                </div>
              ` : `
                <button type="button" class="btn-delete-component" data-comp-id="${item.id}" title="Excluir componente manual do checklist" style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-bg); color: var(--status-danger-solid); padding: 6px 10px; border-radius: 6px; font-size: 0.8rem; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; transition: all 0.15s ease;">
                  <i data-lucide="trash-2" style="width: 14px; height: 14px;"></i> Excluir Manual
                </button>
              `}
            ` : ''}
          </div>
        </div>

        ${item.isExpanded ? `
          <div class="chk-item-expanded-box">
            <div class="chk-detail-inputs-grid">
              <div class="chk-field-group">
                <label class="chk-field-label">Condição encontrada *</label>
                <select class="chk-select sel-item-condition" data-comp-id="${item.id}" ${this.isLocked && !isItemUnlocked ? 'disabled' : ''}>
                  <option value="" disabled selected hidden>Selecione um achado ou digite</option>
                  <option value="Sem anomalias (Aprovado)" ${item.condition === 'Sem anomalias (Aprovado)' || item.condition === 'Sem anomalias / Conforme' ? 'selected' : ''}>Sem anomalias (Aprovado)</option>
                  <option value="Corrosão leve (Aceitável)" ${item.condition === 'Corrosão leve (Aceitável)' ? 'selected' : ''}>Corrosão leve (Aceitável)</option>
                  <option value="Corrosão severa (Reprovado)" ${item.condition === 'Corrosão severa (Reprovado)' ? 'selected' : ''}>Corrosão severa (Reprovado)</option>
                  <option value="Corrosão generalizada" ${item.condition === 'Corrosão generalizada' ? 'selected' : ''}>Corrosão generalizada</option>
                  <option value="Corrosão alveolar / pite" ${item.condition === 'Corrosão alveolar / pite' ? 'selected' : ''}>Corrosão alveolar / pite</option>
                  <option value="Trinca / Deformação estrutural" ${item.condition === 'Trinca / Deformação estrutural' ? 'selected' : ''}>Trinca / Deformação estrutural</option>
                  <option value="Desplacamento de pintura" ${item.condition === 'Desplacamento de pintura' ? 'selected' : ''}>Desplacamento de pintura</option>
                  <option value="Vazamento / Gotejamento" ${item.condition === 'Vazamento / Gotejamento' ? 'selected' : ''}>Vazamento / Gotejamento</option>
                  <option value="Desgaste mecânico" ${item.condition === 'Desgaste mecânico' ? 'selected' : ''}>Desgaste mecânico</option>
                  <option value="Outras anomalias..." ${item.condition === 'Outras anomalias...' ? 'selected' : ''}>Outras anomalias...</option>
                </select>
              </div>

              <div class="chk-field-group">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
                  <label class="chk-field-label" style="margin-bottom: 0;">Comentário técnico *</label>
                  ${(!this.isLocked || isItemUnlocked) ? `
                    <button type="button" class="btn-save-comment" data-comp-id="${item.id}" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); color: var(--color-text-muted); font-size: 0.72rem; font-weight: 600; padding: 2px 8px; border-radius: 4px; cursor: pointer; display: flex; align-items: center; gap: 4px;">
                      <i data-lucide="save" style="width: 12px; height: 12px;"></i> Salvar Comentário
                    </button>
                  ` : ''}
                </div>
                <textarea class="chk-textarea txt-item-comment" data-comp-id="${item.id}" maxlength="500" placeholder="Descreva a condição encontrada, observações e detalhes técnicos..." ${this.isLocked && !isItemUnlocked ? 'readonly' : ''}>${this.escapeHtml(item.technicalComment)}</textarea>
                <div class="char-counter">${item.technicalComment.length} / 500</div>
              </div>
            </div>

            ${(!this.isLocked || isItemUnlocked) ? `
              <div class="chk-actions-subbar">
                <button type="button" class="btn-sub-action btn-add-photo" data-comp-id="${item.id}" style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border-color: var(--color-ink); font-weight: 600;">
                  <i data-lucide="camera" style="width: 14px; height: 14px;"></i> + Adicionar Foto
                </button>

                <button type="button" class="btn-sub-action btn-add-rec" data-comp-id="${item.id}" style="color: var(--status-warning-text); border-color: var(--status-warning-border); background: var(--status-warning-bg);">
                  <i data-lucide="plus-circle" style="width: 14px; height: 14px;"></i> + Adicionar Recomendação
                </button>

                <button type="button" class="btn-sub-action ai-btn btn-gen-rec" data-comp-id="${item.id}">
                  <i data-lucide="sparkles" style="width: 14px; height: 14px;"></i> Sugerir recomendação (IA)
                </button>
              </div>
            ` : ''}

            ${item.photos.length > 0 ? `
              <div class="photos-preview-strip" style="display: flex; gap: 12px; flex-wrap: wrap; margin-top: 10px;">
                ${item.photos.map((ph, pIdx) => {
                  const photoUrl = typeof ph === 'string' ? ph : (ph.url || ph.fileUrl || ph.image || '');
                  const photoComment = typeof ph === 'string' ? '' : (ph.comment || '');
                  // EDITADA quando existe versão anotada; a etiqueta é só UI —
                  // nunca é gravada dentro do arquivo da foto (item 19).
                  const isAnnotated = typeof ph !== 'string' && !!ph.annotatedFileId;
                  return `
                    <div class="photo-thumb-card" style="display: flex; flex-direction: column; width: 230px; background: var(--color-surface-primary); border: 1px solid var(--color-graphite-300); border-radius: 6px; overflow: hidden; padding: 6px; gap: 6px; box-shadow: 0 1px 3px rgba(0,0,0,0.05);">
                      <div style="position: relative; width: 100%; height: 130px; background: var(--color-graphite-800); border-radius: 4px; overflow: hidden; display: flex; align-items: center; justify-content: center;">
                        <img src="${photoUrl}" alt="Evidência ${item.name}" style="width: 100%; height: 100%; object-fit: cover;">
                        <span title="${isAnnotated ? 'Exibindo a versão com marcações' : 'Exibindo a foto original'}" style="position: absolute; bottom: 4px; left: 4px; font-size: 0.58rem; font-weight: 700; letter-spacing: 0.06em; padding: 2px 6px; border-radius: 999px; background: ${isAnnotated ? 'rgba(37,99,235,0.92)' : 'rgba(0,0,0,0.6)'}; color: var(--ia-fg, #fff);">${isAnnotated ? 'EDITADA' : 'ORIGINAL'}</span>
                        ${(!this.isLocked || isItemUnlocked) ? `
                          <button type="button" class="photo-thumb-delete btn-del-photo" data-comp-id="${item.id}" data-photo-idx="${pIdx}" title="Excluir foto" style="position: absolute; top: 4px; right: 4px; width: 22px; height: 22px; background: rgba(239, 68, 68, 0.9); color: var(--color-text-primary); border: none; border-radius: 50%; font-weight: bold; cursor: pointer; display: flex; align-items: center; justify-content: center;">×</button>
                        ` : ''}
                      </div>
                      <div style="display: flex; flex-direction: column; gap: 4px;">
                        <div style="font-size: 0.725rem; font-weight: 600; color: var(--color-text-muted); min-height: 28px; background: var(--color-bg-primary); padding: 4px 6px; border-radius: 4px; border: 1px solid var(--color-graphite-200);">
                          ${photoComment ? this.escapeHtml(photoComment) : '<span style="color: var(--color-graphite-400); font-style: italic;">Sem comentário</span>'}
                        </div>
                        ${(!this.isLocked || isItemUnlocked) ? `
                          <div style="display: flex; gap: 4px; flex-wrap: wrap;">
                            <button type="button" class="btn-edit-photo-modal" data-comp-id="${item.id}" data-photo-idx="${pIdx}" style="flex: 1 1 100px; min-height: 34px; background: var(--color-surface-secondary); border: 1px solid var(--color-border-default); color: var(--color-ink); font-size: 0.7rem; font-weight: 600; padding: 4px; border-radius: 4px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 4px;">
                              <i data-lucide="camera" style="width: 11px; height: 11px;"></i> Comentar
                            </button>
                            <button type="button" class="btn-annotate-photo" data-comp-id="${item.id}" data-photo-idx="${pIdx}" title="Abrir o editor de marcações" style="flex: 1 1 100px; min-height: 34px; background: var(--color-primary); border: 1px solid var(--color-primary); color: var(--ia-fg, #fff); font-size: 0.7rem; font-weight: 700; padding: 4px; border-radius: 4px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 4px;">
                              <i data-lucide="pencil" style="width: 11px; height: 11px;"></i> EDITAR FOTO
                            </button>
                            ${isAnnotated ? `
                              <button type="button" class="btn-restore-photo" data-comp-id="${item.id}" data-photo-idx="${pIdx}" title="Voltar a usar a foto original" style="flex: 1 1 100%; min-height: 32px; background: transparent; border: 1px solid var(--color-border-default); color: var(--color-text-muted); font-size: 0.66rem; font-weight: 600; padding: 4px; border-radius: 4px; cursor: pointer;">
                                RESTAURAR ORIGINAL
                              </button>
                            ` : ''}
                          </div>
                        ` : ''}
                      </div>
                    </div>
                  `;
                }).join('')}
              </div>
            ` : ''}

            ${(item.recommendations && item.recommendations.length > 0) ? `
              <div class="rec-section-container">
                <div class="rec-section-header">
                  <span class="rec-section-title">
                    <i data-lucide="wrench" style="width: 15px; height: 15px; color: var(--ia-primary);"></i>
                    Recomendações Técnicas (${item.recommendations.length})
                  </span>
                </div>

                ${item.recommendations.map((rec, rIdx) => `
                  <div class="rec-card" data-rec-id="${rec.id}">
                    <div class="rec-card-topbar">
                      <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                        <span style="font-weight: 700; font-size: 0.8rem; color: var(--color-text-muted);">${rec.code || `REC-${(rIdx + 1).toString().padStart(3, '0')}`}</span>
                        <span class="rec-crit-badge crit-${rec.criticality.toLowerCase()}">
                          <i data-lucide="alert-circle" style="width: 12px; height: 12px;"></i>
                          ${rec.criticality}
                        </span>
                        <span style="font-size: 0.75rem; color: var(--color-text-secondary); font-weight: 500;">
                          ${rec.discipline} • Execução: ${rec.execution === 'PARADA' ? 'Parada' : 'Rotina'}
                        </span>
                        ${rec.zr ? `<span style="font-size: 0.7rem; background: var(--color-graphite-200); color: var(--color-text-muted); padding: 2px 6px; border-radius: 4px; font-weight: 600;">OS/ZR: ${this.escapeHtml(rec.zr)}</span>` : ''}
                        ${this.renderMinutaRecBadge(rec.id)}
                      </div>
                      ${(() => {
                        const correction = this.minutaCorrections?.find(c => c.recommendationId === rec.id && (c.status === 'PENDENTE' || c.status === 'DEVOLVIDA' || c.status === 'EM CORREÇÃO'));
                        if (correction) {
                          return `
                            <div style="margin-top: 8px; padding: 12px; background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 6px;">
                              <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                                <div>
                                  <span style="color: var(--status-danger-solid); font-weight: 700; font-size: 0.75rem; display: block; margin-bottom: 4px;">⚠️ ENGENHARIA SOLICITOU CORREÇÃO</span>
                                  <p style="margin: 0; font-size: 0.875rem; color: var(--status-danger-text);">Motivo: ${correction.reason}</p>
                                </div>
                                <button type="button" class="btn-submit-correction" data-corrid="${correction.id}" data-recid="${rec.id}" style="background: var(--status-danger-solid); color: white; border: none; padding: 6px 12px; border-radius: 4px; font-weight: 600; cursor: pointer; font-size: 0.75rem;">Enviar para Validação</button>
                              </div>
                            </div>
                          `;
                        }
                        return '';
                      })()}
                      <div style="display: flex; gap: 6px; align-items: center;">
                        ${(!this.isLocked || this.targetRec === rec.id) ? `
                          <button type="button" class="btn-save-rec-item" data-comp-id="${item.id}" data-rec-id="${rec.id}" style="background: var(--color-bg-primary); border: 1px solid var(--color-graphite-300); color: var(--color-text-muted); font-size: 0.72rem; font-weight: 600; padding: 2px 8px; border-radius: 4px; cursor: pointer; display: flex; align-items: center; gap: 4px;">
                            <i data-lucide="save" style="width: 11px; height: 11px;"></i> Salvar
                          </button>
                          <button type="button" class="btn-del-rec btn-remove-rec" data-comp-id="${item.id}" data-rec-id="${rec.id}">
                            <i data-lucide="trash-2" style="width: 13px; height: 13px;"></i> Remover
                          </button>
                        ` : ''}
                      </div>
                    </div>

                    <div class="rec-fields-grid" style="grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));">
                      <div class="chk-field-group">
                        <label class="chk-field-label">Criticidade *</label>
                        <select class="chk-select sel-rec-criticality" data-comp-id="${item.id}" data-rec-id="${rec.id}" ${this.isLocked && this.targetRec !== rec.id ? 'disabled' : ''}>
                          <option value="BAIXA" ${rec.criticality === 'BAIXA' ? 'selected' : ''}>Baixa</option>
                          <option value="MÉDIA" ${rec.criticality === 'MÉDIA' ? 'selected' : ''}>Média</option>
                          <option value="ALTA" ${rec.criticality === 'ALTA' ? 'selected' : ''}>Alta</option>
                          <option value="CRÍTICA" ${rec.criticality === 'CRÍTICA' ? 'selected' : ''}>Crítica</option>
                        </select>
                      </div>

                      <div class="chk-field-group">
                        <label class="chk-field-label">Disciplina *</label>
                        <select class="chk-select sel-rec-discipline" data-comp-id="${item.id}" data-rec-id="${rec.id}" ${this.isLocked && this.targetRec !== rec.id ? 'disabled' : ''}>
                          <option value="CALDEIRARIA" ${rec.discipline === 'CALDEIRARIA' ? 'selected' : ''}>Caldeiraria</option>
                          <option value="TUBULAÇÃO" ${rec.discipline === 'TUBULAÇÃO' ? 'selected' : ''}>Tubulação</option>
                          <option value="MECÂNICA" ${rec.discipline === 'MECÂNICA' ? 'selected' : ''}>Mecânica</option>
                          <option value="PINTURA" ${rec.discipline === 'PINTURA' ? 'selected' : ''}>Pintura</option>
                          <option value="ISOLAMENTO TÉRMICO" ${rec.discipline === 'ISOLAMENTO TÉRMICO' ? 'selected' : ''}>Isolamento Térmico</option>
                          <option value="INSTRUMENTAÇÃO" ${rec.discipline === 'INSTRUMENTAÇÃO' ? 'selected' : ''}>Instrumentação</option>
                          <option value="CIVIL" ${rec.discipline === 'CIVIL' ? 'selected' : ''}>Civil</option>
                          <option value="ELÉTRICA" ${rec.discipline === 'ELÉTRICA' ? 'selected' : ''}>Elétrica</option>
                        </select>
                      </div>

                      <div class="chk-field-group">
                        <label class="chk-field-label">Executar *</label>
                        <select class="chk-select sel-rec-execution" data-comp-id="${item.id}" data-rec-id="${rec.id}" ${this.isLocked && this.targetRec !== rec.id ? 'disabled' : ''}>
                          <option value="PARADA" ${rec.execution === 'PARADA' ? 'selected' : ''}>Parada</option>
                          <option value="ROTINA" ${rec.execution === 'ROTINA' ? 'selected' : ''}>Rotina</option>
                        </select>
                      </div>

                      <div class="chk-field-group">
                        <label class="chk-field-label">OS / ZR</label>
                        <div class="chk-input" style="background: var(--color-graphite-700); color: var(--color-text-muted); cursor: not-allowed; display: flex; align-items: center;">
                          ${rec.zr ? this.escapeHtml(rec.zr) : 'Preencher na aba Recomendações'}
                        </div>
                      </div>
                    </div>

                    <div class="chk-field-group" style="margin-top: 2px;">
                      <label class="chk-field-label">Descritivo da recomendação *</label>
                      <textarea class="chk-textarea txt-rec-desc" data-comp-id="${item.id}" data-rec-id="${rec.id}" maxlength="500" placeholder="Informe o descritivo técnico detalhado da recomendação..." ${this.isLocked && this.targetRec !== rec.id ? 'readonly' : ''}>${this.escapeHtml(rec.description)}</textarea>
                      <div class="char-counter">${rec.description.length} / 500</div>
                    </div>
                  </div>
                `).join('')}
              </div>
            ` : ''}
          </div>
        ` : ''}
        
      </div>
    `;
  }

  private attachEvents() {
    this.bindEngineeringEvents();
    // Stepper & Step Navigation Logic
    this.querySelectorAll('.wizard-step').forEach(el => {
      el.addEventListener('click', (e) => {
        const stepNum = parseInt((e.currentTarget as HTMLElement).dataset['step'] || '1', 10);
        if (stepNum <= this.currentStep) {
          this.currentStep = stepNum as any;
          this.render();
        }
      });
    });

    this.querySelector('#btn-step-prev')?.addEventListener('click', () => {
      if (this.currentStep > 1) {
        this.currentStep--;
        this.render();
      }
    });

    this.querySelector('#btn-step-next')?.addEventListener('click', () => {
      if (this.currentStep === 1) {
        if (!this.isInspectionDataComplete()) {
          const missing = this.getMissingInspectionFields();
          const errors = this.getInspectionValidationErrors();
          const messages = [];
          if (missing.length > 0) messages.push(`Preencha os seguintes campos antes de prosseguir:\n• ${missing.join('\n• ')}`);
          if (errors.length > 0) messages.push(...errors);
          
          this.showValidationErrors(messages);
          return;
        }
        if (!this.isStep1Locked) {
          this.showValidationErrors(['GERAÇÃO DO RELATÓRIO PENDENTE\n\nGere o número do relatório antes de continuar para a próxima etapa.']);
          return;
        }
      }

      if (this.currentStep < 4) {
        this.currentStep++;
        this.render();
      }
    });

    // Link the step save button to the existing save button flow
    this.querySelector('#btn-step-save-btn')?.addEventListener('click', () => {
      const btnSave = this.querySelector('#btn-header-save') as HTMLButtonElement;
      if (btnSave) btnSave.click();
    });

    // Link step 4 generate report button to existing flow
    this.querySelector('#btn-step-generate-ri')?.addEventListener('click', () => {
      const btnGen = this.querySelector('#btn-header-generate-ri') as HTMLButtonElement;
      if (btnGen) btnGen.click();
    });

    // Link step 4 release PLH button to existing flow
    this.querySelector('#btn-step-release-plh')?.addEventListener('click', () => {
      const btnRel = this.querySelector('#btn-header-release-plh') as HTMLButtonElement;
      if (btnRel) btnRel.click();
    });
    this.querySelector('#btn-add-ref-doc')?.addEventListener('click', () => {
      this.referenceDocuments.push({
        id: `ref-doc-${Date.now()}`,
        name: '',
        description: ''
      });
      this.persistLocally();
      this.render();
    });

    this.querySelectorAll('.ref-doc-name').forEach(el => {
      el.addEventListener('input', (e) => {
        const target = e.target as HTMLInputElement;
        const id = target.dataset['id'];
        const doc = this.referenceDocuments.find(d => d.id === id);
        if (doc) {
          doc.name = target.value;
          this.persistLocally();
        }
      });
    });

    this.querySelectorAll('.ref-doc-desc').forEach(el => {
      el.addEventListener('input', (e) => {
        const target = e.target as HTMLInputElement;
        const id = target.dataset['id'];
        const doc = this.referenceDocuments.find(d => d.id === id);
        if (doc) {
          doc.description = target.value;
          this.persistLocally();
        }
      });
    });

    this.querySelectorAll('.ref-doc-file').forEach(el => {
      el.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement;
        const id = target.dataset['id'];
        const doc = this.referenceDocuments.find(d => d.id === id);
        if (doc && target.files && target.files.length > 0) {
          const file = target.files[0] as File;
          doc.fileName = file.name;
          
          const reader = new FileReader();
          reader.onload = (loadEvt) => {
            if (loadEvt.target?.result) {
              doc.fileData = loadEvt.target.result as string;
              this.persistLocally();
              this.render();
            }
          };
          reader.readAsDataURL(file);
        }
      });
    });

    this.querySelectorAll('.ref-doc-delete').forEach(el => {
      el.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const id = target.dataset['id'];
        this.referenceDocuments = this.referenceDocuments.filter(d => d.id !== id);
        this.persistLocally();
        this.render();
      });
    });

    this.querySelectorAll('.ref-doc-view').forEach(el => {
      el.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const id = target.dataset['id'];
        const doc = this.referenceDocuments.find(d => d.id === id);
        if (doc && doc.fileData) {
          const newWindow = window.open();
          if (newWindow) {
            newWindow.document.write(`<iframe src="${doc.fileData}" style="border:0; top:0; left:0; bottom:0; right:0; width:100%; height:100%;" allowfullscreen></iframe>`);
          }
        }
      });
    });

    this.querySelector('#chk-manut')?.addEventListener('input', (e) => {
      this.maintenanceServices = (e.target as HTMLTextAreaElement).value;
    });

    this.querySelectorAll('.txt-ndt-inline-comment').forEach(el => {
      el.addEventListener('input', (e) => {
        const target = e.target as HTMLTextAreaElement;
        const methodId = target.dataset['id'];
        const pt = this.performedTests.find(t => t.methodId === methodId);
        if (pt) {
          pt.comment = target.value;
          this.persistLocally();
        }
      });
    });

    this.querySelectorAll('.chk-ndt-method').forEach(el => {
      el.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement;
        const methodId = target.value;
        if (target.checked) {
          const exists = this.performedTests.find(t => t.methodId === methodId);
          if (!exists) {
            this.performedTests.push({ methodId, result: 'APPROVED', comment: '' });
            this.persistLocally();
            this.render();
          }
          this.openNdtModal(methodId);
        } else {
          const existing = this.performedTests.find(t => t.methodId === methodId);
          if (existing && (existing.result || existing.comment || (methodId === 'OTHER' && existing.otherName))) {
            let confirmDelete = true;
            try {
              confirmDelete = window.confirm("Este ensaio possui informações preenchidas. Deseja removê-lo das inspeções e ensaios realizados?");
            } catch (err) {
              confirmDelete = true; // sandbox fallback
            }
            if (!confirmDelete) {
              target.checked = true; // revert
              return;
            }
          }
          this.performedTests = this.performedTests.filter(t => t.methodId !== methodId);
          this.persistLocally();
          this.render();
        }
      });
    });

    this.querySelectorAll('.rad-ndt-res').forEach(el => {
      el.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement;
        const methodId = target.dataset['id'];
        const val = target.value as any;
        const pt = this.performedTests.find(t => t.methodId === methodId);
        if (pt) {
          pt.result = val;
          this.persistLocally();
        }
      });
    });

    this.querySelectorAll('.txt-ndt-comment').forEach(el => {
      el.addEventListener('input', (e) => {
        const target = e.target as HTMLTextAreaElement;
        const methodId = target.dataset['id'];
        const pt = this.performedTests.find(t => t.methodId === methodId);
        if (pt) {
          pt.comment = target.value;
          this.persistLocally();
        }
      });
    });

    this.querySelectorAll('.inp-ndt-other-name').forEach(el => {
      el.addEventListener('input', (e) => {
        const target = e.target as HTMLInputElement;
        const pt = this.performedTests.find(t => t.methodId === 'OTHER');
        if (pt) {
          pt.otherName = target.value;
          this.persistLocally();
        }
      });
    });

    this.querySelectorAll('.btn-save-ndt-test').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const targetBtn = e.currentTarget as HTMLButtonElement;
        const methodId = targetBtn.dataset['id'];
        if (!methodId) return;

        const radioChecked = this.querySelector(`input[name="ndt-res-${methodId}"]:checked`) as HTMLInputElement | null;
        const commentArea = this.querySelector(`.txt-ndt-comment[data-id="${methodId}"]`) as HTMLTextAreaElement | null;
        const otherNameInp = this.querySelector(`.inp-ndt-other-name[data-id="OTHER"]`) as HTMLInputElement | null;

        let pt = this.performedTests.find(t => t.methodId === methodId);
        if (!pt) {
          pt = { methodId };
          this.performedTests.push(pt);
        }

        if (radioChecked) pt.result = radioChecked.value as any;
        if (commentArea) pt.comment = commentArea.value;
        if (methodId === 'OTHER' && otherNameInp) pt.otherName = otherNameInp.value;

        this.persistLocally();

        if (this.reportId) {
          try {
            const p = window.app.platform;
            const ctx = {
              activeClientId: this.selectedClientId || this.clientId || contextStore.get().activeClientId,
              userId: contextStore.get().userId || 'USER-1',
              correlationId: `cid-${Date.now()}`,
              permissions: contextStore.get().permissions
            } as any;
            const report = await p.reportService.getReportById(ctx, this.reportId as any);
            if (report) {
              (report as any).performedInspectionMethods = this.performedTests;
              await p.reportService.updateReport(ctx, report);
            }
          } catch (err) {
            // Silently fallback if report document is not created in backend yet; state is persisted in localStorage
            console.warn('NDT test saved locally (report draft not yet synchronized):', err);
          }
        }

        const originalHtml = targetBtn.innerHTML;
        targetBtn.style.background = 'var(--status-success-solid)';
        targetBtn.style.borderColor = 'var(--status-success-solid)';
        targetBtn.style.color = '#ffffff';
        targetBtn.innerHTML = `<i data-lucide="check" style="width: 12px; height: 12px;"></i> Salvo!`;
        if (typeof (window as any).lucide !== 'undefined') {
          (window as any).lucide.createIcons({ root: targetBtn });
        }
        setTimeout(() => {
          targetBtn.style.background = '';
          targetBtn.style.borderColor = '';
          targetBtn.style.color = '';
          targetBtn.innerHTML = originalHtml;
          if (typeof (window as any).lucide !== 'undefined') {
            (window as any).lucide.createIcons({ root: targetBtn });
          }
        }, 1500);
      });
    });

    this.querySelector('.btn-save-all-ndt')?.addEventListener('click', async (e) => {
      const targetBtn = e.currentTarget as HTMLButtonElement;
      
      this.performedTests.forEach(pt => {
        const radioChecked = this.querySelector(`input[name="ndt-res-${pt.methodId}"]:checked`) as HTMLInputElement | null;
        const commentArea = this.querySelector(`.txt-ndt-comment[data-id="${pt.methodId}"]`) as HTMLTextAreaElement | null;
        const otherNameInp = this.querySelector(`.inp-ndt-other-name[data-id="OTHER"]`) as HTMLInputElement | null;
        if (radioChecked) pt.result = radioChecked.value as any;
        if (commentArea) pt.comment = commentArea.value;
        if (pt.methodId === 'OTHER' && otherNameInp) pt.otherName = otherNameInp.value;
      });

      this.persistLocally();

      if (this.reportId) {
        try {
          const p = window.app.platform;
          const ctx = {
            activeClientId: this.selectedClientId || this.clientId || contextStore.get().activeClientId,
            userId: contextStore.get().userId || 'USER-1',
            correlationId: `cid-${Date.now()}`,
            permissions: contextStore.get().permissions
          } as any;
          const report = await p.reportService.getReportById(ctx, this.reportId as any);
          if (report) {
            (report as any).performedInspectionMethods = this.performedTests;
            await p.reportService.updateReport(ctx, report);
          }
        } catch (err) {
          // Silently fallback if report document is not created in backend yet; state is persisted in localStorage
          console.warn('All NDT tests saved locally (report draft not yet synchronized):', err);
        }
      }

      const originalHtml = targetBtn.innerHTML;
      targetBtn.style.background = 'var(--status-success-solid)';
      targetBtn.style.borderColor = 'var(--status-success-solid)';
      targetBtn.style.color = '#ffffff';
      targetBtn.innerHTML = `<i data-lucide="check" style="width: 14px; height: 14px;"></i> Ensaios Salvos!`;
      if (typeof (window as any).lucide !== 'undefined') {
        (window as any).lucide.createIcons({ root: targetBtn });
      }
      setTimeout(() => {
        targetBtn.style.background = '';
        targetBtn.style.borderColor = '';
        targetBtn.style.color = '';
        targetBtn.innerHTML = originalHtml;
        if (typeof (window as any).lucide !== 'undefined') {
          (window as any).lucide.createIcons({ root: targetBtn });
        }
      }, 1800);
    });

    // Remove legacy listener
    // this.querySelector('#inp-ndt-other')?.addEventListener('input', ...);

    // Top Sync Banner Actions
    this.querySelector('#btn-top-view-ri')?.addEventListener('click', () => {
      this.persistLocally();
      if (this.reportId) {
        router.navigate(`/reports/integrity/view/${this.reportId}`);
      }
    });

    this.querySelector('#btn-top-goto-ris')?.addEventListener('click', () => {
      this.persistLocally();
      router.navigate('/reports/integrity');
    });

    // 1. Search input
    const searchInput = this.querySelector('#inp-search-comp') as HTMLInputElement;
    searchInput?.addEventListener('input', (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.render();
      // Keep focus on search input
      const newSearch = this.querySelector('#inp-search-comp') as HTMLInputElement;
      if (newSearch) {
        newSearch.focus();
        newSearch.setSelectionRange(newSearch.value.length, newSearch.value.length);
      }
    });

    // 2. Filter Pills
    this.querySelectorAll('.filter-pill-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const filter = (e.currentTarget as HTMLElement).dataset['filter'] as any;
        this.currentFilter = filter;
        this.render();
      });
    });

    // 3. Inspection header data changes
    this.querySelector('#sel-chk-client, #sel-inspection-client')?.addEventListener('change', (e) => {
      if (this.isHydratingContext) return;
      const cId = (e.target as HTMLSelectElement).value;
      this.selectedClientId = cId;
      const wo = this.workOrders.find(w => w.id === this.selectedWorkOrderId);
      if (!wo || (wo.clientId && wo.clientId !== cId)) {
        this.selectedWorkOrderId = '';
        this.selectedSubWorkOrderId = '';
        this.selectedOsCode = '';
        this.selectedSubOsCode = '';
      }
      const proj = this.projects.find(p => p.id === this.selectedProjectId);
      if (!proj || (proj.clientId && proj.clientId !== cId)) {
        this.selectedProjectId = '';
        this.selectedUnitAreaId = '';
      }
      const eq = this.registeredEquipments.find(item => item.id === this.equipmentId);
      if (!eq || (eq.clientId && eq.clientId !== cId)) {
        this.equipmentId = '';
        this.equipmentTag = '';
        this.equipmentName = '';
        this.activeEquipmentSnapshot = null as any;
      }

      const foundClient = this.clients.find(c => c.id === cId);
      this.clientName = foundClient?.name || foundClient?.legalName || '';

      this.persistLocally();
      this.render();
    });

    this.querySelector('#sel-chk-inspector, #sel-inspection-inspector')?.addEventListener('change', (e) => {
      if (this.isHydratingContext) return;
      this.selectedInspectorId = (e.target as HTMLSelectElement).value;
      // Auto adjust Sub-OS if not matching
      const matchingSub = this.subWorkOrders.find(s => s.professionalId === this.selectedInspectorId);
      if (matchingSub && (!this.selectedSubWorkOrderId || !this.subWorkOrders.find(s => s.id === this.selectedSubWorkOrderId && s.professionalId === this.selectedInspectorId))) {
        this.selectedSubWorkOrderId = matchingSub.id;
        this.selectedSubOsCode = matchingSub.code || '';
        if (matchingSub.workOrderId && !this.selectedWorkOrderId) {
          this.selectedWorkOrderId = matchingSub.workOrderId;
          const wo = this.workOrders.find(w => w.id === matchingSub.workOrderId);
          if (wo?.code) this.selectedOsCode = wo.code;
        }
      }
      this.persistLocally();
      this.render();
    });

    this.querySelector('#sel-chk-os, #sel-inspection-work-order')?.addEventListener('change', (e) => {
      if (this.isHydratingContext) return;
      const woId = (e.target as HTMLSelectElement).value;
      this.selectedWorkOrderId = woId;
      const wo = this.workOrders.find(w => w.id === woId);
      this.selectedOsCode = wo?.code || '';

      this.selectedSubWorkOrderId = '';
      this.selectedSubOsCode = '';

      // Auto select matching Sub-OS of inspector IF there is exactly one
      const matchingSubs = this.subWorkOrders.filter(s => s.workOrderId === woId && (s.professionalId === this.selectedInspectorId || !this.selectedInspectorId));
      if (matchingSubs.length === 1) {
        this.selectedSubWorkOrderId = matchingSubs[0].id;
        this.selectedSubOsCode = matchingSubs[0].code || '';
      }

      // If current project is incompatible with new OS, only then clear project
      if (woId && this.selectedProjectId && wo?.projectId) {
        if (wo.projectId !== this.selectedProjectId) {
          this.selectedProjectId = '';
          this.selectedUnitAreaId = '';
        }
      }

      this.persistLocally();
      this.render();
    });

    this.querySelector('#sel-chk-subos, #sel-inspection-sub-work-order')?.addEventListener('change', (e) => {
      if (this.isHydratingContext) return;
      const subId = (e.target as HTMLSelectElement).value;
      this.selectedSubWorkOrderId = subId;
      const sub = this.subWorkOrders.find(s => s.id === subId);
      this.selectedSubOsCode = sub?.code || '';
      if (sub?.workOrderId && !this.selectedWorkOrderId) {
        this.selectedWorkOrderId = sub.workOrderId;
        const wo = this.workOrders.find(w => w.id === sub.workOrderId);
        if (wo?.code) this.selectedOsCode = wo.code;
      }
      if (sub?.professionalId && !this.selectedInspectorId) {
        this.selectedInspectorId = sub.professionalId;
      }
      this.persistLocally();
      this.render();
    });

    this.querySelector('#sel-chk-project, #sel-inspection-project')?.addEventListener('change', (e) => {
      if (this.isHydratingContext) return;
      const pId = (e.target as HTMLSelectElement).value;
      this.selectedProjectId = pId;
      // Changing project NEVER clears OS, Sub-OS, OS code, or Sub-OS code!
      if (pId) {
        const uas = this.unitAreas.filter(u => u.projectId === pId);
        if (this.selectedUnitAreaId !== 'ALL' && !uas.some(u => u.id === this.selectedUnitAreaId)) {
          this.selectedUnitAreaId = '';
        }
      } else {
        this.selectedUnitAreaId = '';
      }

      this.persistLocally();
      this.render();
    });

    this.querySelector('#sel-chk-unit, #sel-inspection-unit-area')?.addEventListener('change', (e) => {
      const uId = (e.target as HTMLSelectElement).value;
      this.selectedUnitAreaId = uId;
      this.equipmentId = '';
      this.equipmentTag = '';
      this.equipmentName = '';
      this.activeEquipmentSnapshot = null as any;

      this.persistLocally();
      this.render();
    });

    this.querySelector('#sel-chk-equipment, #sel-inspection-equipment')?.addEventListener('change', async (e) => {
      const id = (e.target as HTMLSelectElement).value;
      if (id) {
        this.equipmentId = id;
        const found = this.registeredEquipments.find(eq => eq.id === id);
        if (found) {
          this.activeEquipmentSnapshot = found;
          this.equipmentTag = found.tag || found.id;
          this.equipmentName = found.name || found.description || 'Equipamento';
          if (found.projectId) this.selectedProjectId = found.projectId;
          if (found.unitAreaId || found.areaId) this.selectedUnitAreaId = found.unitAreaId || found.areaId;
        }
      } else {
        this.equipmentId = '';
        this.equipmentTag = '';
        this.equipmentName = '';
        this.activeEquipmentSnapshot = null as any;
      }
      await this.generateChecklistForSelectedEquipment(true);
      this.persistLocally();
      this.render();
    });

    this.querySelector('#sel-chk-condition, #sel-inspection-condition')?.addEventListener('change', (e) => {
      this.conditionState = (e.target as HTMLSelectElement).value;
      this.persistLocally();
    });
    this.querySelector('#sel-chk-nature, #sel-inspection-nature')?.addEventListener('change', (e) => {
      this.natureState = (e.target as HTMLSelectElement).value;
      this.persistLocally();
    });
    this.querySelector('#sel-chk-modality, #sel-inspection-modality')?.addEventListener('change', (e) => {
      this.modalityState = (e.target as HTMLSelectElement).value;
      this.persistLocally();
      this.render();
    });
    this.querySelector('#inp-chk-start-date, #inp-inspection-start-date')?.addEventListener('change', (e) => {
      this.startDate = (e.target as HTMLInputElement).value;
      this.persistLocally();
    });
    this.querySelector('#inp-chk-end-date, #inp-inspection-end-date')?.addEventListener('change', (e) => {
      this.endDate = (e.target as HTMLInputElement).value;
      this.persistLocally();
    });

    const triggerGenerateReportNumber = async () => {
      // 1. If reportNumber is already generated and is a genuine number
      if (this.reportNumber && this.reportNumber !== '-' && !this.reportNumber.includes('RI-NOVO') && !this.reportNumber.includes('Elaboração')) {
        const confirmar = confirm(`O relatório já possui número atribuído:\n\n${this.reportNumber}\n\nDeseja gerar um novo número de relatório?`);
        if (!confirmar) return;
      }

      // Auto-fallback activeClientId if not explicitly selected
      if (!this.selectedClientId) {
        this.selectedClientId = contextStore.get().activeClientId || (this.clients[0]?.id || '');
      }

      // STRICT VALIDATION
      if (!this.isInspectionDataComplete()) {
        const missing = this.getMissingInspectionFields();
        const errors = this.getInspectionValidationErrors();
        const messages = [];
        if (missing.length > 0) messages.push(`DADOS OBRIGATÓRIOS PENDENTES\n\nPreencha os seguintes campos antes de gerar o número do relatório:\n• ${missing.join('\n• ')}`);
        if (errors.length > 0) messages.push(...errors);
        
        this.showValidationErrors(messages);
        return;
      }

      const btnGen = this.querySelector('#btn-generate-chk-ri') as HTMLButtonElement;
      if (btnGen) {
        btnGen.disabled = true;
        btnGen.innerHTML = '<i data-lucide="loader" style="width: 14px; height: 14px;"></i>';
      }

      try {
        // Save first (formulário -> backend -> commit)
        const saved = await this.saveChecklistToBackend(true);
        if (!saved) {
          alert('Não foi possível salvar os dados da inspeção antes de gerar o número. Tente novamente.');
          return;
        }

        const activeClient = this.clients.find(c => c.id === this.selectedClientId);
        const cName = activeClient?.name || activeClient?.legalName || this.clientName || 'Cliente';
        
        const foundWo = this.workOrders.find(w => w.id === this.selectedWorkOrderId);
        const os = (this.selectedOsCode || foundWo?.code || '001').replace(/\D/g, '') || '001';
        
        const foundSub = this.subWorkOrders.find(s => s.id === this.selectedSubWorkOrderId);
        const subOs = (this.selectedSubOsCode || foundSub?.code || '01').replace(/\D/g, '') || '01';
        const ano = new Date().getFullYear();
        
        let sequencialCronologico = 1;
        try {
          const p = window.app.platform;
          const ctx = { ...sessionRequestContext(), activeClientId: this.selectedClientId } as any;
          const reports = await p.reportService.listReports(ctx);
          sequencialCronologico = (reports?.length || 0) + 1;
        } catch (e) {
          sequencialCronologico = Math.floor(Math.random() * 900) + 100;
        }
        
        const numFormatado = sequencialCronologico.toString().padStart(2, '0');
        const clientSigla = (cName.split(' ')[0] || 'CLI').substring(0, 3).toUpperCase().replace(/[^A-Z0-9]/g, '');
        
        this.reportNumber = `RI_${clientSigla}_${os}_${subOs}_${ano}_${numFormatado}`;
        this.persistLocally();

        if (this.reportId) {
          const p = window.app.platform;
          const ctx = {
            activeClientId: this.selectedClientId || contextStore.get().activeClientId,
            userId: contextStore.get().userId || 'usr_admin',
            permissions: contextStore.get().permissions
          } as any;
          const report = await p.reportService.getReportById(ctx, this.reportId as any);
          if (report) {
            report.reportNumber = this.reportNumber;
            await p.reportService.updateReport(ctx, report);
          }
        }
      } catch (err: any) {
        console.warn('Could not sync reportNumber immediately to backend:', err);
        alert('Não foi possível gerar o número do relatório.\nOs dados preenchidos foram preservados.');
      } finally {
        if (btnGen) {
          btnGen.disabled = false;
          btnGen.innerHTML = '<i data-lucide="zap" style="width: 14px; height: 14px;"></i>';
        }
        this.render();
      }
    };

    this.querySelector('#btn-generate-chk-ri')?.addEventListener('click', triggerGenerateReportNumber);
    this.querySelector('#btn-header-gen-number')?.addEventListener('click', triggerGenerateReportNumber);
    this.querySelector('#btn-footer-gen-number')?.addEventListener('click', triggerGenerateReportNumber);
    this.querySelector('#inp-chk-client-report-number')?.addEventListener('input', (e) => {
      this.clientReportNumber = (e.target as HTMLInputElement).value;
      this.persistLocally();
    });
    this.querySelector('#txt-general-obs')?.addEventListener('input', (e) => {
      this.generalObservations = (e.target as HTMLTextAreaElement).value;
      const counter = this.querySelector('#lbl-general-obs-counter');
      if (counter) counter.textContent = `${this.generalObservations.length} / 500`;
      this.persistLocally();
    });

    this.querySelector('#chk-manut')?.addEventListener('input', (e) => {
      this.maintenanceServices = (e.target as HTMLTextAreaElement).value;
      this.persistLocally();
    });

    this.querySelector('#txt-damage-mechanisms')?.addEventListener('input', (e) => {
      this.damageMechanisms = (e.target as HTMLTextAreaElement).value;
      const counter = this.querySelector('#lbl-damage-mechanisms-counter');
      if (counter) counter.textContent = `${this.damageMechanisms.length} / 2000`;
      this.persistLocally();
    });

    // 4. Status select dropdowns
    this.querySelectorAll('.sel-comp-status').forEach(sel => {
      sel.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        const compId = target.dataset['compId'];
        const item = this.items.find(i => i.id === compId);
        if (item) {
          // Auto-fill condition for APPROVED if not set
          if (target.value === 'APPROVED' && !item.condition) {
            item.condition = 'Sem anomalias visíveis / Conforme';
          }
          item.status = target.value as any;
          this.persistLocally();
          this.render();
        }
      });
    });

    // 5. Expand / Collapse details
    this.querySelectorAll('.btn-toggle-details').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const compId = (e.currentTarget as HTMLElement).dataset['compId'];
        const item = this.items.find(i => i.id === compId);
        if (item) {
          item.isExpanded = !item.isExpanded;
          this.persistLocally();
          this.render();
        }
      });
    });

    // 6. Condition changes
    this.querySelectorAll('.sel-item-condition').forEach(sel => {
      sel.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        const compId = target.dataset['compId'];
        const item = this.items.find(i => i.id === compId);
        if (item) {
          item.condition = target.value;
          if (item.condition.includes('Corrosão') || item.condition.includes('Trinca') || item.condition.includes('Vazamento')) {
            if (item.status === 'PENDING' || item.status === 'APPROVED') {
              item.status = 'REJECTED';
            }
          }
          this.persistLocally();
        }
      });
    });

    // 7. Technical comment changes
    this.querySelectorAll('.txt-item-comment').forEach(ta => {
      ta.addEventListener('input', (e) => {
        const target = e.target as HTMLTextAreaElement;
        const compId = target.dataset['compId'];
        const item = this.items.find(i => i.id === compId);
        if (item) {
          item.technicalComment = target.value;
          const parent = target.parentElement;
          const counter = parent?.querySelector('.char-counter');
          if (counter) counter.textContent = `${item.technicalComment.length} / 500`;
          this.persistLocally();
        }
      });
    });

    // 8. Photo addition and editing via Modal
    this.querySelectorAll('.btn-add-photo').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const compId = (e.currentTarget as HTMLElement).dataset['compId'];
        if (compId) {
          this.openPhotoModal(compId);
        }
      });
    });

    this.querySelectorAll('.btn-edit-photo-modal').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const compId = (e.currentTarget as HTMLElement).dataset['compId'];
        const pIdx = parseInt((e.currentTarget as HTMLElement).dataset['photoIdx'] || '0', 10);
        if (compId) {
          this.openPhotoModal(compId, pIdx);
        }
      });
    });

    // EDITAR FOTO / RESTAURAR ORIGINAL — editor de marcações da foto.
    this.querySelectorAll('.btn-annotate-photo').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const compId = (e.currentTarget as HTMLElement).dataset['compId'];
        const pIdx = parseInt((e.currentTarget as HTMLElement).dataset['photoIdx'] || '0', 10);
        if (compId) void this.openPhotoEditor(compId, pIdx);
      });
    });
    this.querySelectorAll('.btn-restore-photo').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const compId = (e.currentTarget as HTMLElement).dataset['compId'];
        const pIdx = parseInt((e.currentTarget as HTMLElement).dataset['photoIdx'] || '0', 10);
        if (compId) void this.restorePhotoOriginal(compId, pIdx);
      });
    });
    this.mountPhotoEditor();

    // Photo Modal controls
    this.querySelector('#btn-close-photo-modal')?.addEventListener('click', () => {
      this.showPhotoModal = false;
      this.activePhotoCompId = null;
      this.activePhotoIndex = null;
      this.render();
    });

    this.querySelector('#btn-cancel-photo-modal')?.addEventListener('click', () => {
      this.showPhotoModal = false;
      this.activePhotoCompId = null;
      this.activePhotoIndex = null;
      this.render();
    });

    this.querySelector('#backdrop-photo-modal')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).id === 'backdrop-photo-modal') {
        this.showPhotoModal = false;
        this.activePhotoCompId = null;
        this.activePhotoIndex = null;
        this.render();
      }
    });

    const triggerPhotoFileInput = () => {
      const inp = this.querySelector('#inp-modal-photo-file') as HTMLInputElement;
      if (inp) inp.click();
    };

    this.querySelector('#photo-modal-dropzone')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).id !== 'btn-modal-change-photo') {
        triggerPhotoFileInput();
      }
    });

    this.querySelector('#btn-modal-change-photo')?.addEventListener('click', (e) => {
      e.stopPropagation();
      triggerPhotoFileInput();
    });

    this.querySelector('#inp-modal-photo-file')?.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0] as File;
        const reader = new FileReader();
        reader.onload = (loadEvt) => {
          if (loadEvt.target?.result) {
            this.modalPhotoUrl = loadEvt.target.result as string;
            this.render();
          }
        };
        reader.readAsDataURL(file);
      }
    });

    this.querySelector('#inp-modal-photo-comment')?.addEventListener('input', (e) => {
      this.modalPhotoComment = (e.target as HTMLTextAreaElement).value;
    });

    this.querySelector('#btn-save-photo-modal')?.addEventListener('click', () => {
      this.savePhotoFromModal();
    });

    // Delete photo
    this.querySelectorAll('.btn-del-photo').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const compId = (e.currentTarget as HTMLElement).dataset['compId'];
        const pIdx = parseInt((e.currentTarget as HTMLElement).dataset['photoIdx'] || '0', 10);
        const item = this.items.find(i => i.id === compId);
        if (item) {
          item.photos.splice(pIdx, 1);
          this.persistLocally();
          this.render();
        }
      });
    });

    // NDT Modal Open and Save Handlers
    this.querySelectorAll('.btn-save-ndt-test').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const methodId = (e.currentTarget as HTMLElement).dataset['id'];
        if (methodId) {
          this.openNdtModal(methodId);
        }
      });
    });

    this.querySelectorAll('.btn-open-ndt-modal-direct').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const methodId = (e.currentTarget as HTMLElement).dataset['id'];
        if (methodId) {
          this.openNdtModal(methodId);
        }
      });
    });

    this.querySelector('#btn-close-ndt-modal')?.addEventListener('click', () => {
      this.showNdtModal = false;
      this.activeNdtMethodId = null;
      this.render();
    });

    this.querySelector('#btn-cancel-ndt-modal')?.addEventListener('click', () => {
      this.showNdtModal = false;
      this.activeNdtMethodId = null;
      this.render();
    });

    this.querySelector('#backdrop-ndt-modal')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).id === 'backdrop-ndt-modal') {
        this.showNdtModal = false;
        this.activeNdtMethodId = null;
        this.render();
      }
    });

    this.querySelectorAll('input[name="modal-ndt-result-rad"]').forEach(rad => {
      rad.addEventListener('change', (e) => {
        this.modalNdtResult = (e.target as HTMLInputElement).value as any;
      });
    });

    this.querySelector('#inp-modal-ndt-comment')?.addEventListener('input', (e) => {
      this.modalNdtComment = (e.target as HTMLTextAreaElement).value;
    });

    this.querySelector('#inp-modal-ndt-other-name')?.addEventListener('input', (e) => {
      this.modalNdtOtherName = (e.target as HTMLInputElement).value;
    });

    this.querySelector('#btn-save-ndt-modal')?.addEventListener('click', () => {
      this.saveNdtFromModal();
    });

    this.querySelector('#btn-remove-ndt-modal')?.addEventListener('click', () => {
      if (!this.activeNdtMethodId) return;
      this.performedTests = this.performedTests.filter(t => t.methodId !== this.activeNdtMethodId);
      this.showNdtModal = false;
      const removedId = this.activeNdtMethodId;
      this.activeNdtMethodId = null;
      this.persistLocally();
      this.render();

      // Sync remote update to database asynchronously so it is persisted immediately
      if (this.reportId) {
        try {
          const p = window.app.platform;
          const ctx = {
            activeClientId: this.selectedClientId || contextStore.get().activeClientId,
            userId: contextStore.get().userId || 'USER-1',
            correlationId: `cid-${Date.now()}`,
            permissions: contextStore.get().permissions
          } as any;
          p.reportService.getReportById(ctx, this.reportId as any).then((report: any) => {
            if (report) {
              report.performedInspectionMethods = this.performedTests;
              p.reportService.updateReport(ctx, report).then(() => {
                console.log(`NDT test ${removedId} successfully removed from database report.`);
              });
            }
          });
        } catch (err) {
          console.warn('NDT test removed locally (remote sync failed):', err);
        }
      }
    });

    // Save single technical comment button
    this.querySelectorAll('.btn-save-comment').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const target = e.currentTarget as HTMLElement;
        this.persistLocally();
        const origHtml = target.innerHTML;
        target.innerHTML = `<i data-lucide="check" style="width: 12px; height: 12px;"></i> Salvo!`;
        setTimeout(() => {
          target.innerHTML = origHtml;
          if ((window as any).lucide) (window as any).lucide.createIcons();
        }, 1500);
      });
    });

    // Save single recommendation item button
    this.querySelectorAll('.btn-save-rec-item').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        
        if (this.currentMinuta && this.currentMinuta.status !== 'ATUALIZAÇÃO PENDENTE') {
          if (!confirm("Uma minuta já existe para esta inspeção. Modificar as recomendações exigirá a criação de uma Nova Revisão da Minuta. Deseja prosseguir?")) {
            return;
          }
        }

        const target = e.currentTarget as HTMLElement;
        this.persistLocally();
        const origHtml = target.innerHTML;
        target.innerHTML = `<i data-lucide="check" style="width: 11px; height: 11px;"></i> Salvo!`;
        setTimeout(() => {
          target.innerHTML = origHtml;
          if ((window as any).lucide) (window as any).lucide.createIcons();
        }, 1500);
      });
    });

    this.querySelectorAll('.btn-submit-correction').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const corrId = (e.currentTarget as HTMLElement).getAttribute('data-corrid');
        const recId = (e.currentTarget as HTMLElement).getAttribute('data-recid');
        if (corrId && recId && this.currentMinuta) {
          try {
            const p = (window as any).app?.platform;
            const ctx = sessionRequestContext();
            
            let currentRec = null;
            for (const i of this.items || []) {
              const r = i.recommendations?.find((rr: any) => rr.id === recId);
              if (r) currentRec = r;
            }
            if (!currentRec) return;

            await p.engineeringService.submitInspectorCorrection(ctx, this.currentMinuta.id, corrId, currentRec);
            alert("Correção enviada para validação da Engenharia com sucesso!");
            await this.loadMinutaContext();
            this.render();
          } catch (err) {
            alert("Erro ao enviar correção: " + String(err));
          }
        }
      });
    });

    // 9. Add Recommendation Button - Opens Modal
    this.querySelectorAll('.btn-add-rec').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const compId = (e.currentTarget as HTMLElement).dataset['compId'];
        const item = this.items.find(i => i.id === compId);
        if (item) {
          this.openCreateRecModal(item);
        }
      });
    });

    // Remove Recommendation Button
    this.querySelectorAll('.btn-remove-rec').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const compId = (e.currentTarget as HTMLElement).dataset['compId'];
        const recId = (e.currentTarget as HTMLElement).dataset['recId'];
        const item = this.items.find(i => i.id === compId);
        if (item && item.recommendations) {
          item.recommendations = item.recommendations.filter(r => r.id !== recId);
          this.persistLocally();
          this.render();
        }
      });
    });

    // Recommendation Criticality Change
    this.querySelectorAll('.sel-rec-criticality').forEach(sel => {
      sel.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        const compId = target.dataset['compId'];
        const recId = target.dataset['recId'];
        const item = this.items.find(i => i.id === compId);
        const rec = item?.recommendations?.find(r => r.id === recId);
        if (rec) {
          rec.criticality = target.value as any;
          this.persistLocally();
          this.render();
        }
      });
    });

    // Recommendation Discipline Change
    this.querySelectorAll('.sel-rec-discipline').forEach(sel => {
      sel.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        const compId = target.dataset['compId'];
        const recId = target.dataset['recId'];
        const item = this.items.find(i => i.id === compId);
        const rec = item?.recommendations?.find(r => r.id === recId);
        if (rec) {
          rec.discipline = target.value as any;
          this.persistLocally();
        }
      });
    });

    // Recommendation Execution Change
    this.querySelectorAll('.sel-rec-execution').forEach(sel => {
      sel.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        const compId = target.dataset['compId'];
        const recId = target.dataset['recId'];
        const item = this.items.find(i => i.id === compId);
        const rec = item?.recommendations?.find(r => r.id === recId);
        if (rec) {
          rec.execution = target.value as any;
          this.persistLocally();
        }
      });
    });

    // Recommendation Description Textarea Change
    this.querySelectorAll('.txt-rec-desc').forEach(ta => {
      ta.addEventListener('input', (e) => {
        const target = e.target as HTMLTextAreaElement;
        const compId = target.dataset['compId'];
        const recId = target.dataset['recId'];
        const item = this.items.find(i => i.id === compId);
        const rec = item?.recommendations?.find(r => r.id === recId);
        if (rec) {
          rec.description = target.value;
          const parent = target.parentElement;
          const counter = parent?.querySelector('.char-counter');
          if (counter) counter.textContent = `${rec.description.length} / 500`;
          this.persistLocally();
        }
      });
    });

    // 10. AI Smart Recommendation Generation
    this.querySelectorAll('.btn-gen-rec').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const compId = (e.currentTarget as HTMLElement).dataset['compId'];
        const item = this.items.find(i => i.id === compId);
        if (item) {
          if (!item.recommendations) item.recommendations = [];

          let defaultDisc: ChecklistRecommendation['discipline'] = 'CALDEIRARIA';
          if (item.name.includes('Pintura') || item.name.includes('Revestimento')) defaultDisc = 'PINTURA';
          else if (item.name.includes('Instrumentação') || item.name.includes('Indicadores')) defaultDisc = 'INSTRUMENTAÇÃO';
          else if (item.name.includes('Suporte') || item.name.includes('Escada') || item.name.includes('Plataforma')) defaultDisc = 'CIVIL';
          else if (item.name.includes('Válvula') || item.name.includes('Dreno')) defaultDisc = 'MECÂNICA';
          else if (item.name.includes('Isolamento')) defaultDisc = 'ISOLAMENTO TÉRMICO';

          const isHigh = item.status === 'REJECTED' || (item.condition && (item.condition.includes('Corrosão') || item.condition.includes('Trinca') || item.condition.includes('Vazamento')));
          const defaultCrit: ChecklistRecommendation['criticality'] = isHigh ? 'ALTA' : 'MÉDIA';
          const defaultExec: ChecklistRecommendation['execution'] = defaultCrit === 'ALTA' ? 'PARADA' : 'ROTINA';

          const smartDesc = item.condition && item.condition !== 'Sem anomalias (Aprovado)' && item.condition !== 'Sem anomalias / Conforme'
            ? `Executar intervenção técnica especializada de ${defaultDisc.toLowerCase()} para saneamento de '${item.condition}' no componente ${item.name}, com verificação dimensional e ensaios complementares.`
            : `Realizar manutenção preventiva e inspeção visual periódica de integridade para o componente ${item.name}.`;

          if (!item.recommendations) item.recommendations = [];
          if (item.recommendations && item.recommendations.length > 0) {
            if (item.recommendations[0]) {
              item.recommendations[0].description = smartDesc;
              item.recommendations[0].discipline = defaultDisc;
              item.recommendations[0].criticality = defaultCrit;
              item.recommendations[0].execution = defaultExec;
            }
          } else {
            item.recommendations.push({
              id: crypto.randomUUID(),
              criticality: defaultCrit,
              discipline: defaultDisc,
              execution: defaultExec,
              description: smartDesc
            });
          }

          if (item.status === 'PENDING' || item.status === 'APPROVED') {
            item.status = 'REJECTED';
          }
          this.persistLocally();
          this.render();
        }
      });
    });

    // 11. Bulk conforming action
    this.querySelector('#btn-trigger-bulk')?.addEventListener('click', () => {
      const pendingCount = this.items.filter(i => i.status === 'PENDING').length;
      if (pendingCount === 0) {
        alert("Todos os componentes já foram inspecionados!");
        return;
      }
      this.showBulkConfirmModal = true;
      this.render();
    });

    this.querySelectorAll('.btn-delete-component').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const compId = (e.currentTarget as HTMLElement).dataset['compId'] || '';
        this.deleteConfirmingCompId = compId;
        this.render();
      });
    });

    this.querySelectorAll('.btn-confirm-delete-comp').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const compId = (e.currentTarget as HTMLElement).dataset['compId'];
        if (!compId) return;
        
        const compToDelete = this.items.find(i => i.id === compId);
        if (compToDelete && (compToDelete.recommendations?.length > 0 || compToDelete.photos?.length > 0)) {
           alert("Não é possível excluir este componente pois ele possui recomendações ou fotos vinculadas. Remova-as primeiro.");
           this.deleteConfirmingCompId = null;
           this.render();
           return;
        }
        this.items = this.items.filter(i => i.id !== compId);

        this.items.forEach((it, idx) => {
          it.index = idx + 1;
        });
        this.deleteConfirmingCompId = null;
        this.persistLocally();
        this.render();
      });
    });

    
    this.querySelectorAll('.btn-close-manual-modal').forEach(btn => {
      btn.addEventListener('click', () => {
        this.showManualCompModal = false;
        this.render();
      });
    });

    this.querySelector('#btn-save-manual-comp')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      if (btn.disabled) return;

      const nameInp = this.querySelector('#inp-manual-comp-name') as HTMLInputElement;
      const tagInp = this.querySelector('#inp-manual-comp-tag') as HTMLInputElement;
      const groupInp = this.querySelector('#inp-manual-comp-group') as HTMLInputElement;
      const descInp = this.querySelector('#inp-manual-comp-desc') as HTMLInputElement;

      if (nameInp && nameInp.value.trim()) {
        const compName = nameInp.value.trim();
        const compTag = tagInp?.value.trim().toLowerCase() || "";
        
        // Anti-duplicity
        if (this.items.some(i => i.name.toLowerCase() === compName.toLowerCase() || (compTag && i.name.toLowerCase() === compTag))) {
          alert('Já existe um componente com esta identificação neste Checklist.');
          return;
        }

        btn.disabled = true;
        btn.innerHTML = `<i data-lucide="loader" style="width: 16px; height: 16px;"></i> ADICIONANDO...`;

        const newComp = {
          id: `comp-${Date.now()}-${Math.random().toString(36).substring(2,7)}`,
          index: this.items.length + 1,
          name: compName,
          status: "PENDING",
          condition: descInp?.value.trim() || "",
          technicalComment: "",
          photos: [],
          recommendations: [],
          isExpanded: false,
          isManual: true,
          category: groupInp?.value.trim() || 'Outros'
        };

        this.items.push(newComp as any);
        this.showManualCompModal = false;
        this.persistLocally();
        await this.saveChecklistToBackend(false);
        this.render();
      } else {
        alert('Nome do Componente é obrigatório.');
      }
    });

    this.querySelectorAll('.btn-cancel-delete-comp').forEach(btn => {
      btn.addEventListener('click', () => {
        this.deleteConfirmingCompId = null;
        this.render();
      });
    });

    this.querySelector('#btn-add-manual-component')?.addEventListener('click', () => {
      this.showManualCompModal = true;
      this.render();
    });

    this.querySelector('#btn-cancel-bulk')?.addEventListener('click', () => {
      this.showBulkConfirmModal = false;
      this.render();
    });

    this.querySelector('#btn-confirm-bulk')?.addEventListener('click', () => {
      this.items.forEach(i => {
        if (i.status === 'PENDING') {
          i.status = 'APPROVED';
          if (!i.condition) i.condition = "Sem anomalias (Aprovado)";
        }
      });
      this.showBulkConfirmModal = false;
      this.persistLocally();
      this.render();
    });

    // 12. Bottom Actions
    this.querySelector('#btn-footer-back')?.addEventListener('click', () => {
      if (this.reportId) {
        router.navigate(`/reports/integrity/edit/${this.reportId}`);
      } else {
        router.navigate('/inspections');
      }
    });

    const handleSaveAction = async (e?: Event) => {
      let originalHtml = '';
      let btn: HTMLButtonElement | null = null;
      if (e && e.currentTarget) {
        btn = e.currentTarget as HTMLButtonElement;
        originalHtml = btn.innerHTML;
        if (btn.disabled) return;
        btn.disabled = true;
        btn.innerHTML = `<i data-lucide="loader" style="width: 18px; height: 18px;"></i> SALVANDO...`;
      }
      
      try {
        const saved = await this.saveChecklistToBackend(true);
        if (saved) {
          this.hasUnsavedChanges = false;
          alert("Checklist salvo com sucesso!");
        } else {
          alert("Não foi possível salvar o Checklist. Verifique se o Cliente e o Equipamento foram selecionados.");
        }
      } catch (err: any) {
        console.error("Erro ao salvar checklist:", err);
        alert("Não foi possível salvar o Checklist: " + (err?.message || err));
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.innerHTML = originalHtml;
        }
        this.render();
      }
    };

    this.querySelector('#btn-footer-save')?.addEventListener('click', handleSaveAction);
    this.querySelector('#btn-header-save')?.addEventListener('click', handleSaveAction);
    this.querySelector('#btn-save-checklist')?.addEventListener('click', handleSaveAction);

    

    this.querySelector('#btn-header-edit-checklist')?.addEventListener('click', () => {
      if (!this.canUnlock) {
         alert("Este relatório foi finalizado e está bloqueado. Somente o Engenheiro/PLH ou Administrador pode editá-lo.");
         return;
      }
      this.isLocked = false;
      if (this.inspectionId) {
        localStorage.setItem(`ri_locked_${this.inspectionId}`, 'false');
      }
      this.render();
    });

    this.querySelector('#btn-footer-edit-checklist')?.addEventListener('click', () => {
      if (!this.canUnlock) {
         alert("Este relatório foi finalizado e está bloqueado. Somente o Engenheiro/PLH ou Administrador pode editá-lo.");
         return;
      }
      this.isLocked = false;
      if (this.inspectionId) {
        localStorage.setItem(`ri_locked_${this.inspectionId}`, 'false');
      }
      this.render();
    });

    // Action Bar: Voltar
    this.querySelector('#btn-header-back')?.addEventListener('click', async () => {
      if (this.hasUnsavedChanges) {
        const wantsToSave = confirm("Existem alterações no Checklist que ainda não foram salvas.\nDeseja salvar antes de sair?\n\n(OK = Salvar e Voltar, Cancelar = Permanecer)");
        if (wantsToSave) {
          const saved = await this.saveChecklistToBackend(true);
          if (saved) history.back();
        }
      } else {
        history.back();
      }
    });

    // Action Bar: Liberar para o PLH
    const btnReleasePlh = this.querySelector('#btn-header-release-plh') as HTMLButtonElement;
    btnReleasePlh?.addEventListener('click', async () => {
      const missing = this.validateInspectionRequiredFields();
      if (missing.length > 0) {
        this.showValidationErrors(missing);
        this.render();
        return;
      }

      // Guard Clause: Validate if checklist is started and valid
      if (!this.inspectionId || !this.equipmentId || this.items.length === 0) {
        alert("NENHUM CHECK LIST FOI INICIADO E CONCLUIDO");
        return;
      }

      try {
        if (btnReleasePlh) btnReleasePlh.disabled = true;

        // 1. Forçar o salvamento
        const saved = await this.saveChecklistToBackend(true);
        if (!saved) {
          if (btnReleasePlh) btnReleasePlh.disabled = false;
          return;
        }

        const p = window.app.platform;
        const ctx = { ...sessionRequestContext(), activeClientId: this.selectedClientId } as any;

        // 1.5. Validar via read-back do backend que o checklist possui componentes persistidos
        const persistedState = await p.inspectionService.getChecklistState(ctx, this.inspectionId as InspectionId);
        if (!persistedState || !persistedState.components || persistedState.components.length === 0) {
          alert(`NÃO FOI POSSÍVEL LIBERAR PARA O PLH\n\nFalha ao persistir Checklist no servidor.\n\nReport ID: ${this.reportId || '-'}\nInspection ID: ${this.inspectionId}\nComponentes enviados: ${this.items.length}\nComponentes persistidos: 0`);
          if (btnReleasePlh) btnReleasePlh.disabled = false;
          return;
        }

        // 2. Verificar pendências e kpis locais (só pra avisar)
        this.verifyConsistency(true);
        if (this.consistencyErrors.some(e => e.includes('[IMPEDITIVA]'))) {
          alert("NÃO FOI POSSÍVEL LIBERAR PARA O PLH\n\nExistem pendências impeditivas. Por favor, corrija os erros de consistência antes de liberar o relatório.");
          this.showConsistencyModal = true;
          this.render();
          if (btnReleasePlh) btnReleasePlh.disabled = false;
          return;
        }

        // 3. Confirmar liberação
        const confirmar = confirm("LIBERAR RELATÓRIO PARA O PLH?\n\nO Checklist será salvo e bloqueado para edição pelo Inspetor.\nO relatório ficará disponível para análise do Engenheiro/PLH responsável pelo Projeto.");
        if (!confirmar) {
          if (btnReleasePlh) btnReleasePlh.disabled = false;
          return;
        }

        // 4. Executar liberação via backend (garante existência do relatório no envio)
        const reportId = await this.ensureReportId(ctx);
        const result = await p.reportService.releaseReportToPLH(ctx, reportId as any);
        
        if (result.success) {
          // Recarregar os dados na UI para pegar o novo status travado
          this.isLocked = true;
          this.reportStatus = 'SUBMITTED_TO_ENGINEERING';
          localStorage.setItem(`ri_locked_${this.inspectionId}`, 'true');
          alert("Relatório liberado para o PLH com sucesso!");
          this.render();
        } else {
          alert("NÃO FOI POSSÍVEL LIBERAR PARA O PLH\n\nPendências impeditivas:\n" + result.blockingIssues.join("\n"));
        }
      } catch (err: any) {
        console.error("Erro técnico na liberação para PLH:", err);
        alert("Não foi possível liberar o relatório para o PLH.");
      } finally {
        if (btnReleasePlh && !this.isLocked) {
          btnReleasePlh.disabled = false;
        }
      }
    });

    // 13. Verify Consistency Action
    const triggerVerifyConsistency = () => {
      this.verifyConsistency();
    };
    this.querySelector('#btn-footer-verify')?.addEventListener('click', triggerVerifyConsistency);
    this.querySelector('#btn-footer-verify-bottom')?.addEventListener('click', triggerVerifyConsistency);
    this.querySelector('#btn-close-consistency')?.addEventListener('click', () => {
      this.showConsistencyModal = false;
      this.render();
    });

    // 14. AI Text Revision Action
    const triggerAiRevision = async () => {
      await this.reviseTextsWithAI();
    };
    this.querySelector('#btn-footer-ai')?.addEventListener('click', triggerAiRevision);
    this.querySelector('#btn-footer-ai-bottom')?.addEventListener('click', triggerAiRevision);

    // 15. Generate and Preview RI Action
    const handleGenerateReportAction = async (e?: Event) => {
      let originalHtml = '';
      let btn: HTMLButtonElement | null = null;
      if (e && e.currentTarget) {
        btn = e.currentTarget as HTMLButtonElement;
        originalHtml = btn.innerHTML;
        if (btn.disabled) return;
        btn.disabled = true;
        btn.innerHTML = `<i data-lucide="loader" style="width: 18px; height: 18px;"></i> GERANDO RELATÓRIO...`;
      }

      try {
        // Auto-generate reportNumber if missing or generic
        if (!this.reportNumber || this.reportNumber === '-' || this.reportNumber.includes('RI-NOVO') || this.reportNumber.includes('Elaboração')) {
          if (!this.selectedClientId) {
            this.selectedClientId = contextStore.get().activeClientId || (this.clients[0]?.id || '');
          }
          const activeClient = this.clients.find(c => c.id === this.selectedClientId);
          const cName = activeClient?.name || activeClient?.legalName || this.clientName || 'CLI';
          const foundWo = this.workOrders.find(w => w.id === this.selectedWorkOrderId);
          const os = (this.selectedOsCode || foundWo?.code || '001').replace(/\D/g, '') || '001';
          const foundSub = this.subWorkOrders.find(s => s.id === this.selectedSubWorkOrderId);
          const subOs = (this.selectedSubOsCode || foundSub?.code || '01').replace(/\D/g, '') || '01';
          const ano = new Date().getFullYear();
          const clientSigla = (cName.split(' ')[0] || 'CLI').substring(0, 3).toUpperCase().replace(/[^A-Z0-9]/g, '');
          const numFormatado = (Math.floor(Math.random() * 90) + 10).toString();
          this.reportNumber = `RI_${clientSigla}_${os}_${subOs}_${ano}_${numFormatado}`;
        }

        // Auto-save checklist to backend
        await this.saveChecklistToBackend(true); 

        // Open preview modal
        this.showPdfPreviewModal = true;
        this.render();

        setTimeout(async () => {
          const previewEl = this.querySelector('#chk-pdf-preview-el') as any;
          const p = window.app.platform;
          const ctx = { 
            activeClientId: this.selectedClientId || contextStore.get().activeClientId,
            userId: contextStore.get().userId,
            permissions: contextStore.get().permissions
          } as any;

          try {
            if (this.reportId && p.reportService?.buildDocumentSnapshot) {
              const snapshot = await p.reportService.buildDocumentSnapshot(ctx, this.reportId as any);
              if (previewEl && snapshot) {
                previewEl.snapshot = snapshot;
                return;
              }
            }
          } catch (err) {
            console.warn('Could not load snapshot from reportService, falling back to mapper', err);
          }

          try {
            let eq: any = null;
            if (this.equipmentId && p.equipmentService?.getEquipment) {
              try {
                eq = await p.equipmentService.getEquipment(ctx, this.equipmentId as any);
              } catch {}
            }
            if (previewEl) {
              // STUDIO INSPECT IA: RI em elaboração usa o logo documental publicado (mesmo que o serviço usaria).
              const riVisual = await p.studioDesignService?.resolveDocumentVisual(ctx, "RI", this.reportId || "RI-NOVO", false).catch(() => null);
              const docSnapshot = UnifiedInspectionIntegrityReportMapper.mapToDocumentSnapshot({
                tenantLogo: riVisual?.logoImage ?? null,
                report: {
                  id: (this.reportId || 'RI-NOVO') as any,
                  inspectionId: (this.inspectionId || 'INSP-TEMP') as any,
                  equipmentSnapshotId: null as any,
                  reportNumber: this.reportNumber || 'RI-NOVO',
                  clientReportNumber: this.clientReportNumber || null,
                  status: 'IN_ELABORATION',
                  objective: 'Avaliar as condições físicas e operacionais para garantir a conformidade com as normas vigentes e a segurança do processo.',
                  inspectionObservations: this.generalObservations || '',
                  maintenanceServices: this.maintenanceServices || '',
                  damageMechanisms: this.damageMechanisms || '',
                  nextInspectionDates: '',
                  condition: 'EM AVALIAÇÃO',
                  conclusion: '',
                  restrictions: '',
                  revision: '00',
                  createdAt: new Date(),
                  updatedAt: new Date()
                } as any,
                inspection: {
                  id: (this.inspectionId || 'INSP-TEMP') as any,
                  equipmentId: (this.equipmentId || '') as any,
                  startDate: this.startDate || todayLocalDay(),
                  endDate: this.endDate || todayLocalDay(),
                  modality: this.modalityState || 'Externa',
                  nature: this.natureState || 'Programada',
                  inspectionType: this.inspectionType || 'Integridade NR-13',
                  operationalCondition: this.conditionState || 'Em parada'
                } as any,
                equipmentSnapshot: eq ? { equipmentData: eq } : {
                  equipmentData: {
                    tag: this.equipmentTag || 'EQ-01',
                    name: this.equipmentName || 'Equipamento',
                    service: '',
                    equipmentType: 'PRESSURE_VESSEL',
                    properties: {}
                  }
                },
                checklistComponents: this.items.map(i => ({
                  id: i.id as any,
                  groupId: i.name,
                  definitionComponentId: i.name,
                  result: i.status === 'APPROVED' ? 'APPROVED' : (i.status === 'REJECTED' ? 'REJECTED' : (i.status === 'NOT_APPLICABLE' ? 'NOT_APPLICABLE' : 'PENDING')),
                  description: i.condition || (i.status === 'APPROVED' ? 'Sem anomalias (Aprovado)' : 'Anomalia registrada'),
                  inspectionNotes: i.technicalComment || '',
                  recommendations: []
                })),
                performedInspectionMethods: this.extractNdts().map(n => ({
                  id: (n as any).id || n.name,
                  method: n.name,
                  methodName: n.name,
                  result: n.result || 'APPROVED',
                  notes: n.comment || ''
                })),
                recommendations: this.items.flatMap(i => (i.recommendations || []).map((r, idx) => ({
                  id: (r.id || `REC-${i.index}-${idx+1}`) as any,
                  integrityReportId: (this.reportId || 'RI-NOVO') as any,
                  componentId: i.id as any,
                  checklistItemId: i.id as any,
                  criticality: r.criticality === 'ALTA' ? 'HIGH' : (r.criticality === 'MÉDIA' ? 'MODERATE' : 'LOW'),
                  discipline: r.discipline || 'MECNICA',
                  executeIn: r.execution === 'PARADA' ? 'SHUTDOWN' : 'ROUTINE',
                  scope: `${i.name}: ${r.description}`,
                  status: 'OPEN'
                }))),
                photos: this.items.flatMap(i => (i.photos || []).map((p, pIdx) => {
                  const photoUrl = typeof p === 'string' ? p : (p.url || p.fileUrl || p.image || '');
                  const photoComment = typeof p === 'string' ? '' : (p.comment || '');
                  return {
                    id: (typeof p === 'object' && p.id) ? p.id : `photo-${i.id}-${pIdx}`,
                    checklistComponentId: i.id as any,
                    fileUrl: photoUrl,
                    fileName: `${i.name}.jpg`,
                    title: i.name,
                    comment: photoComment || i.technicalComment || 'Registro fotográfico da inspeção',
                    uploadedAt: new Date()
                  };
                })),
                signatories: {
                  inspector: {
                    name: contextStore.get().userName,
                    cred: 'Inspetor de Equipamentos'
                  },
                  plh: null,
                  fiscal: {
                    name: 'FISCALIZAÇÃO',
                    cred: 'FISCAL DO CONTRATO'
                  }
                },
                client: { name: this.clientName || contextStore.get().activeClientName || 'Cliente' }
              });
              previewEl.snapshot = docSnapshot;
            }
          } catch (err) {
            console.error('Erro ao gerar snapshot do checklist', err);
          }
        }, 50);
      } catch (err: any) {
        console.error("Erro ao gerar relatório:", err);
        alert("Não foi possível gerar o relatório: " + (err?.message || err));
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.innerHTML = originalHtml;
        }
      }
    };

    this.querySelector('#btn-footer-generate-ri')?.addEventListener('click', handleGenerateReportAction);
    this.querySelector('#btn-header-generate-ri')?.addEventListener('click', handleGenerateReportAction);

    // Release PLH bottom button
    this.querySelector('#btn-footer-release-plh')?.addEventListener('click', () => {
      btnReleasePlh?.click();
    });

    // Report Viewer Modal Buttons
    this.querySelector('#btn-modal-open-report-viewer')?.addEventListener('click', () => {
      if (this.reportId) {
        this.showPdfPreviewModal = false;
        router.navigate(`/reports/integrity/view/${this.reportId}`);
      } else {
        alert("Salve o checklist primeiro para gerar o identificador do relatório.");
      }
    });

    this.querySelector('#btn-close-pdf-modal')?.addEventListener('click', this.closeReportViewer);
    this.querySelector('#chk-pdf-preview-el')?.addEventListener('close-preview', this.closeReportViewer);
    this.querySelector('#chk-pdf-preview-el')?.addEventListener('close', this.closeReportViewer);

    // Consistency Modal close buttons
    this.querySelector('#btn-close-consistency-modal')?.addEventListener('click', () => {
      this.showConsistencyModal = false;
      this.render();
    });
    this.querySelector('#btn-close-validation-modal')?.addEventListener('click', () => {
      this.showValidationModal = false;
      const mod = this.querySelector('#modal-validation-errors') as HTMLElement;
      if (mod) mod.style.display = 'none';
      this.render();
    });
    this.querySelector('#btn-ok-validation-modal')?.addEventListener('click', () => {
      this.showValidationModal = false;
      const mod = this.querySelector('#modal-validation-errors') as HTMLElement;
      if (mod) mod.style.display = 'none';
      this.render();
      
      // Attempt to focus first empty field
      setTimeout(() => {
        const firstEmpty = this.querySelector('.chk-input:invalid, .chk-select:invalid') as HTMLElement;
        if (firstEmpty) firstEmpty.focus();
      }, 50);
    });
    this.querySelector('#btn-ack-validation')?.addEventListener('click', () => {
      this.showValidationModal = false;
      const mod = this.querySelector('#modal-validation-errors') as HTMLElement;
      if (mod) mod.style.display = 'none';
      this.render();
    });
    this.querySelector('#btn-ok-consistency')?.addEventListener('click', () => {
      this.showConsistencyModal = false;
      this.render();
    });

    // Recommendation Modal Event Listeners
    this.querySelector('#inp-rec-discipline')?.addEventListener('change', (e) => {
      this.recDiscipline = (e.target as HTMLSelectElement).value as any;
    });
    this.querySelector('#inp-rec-criticality')?.addEventListener('change', (e) => {
      this.recCriticality = (e.target as HTMLSelectElement).value as any;
    });
    this.querySelector('#inp-rec-execution')?.addEventListener('change', (e) => {
      this.recExecution = (e.target as HTMLSelectElement).value as any;
    });
    // ZR input is now readonly, event listener removed
    this.querySelector('#inp-rec-scope')?.addEventListener('input', (e) => {
      this.recScope = (e.target as HTMLTextAreaElement).value;
    });
    this.querySelector('#btn-cancel-rec-modal')?.addEventListener('click', () => {
      this.showCreateRecModal = false;
      this.recTargetItem = null;
      this.render();
    });
    this.querySelector('#btn-close-rec-modal')?.addEventListener('click', () => {
      this.showCreateRecModal = false;
      this.recTargetItem = null;
      this.render();
    });
    this.querySelector('#btn-save-rec-modal')?.addEventListener('click', () => {
      this.saveRecommendationFromModal();
    });

    // ZR card input is now readonly, event listener removed

    // Direct NDT Module Navigation
    this.querySelectorAll('.btn-goto-ndt-module').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const mod = (e.currentTarget as HTMLElement).dataset['module'];
        this.persistLocally();
        if (mod === '1.1') router.navigate('/ndt/pm');
        else if (mod === '1.2') router.navigate('/ndt/lp');
        else if (mod === '1.3') router.navigate('/ndt/me');
        else if (mod === '1.4') router.navigate('/ndt/ev');
        else if (mod === '1.5') router.navigate('/ndt/th');
        else if (mod === '1.6') router.navigate('/ndt/dureza');
        else if (mod === '2.1') router.navigate('/ndt/us-convencional');
        else if (mod === '2.2') router.navigate('/ndt/us-phased-array');
        else if (mod === '2.3') router.navigate('/ndt/us-tofd');
        else if (mod === '3.1') router.navigate('/ndt/acoplamento-acustico');
        else router.navigate('/ndt');
      });
    });

    // Finalize Report Actions
    const triggerFinalizeReport = async () => {
      const missing = this.validateInspectionRequiredFields();
      if (missing.length > 0) {
        this.showValidationErrors(missing);
        this.render();
        return;
      }

      try {
        // Auto-sanitize references and comments before consistency checks so technical inspectors are never blocked
        this.referenceDocuments = this.referenceDocuments.filter(d => (d.name && d.name.trim()) || d.fileData);
        this.items.forEach(item => {
          if (item.status === 'REJECTED' && (!item.technicalComment || !item.technicalComment.trim())) {
            item.technicalComment = item.condition && item.condition !== 'Anomalia registrada' 
              ? `Constatada anomalia técnica (${item.condition}) durante inspeção visual conforme critérios NR-13.` 
              : 'Constatada não conformidade durante inspeção visual técnica conforme critérios normativos da NR-13.';
          }
          if (item.recommendations && item.recommendations.length > 0) {
            item.recommendations.forEach(r => {
              if (!r.description || !r.description.trim()) {
                r.description = `Adequação técnica corretiva e inspeção dimensional para o componente ${item.name}.`;
              }
            });
          }
        });

        this.verifyConsistency(true);
        const blockers = this.consistencyErrors.filter(err => err.includes('[IMPEDITIVA]'));
        if (blockers.length > 0) {
          this.showConsistencyModal = true;
          this.render();
          try {
            alert(`Não é possível finalizar o relatório. Existem ${blockers.length} pendências impeditivas que precisam ser corrigidas.`);
          } catch {
            console.warn(`Blockers present: ${blockers.length}`);
          }
          return;
        }

        let userConfirmed = true;
        try {
          userConfirmed = confirm("Tem certeza que deseja finalizar o Laudo de Integridade? O documento será registrado com status FINALIZADO e estará pronto para validação da engenharia.");
        } catch (err) {
          userConfirmed = true; // Safe fallback for iframe/sandbox
        }
        if (!userConfirmed) {
          return;
        }

        const saved = await this.saveChecklistToBackend(true, true);
        if (!saved) {
          throw new Error("Não foi possível salvar os dados do relatório antes de finalizar.");
        }

        const p = window.app.platform;
        const effectiveClientId = this.selectedClientId || this.clientId || contextStore.get().activeClientId;
        const ctxFind = {
          activeClientId: effectiveClientId,
          userId: contextStore.get().userId || 'USER-1',
          correlationId: `cid-${Date.now()}`,
          permissions: contextStore.get().permissions
        } as any;
        const reportId = await this.ensureReportId(ctxFind);
        const report = await p.reportService.getReportById(ctxFind, reportId as IntegrityReportId);
        if (!report) {
          throw new Error("ID do Relatório não encontrado para finalização.");
        }

        // Update using report's own clientId to ensure authorization passes
        const ctxUpdate = {
          activeClientId: report.clientId,
          userId: contextStore.get().userId || 'USER-1',
          correlationId: `cid-${Date.now()}`,
          permissions: contextStore.get().permissions
        } as any;

        if ((p.reportService as any)?.finalizeReport) {
          try {
            const finResult = await (p.reportService as any).finalizeReport(ctxUpdate, reportId as IntegrityReportId);
            if (finResult && !finResult.success) {
              throw new Error(finResult.blockingIssues.join('\n'));
            }
          } catch (finErr: any) {
            throw new Error("Erro na validação do relatório: " + (finErr.message || finErr));
          }
        }

        // Update local state if successful
        report.status = 'FINALIZED_BY_INSPECTOR';
        report.updatedAt = new Date().toISOString();
        await p.reportService.updateReport(ctxUpdate, report);

        // Invalidate cached preview and update metadata cache
        if (this.inspectionId) {
          try {
            const metaKey = `ri_meta_${this.inspectionId}`;
            const cachedMeta = localStorage.getItem(metaKey);
            if (cachedMeta) {
              const parsedMeta = JSON.parse(cachedMeta);
              parsedMeta.status = 'FINALIZED_BY_INSPECTOR';
              localStorage.setItem(metaKey, JSON.stringify(parsedMeta));
            }
          } catch {}
        }

        // Notify all page listeners so inspections-page and integrity-reports-page reflect state immediately
        window.dispatchEvent(new CustomEvent('ri_report_finalized', { detail: { reportId: this.reportId } }));
        window.dispatchEvent(new CustomEvent('ri_checklist_updated', { detail: { inspectionId: this.inspectionId } }));

        try {
          alert("Relatório de Integridade finalizado com sucesso!");
        } catch {
          console.log("Relatório de Integridade finalizado com sucesso!");
        }
        router.navigate(`/reports/integrity/view/${this.reportId}`);
      } catch (globalError: any) {
        console.error("Erro ao finalizar relatório:", globalError);
        try {
          alert("Ocorreu um erro ao finalizar o relatório: " + (globalError.message || globalError));
        } catch {
          // fallback
        }
      }
    };

    this.querySelector('#btn-header-finalize-ri')?.addEventListener('click', triggerFinalizeReport);
    this.querySelector('#btn-footer-finalize-ri')?.addEventListener('click', triggerFinalizeReport);

    // Close on ESC key
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.showPdfPreviewModal) {
          this.showPdfPreviewModal = false;
          this.render();
        } else if (this.showConsistencyModal) {
          this.showConsistencyModal = false;
          this.render();
        } else if (this.showValidationModal) {
          this.showValidationModal = false;
          this.render();
        } else if (this.showBulkConfirmModal) {
          this.showBulkConfirmModal = false;
          this.render();
        } else if (this.showCreateRecModal) {
          this.showCreateRecModal = false;
          this.recTargetItem = null;
          this.render();
        } else if (this.showNdtModal) {
          this.showNdtModal = false;
          this.activeNdtMethodId = null;
          this.render();
        } else if (this.showPhotoModal) {
          this.showPhotoModal = false;
          this.activePhotoCompId = null;
          this.activePhotoIndex = null;
          this.render();
        }
      }
    });
  }

  private showValidationErrors(errors: string[]) {
    this.validationModalMessage = errors;
    this.showValidationModal = true;
    this.render();
  }

  private openCreateRecModal(item: VisualChecklistItem) {
    this.recTargetItem = item;
    
    // Determine smart discipline
    let defaultDisc: ChecklistRecommendation['discipline'] = 'CALDEIRARIA';
    if (item.name.includes('Pintura') || item.name.includes('Revestimento')) defaultDisc = 'PINTURA';
    else if (item.name.includes('Instrumentação') || item.name.includes('Indicadores')) defaultDisc = 'INSTRUMENTAÇÃO';
    else if (item.name.includes('Suporte') || item.name.includes('Escada') || item.name.includes('Plataforma')) defaultDisc = 'CIVIL';
    else if (item.name.includes('Válvula') || item.name.includes('Dreno')) defaultDisc = 'MECÂNICA';
    else if (item.name.includes('Isolamento')) defaultDisc = 'ISOLAMENTO TÉRMICO';
    else if (item.name.includes('Tubulação') || item.name.includes('Flange') || item.name.includes('Conexão')) defaultDisc = 'TUBULAÇÃO';

    // Determine smart criticality & execution
    const isCritical = item.condition && (item.condition.includes('Trinca') || item.condition.includes('Vazamento severo'));
    const isHigh = isCritical || item.status === 'REJECTED' || (item.condition && item.condition.includes('Corrosão'));
    const defaultCrit: ChecklistRecommendation['criticality'] = isCritical ? 'CRÍTICA' : (isHigh ? 'ALTA' : 'MÉDIA');
    const defaultExec: ChecklistRecommendation['execution'] = (defaultCrit === 'ALTA' || defaultCrit === 'CRÍTICA') ? 'PARADA' : 'ROTINA';

    const initialDesc = item.condition && item.condition !== 'Sem anomalias (Aprovado)' && item.condition !== 'Sem anomalias / Conforme'
      ? `Realizar intervenção de ${defaultDisc.toLowerCase()} para saneamento de '${item.condition}' no componente ${item.name}.`
      : `Executar inspeção e manutenção preventiva no componente ${item.name}.`;

    this.recDiscipline = defaultDisc;
    this.recCriticality = defaultCrit;
    this.recExecution = defaultExec;
    this.recScope = initialDesc;
    this.recZr = '';
    this.showCreateRecModal = true;
    this.render();
  }

  private saveRecommendationFromModal() {
    if (!this.recTargetItem) return;
    if (!this.recScope || !this.recScope.trim()) {
      alert("Informe o descritivo da recomendação.");
      return;
    }

    if (this.currentMinuta && this.currentMinuta.status !== 'ATUALIZAÇÃO PENDENTE') {
      if (!confirm("Uma minuta já existe para esta inspeção. Adicionar recomendações exigirá a criação de uma Nova Revisão da Minuta. Deseja prosseguir?")) {
        return;
      }
    }

    if (!this.recTargetItem.recommendations) {
      this.recTargetItem.recommendations = [];
    }

    const recIdx = this.recTargetItem.recommendations.length + 1;
    const newRec = {
      id: crypto.randomUUID(),
      code: `REC-${this.recTargetItem.index.toString().padStart(2, '0')}-${recIdx.toString().padStart(2, '0')}`,
      criticality: this.recCriticality as any,
      discipline: this.recDiscipline as any,
      execution: this.recExecution as any,
      description: this.recScope.trim(),
      zr: this.recZr ? this.recZr.trim() : undefined
    } as any;

    this.recTargetItem.recommendations.push(newRec);

    if (this.recTargetItem.status === 'PENDING' || this.recTargetItem.status === 'APPROVED') {
      this.recTargetItem.status = 'REJECTED';
    }

    this.showCreateRecModal = false;
    this.recTargetItem = null;
    this.recScope = '';
    this.recZr = '';
    this.persistLocally();
    this.render();
  }

  private verifyConsistency(silent: boolean = false) {
    const missing = this.validateInspectionRequiredFields();
    if (missing.length > 0 && !silent) {
      this.showValidationErrors(missing);
      this.render();
      return;
    }

    this.consistencyErrors = [];
    
    // NDT Validations
    if (this.performedTests.length === 0) {
      this.consistencyErrors.push(`[INFORMATIVA] Nenhum método de ensaio ou inspeção foi selecionado.`);
    }

    this.performedTests.forEach(pt => {
      const cat = InspectionTestMethodCatalog.find(c => c.id === pt.methodId);
      const name = cat ? cat.name : 'OUTROS';
      
      if (!pt.result) {
        this.consistencyErrors.push(`[PENDÚNCIA] Ensaio '${name}' selecionado sem resultado definido.`);
      }
      if (pt.result === 'REJECTED' && (!pt.comment || !pt.comment.trim())) {
        this.consistencyErrors.push(`[IMPEDITIVA] Ensaio '${name}' reprovado sem comentário/justificativa.`);
      }
      if (pt.result === 'PENDING' && (!pt.comment || !pt.comment.trim())) {
        this.consistencyErrors.push(`[IMPEDITIVA] Ensaio '${name}' com status pendente requer justificativa no comentário.`);
      }
      if (pt.methodId === 'OTHER' && (!pt.otherName || !pt.otherName.trim())) {
        this.consistencyErrors.push(`[IMPEDITIVA] A opção 'OUTROS' foi selecionada, mas o nome do ensaio não foi informado.`);
      }
    });

    const linkedTests = this.performedTests.filter(t => ['THICKNESS', 'LIQUID_PENETRANT', 'MAGNETIC_PARTICLES', 'BSCAN'].includes(t.methodId));
    if (linkedTests.length > 0) {
      const names = linkedTests.map(t => InspectionTestMethodCatalog.find(c => c.id === t.methodId)?.name).join(', ');
      this.consistencyErrors.push(`[ATENÇÃO] Os seguintes ensaios exigem preenchimento de seus respectivos laudos no módulo de END: ${names}`);
    }

    // Reference Documents Validations
    const docsWithoutName = this.referenceDocuments.filter(d => !d.name || !d.name.trim());
    if (docsWithoutName.length > 0) {
      this.consistencyErrors.push(`[IMPEDITIVA] Existem ${docsWithoutName.length} documento(s) de referência sem 'NOME' preenchido.`);
    }

    const docsWithoutFile = this.referenceDocuments.filter(d => !d.fileData);
    if (docsWithoutFile.length > 0) {
      this.consistencyErrors.push(`[ATENÇÃO] Existem ${docsWithoutFile.length} documento(s) de referência aguardando anexo de arquivo.`);
    }

    const kpis = this.getKpis();
    const mathConsistent = kpis.total === (kpis.approved + kpis.rejected + kpis.notApplicable + kpis.pending);
    
    if (!mathConsistent) {
      this.consistencyErrors.push(`Inconsistência matemática na contagem de componentes. O total (${kpis.total}) não equivale à soma dos status.`);
    }

    const pendingItems = this.items.filter(i => i.status === 'PENDING');
    if (pendingItems.length > 0) {
      this.consistencyErrors.push(`${pendingItems.length} componente(s) ainda constam como 'Pendente' (Sem inspeção).`);
    }

    const nonConformingWithoutDesc = this.items.filter(i => i.status === 'REJECTED' && (!i.technicalComment || !i.technicalComment.trim()));
    if (nonConformingWithoutDesc.length > 0) {
      this.consistencyErrors.push(`${nonConformingWithoutDesc.length} componente(s) com anomalia não possuem comentário técnico preenchido.`);
    }

    for (const item of this.items) {
      if (item.recommendations && item.recommendations.length > 0) {
        const emptyRecs = item.recommendations.filter(r => !r.description || !r.description.trim());
        if (emptyRecs.length > 0) {
          this.consistencyErrors.push(`O componente '${item.name}' possui ${emptyRecs.length} recomendação(ões) sem descritivo preenchido.`);
        }
      }
    }

    if (!silent) {
      this.showConsistencyModal = true;
      this.render();
    }
  }

  private async reviseTextsWithAI() {
    const btn = this.querySelector('#btn-footer-ai') as HTMLButtonElement;
    if (btn) btn.textContent = "Revisando com IA...";

    try {
      let count = 0;
      this.items.forEach(item => {
        if (item.technicalComment && !item.technicalComment.includes('avaliado conforme')) {
          item.technicalComment = `Constatado em vistoria técnica: ${item.technicalComment.trim()}. Condição avaliada conforme critérios de aceitação da norma NR-13.`;
          count++;
        }
        if (item.recommendations) {
          item.recommendations.forEach(rec => {
            if (rec.description && !rec.description.includes('Procedimento normativo')) {
              rec.description = `${rec.description.trim()} Procedimento normativo em conformidade com as diretrizes de integridade mecânica.`;
              count++;
            }
          });
        }
      });

      if (this.generalObservations && !this.generalObservations.includes('procedimentos normativos')) {
        this.generalObservations = `${this.generalObservations.trim()} Todas as etapas de inspeção visual seguiram os procedimentos normativos NR-13 e ASME Sec. VIII.`;
      }

      alert(`Revisão por IA concluída! ${count} texto(s) técnico(s) foram enriquecidos.`);
    } catch (e: any) {
      alert("Erro na revisão de textos: " + e.message);
    }

    this.render();
  }

  private async saveChecklistToBackend(silent: boolean = false, finalize: boolean = false): Promise<boolean> {
    const saveBtn = this.querySelector('#btn-footer-save, #btn-header-save') as HTMLButtonElement | null;
    if (saveBtn) saveBtn.textContent = "Salvando...";

    if (finalize) {
      const kpis = this.getKpis();
      const sum = kpis.approved + kpis.rejected + kpis.notApplicable + (kpis.clientDiscretion || 0) + kpis.pending;
      if (kpis.total !== sum) {
        alert("ERRO DE CONSISTÚNCIA: A soma dos status não equivale ao total de componentes. Salvar desabilitado.");
        if (saveBtn) saveBtn.textContent = "Salvar Checklist";
        return false;
      }
    }

    try {
      const p = window.app.platform;
      const canonicalClientId = this.selectedClientId || this.clientId || contextStore.get().activeClientId || (this.clients[0]?.id || 'CLI-001');
      this.selectedClientId = canonicalClientId;
      this.clientId = canonicalClientId;
      contextStore.set({ ...contextStore.get(), activeClientId: canonicalClientId });

      const ctx = {
        activeClientId: canonicalClientId,
        userId: contextStore.get().userId || 'usr_admin',
        correlationId: `cid-${Date.now()}`,
        permissions: contextStore.get().permissions
      } as any;

      const routePath = typeof window !== 'undefined' ? window.location.pathname : '';
      const routeId = this.reportId || this.inspectionId;
      const routeIdType = this.reportId ? 'REPORT_ID' : 'INSPECTION_ID';
      const activeClientIdBefore = contextStore.get().activeClientId;

      console.log('[CHECKLIST-CONTEXT]', {
        routePath,
        routeId,
        routeIdType,
        reportId: this.reportId,
        reportInspectionId: this.inspectionId,
        inspectionIdUsedForLoad: this.inspectionId,
        inspectionIdUsedForSave: this.inspectionId,
        reportClientId: this.selectedClientId,
        activeClientIdBefore,
        activeClientIdAfter: this.selectedClientId,
        componentCountUi: this.items.length
      });

      const selectedProf = this.professionals.find(p => p.id === this.selectedInspectorId);
      const inspectorIdToSave = this.selectedInspectorId || selectedProf?.id || contextStore.get().professionalId || contextStore.get().userId || (this.professionals[0]?.id) || 'usr_admin';
      const inspectorNameToSave = selectedProf?.name || contextStore.get().userName || 'Inspetor Responsável';

      if (!this.equipmentId) {
        if (this.registeredEquipments.length > 0) {
          const matched = this.registeredEquipments.find(e => !this.selectedClientId || e.clientId === this.selectedClientId) || this.registeredEquipments[0];
          this.equipmentId = matched.id;
          this.equipmentTag = matched.tag || matched.id;
          this.equipmentName = matched.name || matched.description || 'Equipamento';
        } else if (this.equipmentTag) {
          this.equipmentId = this.equipmentTag;
          this.equipmentName = this.equipmentName || 'Equipamento';
        } else {
          try {
            const eqList = await p.equipmentService?.listEquipments(ctx);
            if (eqList && eqList.length > 0) {
              const firstEq = eqList[0];
              if (firstEq) {
                this.registeredEquipments = eqList;
                this.equipmentId = firstEq.id;
                this.equipmentTag = firstEq.tag || firstEq.id;
                this.equipmentName = firstEq.name || firstEq.description || 'Equipamento';
              }
            }
          } catch {}
          if (!this.equipmentId) {
            this.equipmentId = 'EQ-001';
            this.equipmentTag = this.equipmentTag || 'EQ-001';
            this.equipmentName = this.equipmentName || 'Vaso de Pressão';
          }
        }
      }

      if (!this.inspectionId) {
        const modality = (this.modalityState === 'Interna' ? 'INTERNAL' : this.modalityState === 'Externa' ? 'EXTERNAL' : 'GENERAL') as any;
        const nature = (this.natureState === 'Inicial' ? 'INITIAL' : this.natureState === 'Extraordinária' ? 'EXTRAORDINARY' : 'PERIODIC') as any;

        const result = await p.inspectionService.startInspection(ctx, {
          equipmentId: this.equipmentId as any,
          projectId: (this.selectedProjectId || null) as any,
          unitAreaId: (this.selectedUnitAreaId || null) as any,
          workOrderId: (this.selectedWorkOrderId || null) as any,
          subWorkOrderId: (this.selectedSubWorkOrderId || null) as any,
          operationalCondition: (this.conditionState || null) as any,
          inspectionType: this.inspectionType || 'Inspeção Externa',
          modality,
          nature,
          inspectorId: inspectorIdToSave as any,
          notes: this.generalObservations,
          startDate: this.startDate ? new Date(this.startDate).toISOString() : new Date().toISOString(),
          endDate: this.endDate ? new Date(this.endDate).toISOString() : null
        });
        this.inspectionId = result.inspection.id;

        // Enrich inspection object with OS, Sub-OS and newly added parameters
        if (result.inspection) {
          if (this.selectedClientId) {
            result.inspection.clientId = this.selectedClientId as any;
          }
          result.inspection.projectId = this.selectedProjectId || null;
          result.inspection.unitAreaId = this.selectedUnitAreaId || null;
          result.inspection.workOrderId = this.selectedWorkOrderId || null;
          result.inspection.subWorkOrderId = this.selectedSubWorkOrderId || null;
          result.inspection.operationalCondition = this.conditionState || null;
          if (this.startDate) {
            result.inspection.startDate = new Date(this.startDate).toISOString();
          }
          if (this.endDate) {
            result.inspection.endDate = new Date(this.endDate).toISOString();
          }
          if (p.inspectionService?.save) {
            await p.inspectionService.save(ctx, result.inspection);
          }
        }
      } else {
        // Checar se já existe relatório vinculado sem criar rascunho automaticamente
        if (!this.reportId) {
          try {
            const existingReport = await p.reportService.getReportByInspection(ctx, this.inspectionId as InspectionId);
            if (existingReport) {
              this.reportId = existingReport.id;
            }
          } catch (e) {
            console.warn("Could not find existing report:", e);
          }
        }
        // Update existing inspection with inspector and sub OS
        try {
          const currentInsp = await p.inspectionService.getInspectionById(ctx, this.inspectionId as any);
          if (currentInsp) {
            currentInsp.inspectorId = inspectorIdToSave as any;
            if (this.selectedClientId) {
              currentInsp.clientId = this.selectedClientId as any;
            }
            currentInsp.projectId = this.selectedProjectId || null;
            currentInsp.unitAreaId = this.selectedUnitAreaId || null;
            currentInsp.workOrderId = this.selectedWorkOrderId || null;
            currentInsp.subWorkOrderId = this.selectedSubWorkOrderId || null;
            currentInsp.operationalCondition = this.conditionState || null;
            currentInsp.equipmentId = this.equipmentId as any;
            currentInsp.notes = this.generalObservations || null;
            currentInsp.nature = (this.natureState === 'Inicial' ? 'INITIAL' : this.natureState === 'Extraordinária' ? 'EXTRAORDINARY' : 'PERIODIC') as any;
            currentInsp.modality = (this.modalityState === 'Interna' ? 'INTERNAL' : this.modalityState === 'Externa' ? 'EXTERNAL' : 'GENERAL') as any;
            if (this.startDate) {
              currentInsp.startDate = new Date(this.startDate).toISOString();
            }
            if (this.endDate) {
              currentInsp.endDate = new Date(this.endDate).toISOString();
            } else {
              currentInsp.endDate = null;
            }

            if (p.inspectionService?.save) {
              await p.inspectionService.save(ctx, currentInsp);
            }
          }
        } catch {}
      }

      // Re-fetch report to ensure we have the latest version or draft
      if (this.reportId) {
        this.persistLocally();
        const report = await p.reportService.getReportById(ctx, this.reportId as IntegrityReportId);
        if (report) {
          report.inspectionObservations = this.generalObservations;
          (report as any).damageMechanisms = this.damageMechanisms;
          (report as any).maintenanceServices = this.maintenanceServices;
          (report as any).performedInspectionMethods = this.performedTests;
          report.status = 'IN_ELABORATION';
          if (this.reportNumber) {
            report.reportNumber = this.reportNumber;
          }
          if (this.selectedClientId) {
            report.clientId = this.selectedClientId as any;
          }
          (report as any).inspectorName = inspectorNameToSave;
          report.inspectorId = inspectorIdToSave as any;
          (report as any).workOrderId = this.selectedWorkOrderId || undefined;
          (report as any).subWorkOrderId = this.selectedSubWorkOrderId || undefined;
          (report as any).osNumber = this.selectedOsCode || undefined;
          (report as any).subOsNumber = this.selectedSubOsCode || undefined;

          if (this.selectedProjectId) {
            const prj = this.projects.find(p => p.id === this.selectedProjectId);
            if (prj && prj.engineerProfessionalId) {
              report.engineerPlhId = prj.engineerProfessionalId;
            }
          }

          await p.reportService.updateReport(ctx, report);
        }
      }

      // Persist checklist components properly using saveChecklistState API
      if (this.inspectionId && p.inspectionService?.saveChecklistState) {
        const compStateItems = this.items.map(item => ({
          id: item.id,
          name: item.name,
          definitionComponentId: (item as any).definitionComponentId || item.name,
          groupId: item.category || (item as any).groupId || item.name,
          category: item.category || (item as any).groupId || item.name,
          status: item.status,
          result: item.status,
          condition: item.condition,
          description: item.condition,
          technicalComment: item.technicalComment,
          inspectionNotes: item.technicalComment,
          photos: item.photos || [],
          recommendations: item.recommendations || []
        }));

        const saveRes = await p.inspectionService.saveChecklistState(ctx, {
          inspectionId: this.inspectionId as any,
          components: compStateItems
        });

        if (saveRes && saveRes.components && saveRes.components.length > 0) {
          saveRes.components.forEach((sc: any) => {
            const match = this.items.find(it => it.name === sc.definitionComponentId || it.id === sc.id);
            if (match) {
              match.id = sc.id;
            }
          });
        }

        // Read-back verification
        const readBack = await p.inspectionService.getChecklistState(ctx, this.inspectionId as InspectionId);
        if (!readBack || !readBack.components || readBack.components.length === 0) {
          throw new Error(`Falha ao persistir Checklist no servidor.\n\nReport ID: ${this.reportId}\nInspection ID: ${this.inspectionId}\nComponentes enviados: ${this.items.length}\nComponentes persistidos: 0`);
        }
      }

      // Sync report recommendations
      if (this.reportId) {
        // Sync recommendations while preserving those created manually in the recommendations page
        if (p.reportService?.listRecommendations) {
          const existingRecs = await p.reportService.listRecommendations(ctx, this.reportId as IntegrityReportId);
          const existingByChecklistItemId = new Map<string, string>();
          for (const rec of existingRecs) {
            if (rec.checklistItemId) existingByChecklistItemId.set(rec.checklistItemId, rec.id);
          }
          const validRecIds = new Set<string>();

          for (const item of this.items) {
            if (item.recommendations && item.recommendations.length > 0) {
              for (const rec of item.recommendations) {
                if (rec.id) validRecIds.add(rec.id);
              }
            }
          }

          for (const rec of existingRecs) {
            if (rec.checklistItemId && !validRecIds.has(rec.id) && p.reportService.deleteRecommendation) {
              await p.reportService.deleteRecommendation(ctx, rec.id).catch(() => {});
            }
          }
        }

        // Add recommendations from checklist directly linked to the component
        for (const item of this.items) {
          const persistentComponentId = item.id;

          if (item.recommendations && item.recommendations.length > 0) {
            for (const rec of item.recommendations) {
              if (rec.description && rec.description.trim()) {
                try {
                  const toAdd: any = {
                    id: rec.id,
                    discipline: rec.discipline as any,
                    criticality: (rec.criticality === 'ALTA' || rec.criticality === 'CRÍTICA') ? 'HIGH' : rec.criticality === 'MÉDIA' ? 'MODERATE' : 'LOW',
                    executeIn: rec.execution === 'PARADA' ? 'SHUTDOWN' : 'ROUTINE',
                    scope: `${item.name}: ${rec.description}`,
                    zr: rec.zr || undefined,
                    componentId: persistentComponentId,
                    checklistItemId: persistentComponentId,
                    componentName: item.name,
                    code: rec.code
                  };
                  await p.reportService.addRecommendation(ctx, this.reportId as IntegrityReportId, toAdd);
                } catch {
                  // Ignore duplicates
                }
              }
            }
          } else if (item.recommendation && item.recommendation.trim()) {
            try {
              const toAdd: any = {
                id: `rec-legacy-${item.id}`,
                discipline: "CALDEIRARIA",
                criticality: item.status === 'REJECTED' ? "HIGH" : "MODERATE",
                executeIn: "ROUTINE",
                scope: `${item.name}: ${item.recommendation}`,
                zr: undefined,
                componentId: persistentComponentId,
                checklistItemId: persistentComponentId,
                componentName: item.name
              };
              await p.reportService.addRecommendation(ctx, this.reportId as IntegrityReportId, toAdd);
            } catch {
              // Ignore duplicates
            }
          }
        }
      }

      if (finalize) {
        this.isLocked = true;
      }
      this.persistLocally();
      this.hasUnsavedChanges = false;
      // Explicitly invalidate RI document snapshot caches
      if (this.reportId) {
        try {
          localStorage.removeItem(`ri_cache_${this.reportId}`);
          localStorage.removeItem(`ri_checklist_${this.reportId}`);
        } catch {
          // ignore
        }
      }
      window.dispatchEvent(new CustomEvent('ri_checklist_updated', {
        detail: { reportId: this.reportId, inspectionId: this.inspectionId, items: this.items }
      }));
      if (!silent) {
        if (finalize) {
          alert("Checklist finalizado com sucesso! Os campos foram bloqueados para edição.");
        } else {
          alert("Checklist salvo com sucesso!");
        }
      }
      if (saveBtn) saveBtn.innerHTML = `<i data-lucide="save" style="width: 16px; height: 16px;"></i> Salvar Checklist`;
      this.render();
      return true;
    } catch (e: any) {
      console.error(e);
      if (!silent) {
        alert("Erro ao salvar: " + e.message);
      }
      if (saveBtn) saveBtn.innerHTML = `<i data-lucide="save" style="width: 16px; height: 16px;"></i> Salvar Checklist`;
      this.render();
      if (silent) {
        throw e;
      }
      return false;
    }
  }
}

if (!customElements.get("app-visual-checklist-page")) {
  customElements.define("app-visual-checklist-page", AppVisualChecklistPage);
}










